/* =======================================================================
   DRIVE IQ — SCAN ENGINE JS V4.3 ENTITY REGISTRY V1 + DISTRICT MEMBERSHIP
   Asset File:
   assets/driveiq-scan-engine.js

   PURPOSE:
   - Shared recommendation brain for Drive IQ
   - No UI, no Mapbox rendering, no DOM dependency required
   - Adventure Mode / Travel IQ / Ask IQ / Drive Mode smart stops can call it
   - Enforces Real Results Authority:
     1. No cities / villages / counties / admin labels as recommendations
     2. No radio stations / offices / utilities / placeholder POIs
     3. Category Truth is allowlist-first, not blacklist-only
     4. Rural scans may return fewer real results instead of padding with junk
     5. Diagnostics show exactly where candidates are removed

   CORE RULE:
   Search IQ finds the scan center.
   Scan Engine returns only real, visitable, category-correct places.

   ES5 SAFE
======================================================================== */

(function(){
"use strict";

if(window.__DRIVE_IQ_SCAN_ENGINE_V4_2_2_EXPLICIT_ENTITY_IQ__){ return; }
window.__DRIVE_IQ_SCAN_ENGINE_V4_2_2_EXPLICIT_ENTITY_IQ__ = true;
window.__DRIVE_IQ_SCAN_ENGINE_V3_4_CLEAN_RESTORE_FINAL_AUTHORITY__ = true;

var BUILD = "V5_2_UNIVERSAL_TARGET_RESULTS_AUTHORITY";
var DEBUG_KEY = "diq_scan_engine_debug_v1";
var LAST_REPORT_KEY = "diq_scan_engine_last_report_v1";

function clean(v){ return String(v || "").replace(/\s+/g," ").trim(); }
function lower(v){ return String(v || "").toLowerCase(); }
function normText(v){ return lower(v).replace(/[^a-z0-9]+/g," ").replace(/\s+/g," ").trim(); }
function finite(v){ return typeof v === "number" && isFinite(v); }
function nowIso(){ try{ return new Date().toISOString(); }catch(e){ return ""; } }
function safeJsonSet(k,v){ try{ localStorage.setItem(k,JSON.stringify(v)); }catch(e){} }
function safeJsonGet(k){ try{ return JSON.parse(localStorage.getItem(k) || "null"); }catch(e){ return null; } }
function safeGet(k){ try{ return localStorage.getItem(k); }catch(e){ return null; } }

function clone(obj){
  var out;
  try{ out = JSON.parse(JSON.stringify(obj || {})); }
  catch(e){ out = {}; }
  return out;
}

function list(v){ return v && v.length ? v : []; }

function miles(lat1,lng1,lat2,lng2){
  var R = 3958.8;
  var dLat;
  var dLng;
  var a1;
  var a2;
  var a;

  lat1 = Number(lat1); lng1 = Number(lng1); lat2 = Number(lat2); lng2 = Number(lng2);
  if(!finite(lat1) || !finite(lng1) || !finite(lat2) || !finite(lng2)){ return null; }

  dLat = (lat2 - lat1) * Math.PI / 180;
  dLng = (lng2 - lng1) * Math.PI / 180;
  a1 = lat1 * Math.PI / 180;
  a2 = lat2 * Math.PI / 180;

  a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.sin(dLng/2) * Math.sin(dLng/2) *
    Math.cos(a1) *
    Math.cos(a2);

  return R * 2 * Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
}

function uniquePush(arr,item,key){
  var k = clean(key || "");
  var i;

  if(!k){ arr.push(item); return; }

  for(i=0;i<arr.length;i++){
    if(clean(arr[i]._dedupeKey || "") === k){ return; }
  }

  item._dedupeKey = k;
  arr.push(item);
}

/* =====================================================================
   CATEGORY PROFILES — ALLOWLIST FIRST
===================================================================== */

var CATEGORY_PROFILES = {
  coffee:{
    id:"coffee",
    label:"Coffee IQ",
    radiusMiles:14,
    expandedRadiusMiles:28,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:60,
    allowChains:false,
    hardTerms:["coffee","cafe","café","espresso","latte","roaster","coffeehouse","bakery"],
    softTerms:["breakfast","pastry","donut","tea"],
    blockedTerms:["smoothie only","nutrition club","gas station","convenience","fast food","hotel","motel","inn","suites","lodging","best western","hampton inn","holiday inn","la quinta","comfort inn","quality inn","super 8","days inn","school","church","office","bank","radio"],
    allowedKinds:["catering.restaurant","catering.cafe","catering.coffee_shop","catering.fast_food","catering.bar","catering.pub","commercial.food_and_drink"],
    weights:{categoryTruth:34,distance:22,localFavorite:18,rating:12,worthDrive:8,taste:6}
  },

  food:{
    id:"food",
    label:"Food IQ",
    radiusMiles:24,
    expandedRadiusMiles:75,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:58,
    allowChains:true,
    hardTerms:["restaurant","grill","grille","bbq","barbecue","pizza","burger","taco","seafood","cafe","café","diner","steak","sandwich","wings","sushi","food","kitchen","mexican","italian","breakfast","brunch","bakery","bistro","pub","brewery","bar and grill","eatery","smokehouse","catfish","chicken","ice cream","dessert"],
    softTerms:["coffee","bar","market","food truck","drive in","drive-in"],
    blockedTerms:["school","hotel","motel","inn","suites","lodging","best western","hampton inn","holiday inn","la quinta","comfort inn","quality inn","super 8","days inn","airport","hospital","office","church","bank","retail","best buy","walmart","target","gas station","radio","fm","am","government","city hall","post office"],
    allowedKinds:["catering","catering.restaurant","catering.cafe","catering.fast_food","catering.bar","catering.pub","catering.ice_cream","commercial.food_and_drink"],
    weights:{categoryTruth:34,localFavorite:20,rating:16,distance:12,worthDrive:12,taste:6}
  },

  breakfast:{
    id:"breakfast",
    label:"Breakfast IQ",
    radiusMiles:18,
    expandedRadiusMiles:45,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:58,
    allowChains:true,
    hardTerms:["breakfast","brunch","cafe","café","diner","bakery","coffee","restaurant","donut","pancake","waffle","biscuit"],
    softTerms:["kitchen","eatery"],
    blockedTerms:["bar only","nightclub","school","hotel","motel","inn","suites","lodging","best western","hampton inn","holiday inn","la quinta","comfort inn","quality inn","super 8","days inn","office","retail","radio"],
    allowedKinds:["catering.restaurant","catering.cafe","catering.coffee_shop","catering.fast_food","commercial.food_and_drink"],
    weights:{categoryTruth:32,distance:18,localFavorite:18,rating:16,worthDrive:10,taste:6}
  },

  dinner:{
    id:"dinner",
    label:"Dinner IQ",
    radiusMiles:30,
    expandedRadiusMiles:80,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:58,
    allowChains:true,
    hardTerms:["restaurant","grill","grille","steak","seafood","italian","mexican","bbq","barbecue","sushi","dinner","bar and grill","bistro","kitchen","pub","brewery","pizza","catfish"],
    softTerms:["bar","cafe"],
    blockedTerms:["school","hotel","motel","inn","suites","lodging","best western","hampton inn","holiday inn","la quinta","comfort inn","quality inn","super 8","days inn","airport","hospital","office","church","bank","retail","gas station","radio"],
    allowedKinds:["catering.restaurant","catering.cafe","catering.bar","catering.pub","catering.fast_food","commercial.food_and_drink"],
    weights:{localFavorite:26,rating:22,worthDrive:16,categoryTruth:24,distance:6,taste:6}
  },

  dessert:{
    id:"dessert",
    label:"Dessert IQ",
    radiusMiles:18,
    expandedRadiusMiles:45,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:58,
    allowChains:true,
    hardTerms:["dessert","ice cream","bakery","donut","cupcake","cookie","chocolate","sweet","treat","frozen yogurt","cake","pie"],
    softTerms:["coffee","cafe"],
    blockedTerms:["school","office","gas station","hospital","bank","radio"],
    allowedKinds:["catering.ice_cream","catering.cafe","catering.coffee_shop","catering.restaurant","commercial.food_and_drink"],
    weights:{categoryTruth:34,distance:18,localFavorite:18,rating:14,worthDrive:10,taste:6}
  },

  local_favorites:{
    id:"local_favorites",
    label:"Local Favorites IQ",
    radiusMiles:28,
    expandedRadiusMiles:85,
    maxResults:20,
    minUsefulResults:3,
    confidenceThreshold:55,
    allowChains:false,
    hardTerms:["restaurant","coffee","cafe","café","bar","brewery","pub","park","boutique","local","grill","hangout","market","museum","attraction","trail","marina","theater","music","bakery","bookstore","ice cream","pizza","bbq","barbecue","golf","course","shopping"],
    softTerms:["downtown","main street","historic","neighborhood","family owned","independent"],
    blockedTerms:["school","hospital","office","generic","administrative","road","street","radio","fm","am","government","city hall","county","utility","post office","police","fire station"],
    allowedKinds:["catering","commercial","entertainment","leisure","tourism","natural","sport","national_park","beach","park"],
    weights:{localFavorite:32,worthDrive:18,rating:14,categoryTruth:18,distance:10,taste:8}
  },

  golf:{
    id:"golf",
    label:"Golf IQ",
    radiusMiles:75,
    expandedRadiusMiles:120,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:56,
    allowChains:false,
    hardTerms:["golf","course","country club","links","driving range","golf club"],
    softTerms:["public course","championship"],
    blockedTerms:["mini golf","putt putt","retail","school","office","radio"],
    allowedKinds:["sport.golf","sport","leisure"],
    weights:{categoryTruth:34,worthDrive:22,rating:18,localFavorite:10,distance:8,quality:8}
  },

  lake:{
    id:"lake",
    label:"Lake IQ",
    radiusMiles:100,
    expandedRadiusMiles:140,
    maxResults:20,
    minUsefulResults:3,
    confidenceThreshold:54,
    allowChains:false,
    hardTerms:["lake","reservoir","marina","boat ramp","campground","beach","waterfront","fishing","recreation area","state park","park","landing","dock"],
    softTerms:["water","trail","picnic","camping"],
    blockedTerms:["hotel only","apartment","office","school","street","road","administrative","radio"],
    allowedKinds:["natural","tourism","leisure","camping","beach","park"],
    weights:{categoryTruth:32,worthDrive:22,distance:8,recreation:18,localFavorite:10,rating:10}
  },

  nightlife:{
    id:"nightlife",
    label:"Nightlife IQ",
    radiusMiles:38,
    expandedRadiusMiles:80,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:56,
    allowChains:false,
    hardTerms:["bar","pub","brewery","cocktail","live music","nightlife","lounge","music venue","tavern","dance","club"],
    softTerms:["restaurant","patio","sports bar"],
    blockedTerms:["school","church","office","retail","gas station","radio"],
    allowedKinds:["catering.bar","catering.pub","entertainment","commercial.food_and_drink","catering.restaurant"],
    weights:{categoryTruth:30,liveSignal:20,localFavorite:16,rating:12,distance:10,worthDrive:12}
  },

  parks:{
    id:"parks",
    label:"Parks IQ",
    radiusMiles:35,
    expandedRadiusMiles:85,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:56,
    allowChains:false,
    hardTerms:["park","trail","greenway","recreation","playground","nature","wildlife","scenic","state park","waterfall","overlook"],
    softTerms:["lake","river","picnic","walking"],
    blockedTerms:["office","school only","apartment","street","road","administrative","radio"],
    allowedKinds:["leisure.park","natural","tourism","park","beach"],
    weights:{categoryTruth:32,distance:14,worthDrive:22,rating:12,recreation:12,localFavorite:8}
  },

  shopping:{
    id:"shopping",
    label:"Shopping IQ",
    radiusMiles:28,
    expandedRadiusMiles:70,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:56,
    allowChains:true,
    hardTerms:["boutique","shop","store","market","mall","antique","vintage","gift","bookstore","outlet","flea market"],
    softTerms:["local","artisan","downtown"],
    blockedTerms:["office","school","hospital","road","street","administrative","radio"],
    allowedKinds:["commercial","commercial.shopping_mall","commercial.gift_and_souvenir","commercial.clothing","commercial.books","commercial.antiques"],
    weights:{categoryTruth:30,localFavorite:16,distance:14,rating:14,worthDrive:14,taste:12}
  },

  hidden_gems:{
    id:"hidden_gems",
    label:"Hidden Gems IQ",
    radiusMiles:38,
    expandedRadiusMiles:90,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:55,
    allowChains:false,
    hardTerms:["local","hidden","gem","scenic","cafe","restaurant","park","boutique","gallery","historic","museum","bakery","waterfront","trail"],
    softTerms:["family owned","independent","downtown","neighborhood"],
    blockedTerms:["chain","franchise","hotel","motel","inn","suites","lodging","best western","hampton inn","holiday inn","la quinta","comfort inn","quality inn","super 8","days inn","school","hospital","office","gas station","radio","city hall","government"],
    allowedKinds:["catering","commercial","tourism","leisure","natural","entertainment"],
    weights:{localFavorite:28,worthDrive:24,categoryTruth:18,rating:10,distance:10,taste:10}
  },

  family_fun:{
    id:"family_fun",
    label:"Family Fun IQ",
    radiusMiles:35,
    expandedRadiusMiles:80,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:56,
    allowChains:true,
    hardTerms:["family","kids","park","arcade","bowling","museum","zoo","playground","mini golf","entertainment","theater","water park"],
    softTerms:["ice cream","pizza","family restaurant"],
    blockedTerms:["bar only","nightclub","office","school","hospital","radio"],
    allowedKinds:["entertainment","leisure","tourism","catering.restaurant"],
    weights:{categoryTruth:30,familyFit:20,rating:16,distance:12,worthDrive:14,localFavorite:8}
  }
};

function defaultProfile(){
  return {
    id:"general",
    label:"Adventure IQ",
    radiusMiles:28,
    expandedRadiusMiles:80,
    maxResults:20,
    minUsefulResults:2,
    confidenceThreshold:55,
    allowChains:true,
    hardTerms:["restaurant","coffee","park","shop","bar","attraction","local","scenic","entertainment","museum","trail","market"],
    softTerms:["downtown","historic","waterfront"],
    blockedTerms:["road","street","administrative","postcode","office only","radio","city hall","government"],
    allowedKinds:["catering","commercial","leisure","tourism","entertainment","natural","sport"],
    weights:{categoryTruth:24,localFavorite:18,worthDrive:18,rating:16,distance:14,taste:10}
  };
}

function resolveIntent(options){
  var q = normText((options && (options.intent || options.category || options.query)) || "");
  var out = { raw:clean((options && (options.intent || options.category || options.query)) || ""), id:"general", profile:defaultProfile() };

  if(!q){ return out; }

  if(/\bcoffee|cafe|espresso|latte|roaster\b/.test(q)){ out.id = "coffee"; }
  else if(/\bbreakfast|brunch\b/.test(q)){ out.id = "breakfast"; }
  else if(/\bdinner|date night|supper\b/.test(q)){ out.id = "dinner"; }
  else if(/\bdessert|ice cream|sweet|treat|bakery|donut|cupcake\b/.test(q)){ out.id = "dessert"; }
  else if(/\bgolf|country club|driving range|links\b/.test(q)){ out.id = "golf"; }
  else if(/\blake|reservoir|marina|boat ramp|campground|waterfront|fishing\b/.test(q)){ out.id = "lake"; }
  else if(/\bnightlife|bar|pub|brewery|live music|cocktail|lounge\b/.test(q)){ out.id = "nightlife"; }
  else if(/\bpark|trail|outdoor|scenic|nature|playground\b/.test(q)){ out.id = "parks"; }
  else if(/\bshopping|shop|boutique|market|antique|vintage|gift|bookstore|outlet\b/.test(q)){ out.id = "shopping"; }
  else if(/\bhidden gem|hidden gems\b/.test(q)){ out.id = "hidden_gems"; }
  else if(/\blocal favorite|local favorites|local faves|hangout\b/.test(q)){ out.id = "local_favorites"; }
  else if(/\bfamily|kids|arcade|bowling|museum|zoo\b/.test(q)){ out.id = "family_fun"; }
  else if(/\bfood|restaurant|lunch|pizza|burger|taco|bbq|barbecue|seafood|sushi|wings\b/.test(q)){ out.id = "food"; }

  out.profile = clone(CATEGORY_PROFILES[out.id] || defaultProfile());
  return out;
}

/* =====================================================================
   DEBUG REPORT
===================================================================== */

function isDebugEnabled(options){
  if(options && options.debug === true){ return true; }
  if(window.DRIVE_IQ_SCAN_DEBUG === true){ return true; }
  if(safeGet(DEBUG_KEY) === "1"){ return true; }
  return false;
}

function newReport(options,intent){
  return {
    build:BUILD,
    version:"v3_4_clean_restore_final_authority",
    ts:nowIso(),
    query:clean((options && options.query) || ""),
    intent:intent ? intent.id : "general",
    profile:intent && intent.profile ? intent.profile.label : "Adventure IQ",
    scope:clean((options && options.scope) || "near_me"),
    destination:options && options.destination ? clean(options.destination.label || options.destination.name || "") : "",
    center:options && options.location ? { lat:Number(options.location.lat), lng:Number(options.location.lng) } : null,
    counts:{
      pulled:0,
      afterConsumerDestinationGate:0,
      afterPlaceTruth:0,
      afterCategoryTruth:0,
      afterFreshnessTruth:0,
      afterDistanceTruth:0,
      afterScoring:0,
      afterVariety:0,
      afterFinalAuthority:0,
      returned:0
    },
    removed:{
      consumerDestinationGate:[],
      placeTruth:[],
      categoryTruth:[],
      freshnessTruth:[],
      distanceTruth:[],
      scoring:[],
      variety:[],
      finalAuthority:[]
    },
    scores:[],
    averageConfidence:0,
    engineTimeMs:0,
    notes:[]
  };
}

function addRemoval(report,layer,item,reason){
  var bucket;
  if(!report || !report.removed){ return; }
  bucket = report.removed[layer];
  if(!bucket){ return; }

  if(bucket.length < 20){
    bucket.push({
      name:clean(item && (item.label || item.name || item.full) || "Unknown"),
      category:clean(item && (item.category || item.kind || item.type) || ""),
      reason:clean(reason || "removed")
    });
  }
}

function finishReport(report,startedAt,results,options){
  var i;
  var total = 0;

  if(!report){ return null; }

  report.engineTimeMs = Math.max(0,Date.now() - Number(startedAt || Date.now()));
  report.counts.returned = results && results.length ? results.length : 0;

  report.scores = [];
  for(i=0;i<list(results).length;i++){
    report.scores.push({
      name:clean(results[i].label || results[i].name || "Recommendation"),
      score:Math.round(Number(results[i].diqScore || 0)),
      confidence:Math.round(Number(results[i].confidence || 0)),
      category:clean(results[i].category || results[i].kind || ""),
      poiClass:clean(results[i].poiClass || "")
    });
    total += Number(results[i].confidence || 0);
  }

  report.averageConfidence = results && results.length ? Math.round(total / results.length) : 0;

  if(results && results.length && results.length < Number((options && options.targetResults) || 6)){
    report.notes.push("Returned fewer results by design: Real Results Authority does not pad shelves with city/admin/radio placeholders.");
  }
  if(!results || !results.length){
    report.notes.push("No trustworthy category-correct consumer destinations survived. Expand radius or improve provider candidate pull.");
  }

  try{ safeJsonSet(LAST_REPORT_KEY,report); }catch(e){}

  if(isDebugEnabled(options || {})){
    try{
      console.log("🧠 Drive IQ Scan Report",report);
      if(console && console.table && report.scores && report.scores.length){ console.table(report.scores); }
    }catch(logErr){}
  }

  return report;
}

/* =====================================================================
   TEXT / NORMALIZATION
===================================================================== */

function getText(item){
  var cats = "";
  var i;

  if(item && item.categories && typeof item.categories === "object"){
    try{ cats = item.categories.join ? item.categories.join(" ") : JSON.stringify(item.categories); }catch(e){ cats = ""; }
  }else{
    cats = String(item && item.categories || "");
  }

  return normText(
    String(item && item.label || "") + " " +
    String(item && item.name || "") + " " +
    String(item && item.full || "") + " " +
    String(item && item.address || "") + " " +
    String(item && item.formatted || "") + " " +
    String(item && item.category || "") + " " +
    cats + " " +
    String(item && item.kind || "") + " " +
    String(item && item.type || "") + " " +
    String(item && item.resultType || "") + " " +
    String(item && item.matchLabel || "") + " " +
    String(item && item.sourceQuery || "")
  );
}

function getNameText(item){
  return normText(String(item && item.label || "") + " " + String(item && item.name || "") + " " + String(item && item.full || ""));
}

/* V3.2 POI CLASSIFIER AUTHORITY
   Critical: category checks must use the POI's own identity/category text,
   not the user's scan query/sourceQuery. Otherwise a hotel passed into a
   Food IQ scan can inherit the word "food" from the query and get scored. */
function getPoiText(item){
  var cats = "";

  if(item && item.categories && typeof item.categories === "object"){
    try{ cats = item.categories.join ? item.categories.join(" ") : JSON.stringify(item.categories); }catch(e){ cats = ""; }
  }else{
    cats = String(item && item.categories || "");
  }

  return normText(
    String(item && item.label || "") + " " +
    String(item && item.name || "") + " " +
    String(item && item.full || "") + " " +
    String(item && item.address || "") + " " +
    String(item && item.formatted || "") + " " +
    String(item && item.category || "") + " " +
    cats + " " +
    String(item && item.kind || "") + " " +
    String(item && item.type || "") + " " +
    String(item && item.resultType || "") + " " +
    String(item && item.matchLabel || "")
  );
}

function classifyPoi(item){
  var txt = getPoiText(item);
  var name = getNameText(item);

  if(!txt && !name){ return "unknown"; }

  if(/^city of\b/.test(name) || /^village of\b/.test(name) || /^town of\b/.test(name) || /^county of\b/.test(name) ||
     /\b(administrative|boundary|postcode|postal code|neighbourhood|neighborhood|suburb|district|county|locality|municipality|census|township|city of|village of|town of|state of)\b/.test(txt)){
    return "admin";
  }

  if(/\bradio station\b|\bfm\b|\bam\b|\btranslator\b|\bbroadcast\b|\btower\b|\bantenna\b/.test(txt)){ return "radio"; }
  if(/\bpolice\b|\bfire station\b|\bcourthouse\b|\bcity hall\b|\btown hall\b|\bpost office\b|\bdmv\b|\bgovernment\b/.test(txt)){ return "government"; }
  if(/\bschool\b|\belementary\b|\bmiddle school\b|\bhigh school\b|\buniversity\b|\bcollege\b/.test(txt)){ return "school"; }
  if(/\bchurch\b|\btemple\b|\bmosque\b|\bcemetery\b|\bfuneral\b/.test(txt)){ return "worship"; }
  if(/\bhospital\b|\bclinic\b|\bdoctor\b|\bdentist\b|\bpharmacy only\b/.test(txt)){ return "medical"; }
  if(/\butility\b|\belectric company\b|\bwater department\b|\bsewer\b|\btelecom\b/.test(txt)){ return "utility"; }
  if(/\binsurance\b|\breal estate\b|\battorney\b|\blaw office\b|\baccounting\b|\bbank\b|\bcredit union\b|\boffice\b/.test(txt)){ return "office"; }
  if(/\bapartment\b|\bresidential\b|\bsubdivision\b|\bmobile home\b/.test(txt)){ return "residential"; }

  if(/\bbest western\b|\bhampton inn\b|\bholiday inn\b|\bla quinta\b|\bcomfort inn\b|\bquality inn\b|\bsuper 8\b|\bdays inn\b|\bmotel 6\b|\bred roof inn\b|\bmarriott\b|\bhilton\b|\bhyatt\b|\bsheraton\b|\bwyndham\b|\bdrury inn\b|\becono lodge\b|\bsleep inn\b|\btravelodge\b/.test(txt) ||
     /\bhotel\b|\bmotel\b|\binn\b|\bsuites\b|\blodging\b|\blodge\b|\bresort\b|\bextended stay\b|\bguesthouse\b|\bbed and breakfast\b|\bbnb\b|\brv park\b/.test(txt) ||
     /\bhotel\b|\bmotel\b|\binn\b|\bsuites\b|\blodge\b|\bresort\b/.test(name)){
    return "lodging";
  }

  if(/\bcoffee\b|\bcafe\b|\bcafé\b|\bespresso\b|\broaster\b|\bcoffeehouse\b/.test(txt)){ return "coffee"; }
  if(/\bbakery\b|\bdonut\b|\bcupcake\b|\bcookie\b|\bice cream\b|\bdessert\b|\bchocolate\b|\bsweet\b|\btreat\b/.test(txt)){ return "dessert"; }
  if(/\brestaurant\b|\bgrill\b|\bgrille\b|\bbbq\b|\bbarbecue\b|\bpizza\b|\bburger\b|\btaco\b|\bseafood\b|\bdiner\b|\bsteak\b|\bsandwich\b|\bwings\b|\bsushi\b|\bkitchen\b|\beatery\b|\bbistro\b|\bsmokehouse\b|\bcatfish\b|\bchicken\b|\bcatering.restaurant\b|\bcatering.fast_food\b/.test(txt)){
    return "restaurant";
  }
  if(/\bbar\b|\bpub\b|\bbrewery\b|\bcocktail\b|\blive music\b|\bnightlife\b|\blounge\b|\btavern\b/.test(txt)){ return "nightlife"; }
  if(/\bpark\b|\btrail\b|\bgreenway\b|\bplayground\b|\bnature\b|\bwildlife\b|\bscenic\b|\bstate park\b|\brecreation\b/.test(txt)){ return "park"; }
  if(/\blake\b|\breservoir\b|\bmarina\b|\bboat ramp\b|\bcampground\b|\bbeach\b|\bwaterfront\b|\bfishing\b/.test(txt)){ return "outdoor_water"; }
  if(/\bgolf\b|\bcourse\b|\bcountry club\b|\blinks\b|\bdriving range\b/.test(txt)){ return "golf"; }
  if(/\bshop\b|\bstore\b|\bboutique\b|\bmarket\b|\bmall\b|\bantique\b|\bvintage\b|\bgift\b|\bbookstore\b|\boutlet\b/.test(txt)){ return "shopping"; }
  if(/\bmuseum\b|\bzoo\b|\barcade\b|\bbowling\b|\btheater\b|\btheatre\b|\bmusic venue\b|\bgallery\b|\battraction\b|\bentertainment\b/.test(txt)){ return "attraction"; }

  if(/\broad\b|\bstreet\b|\bhighway\b|\broute\b|\bintersection\b|\baddress\b/.test(txt)){ return "road"; }

  return "unknown";
}

function isFoodClass(cls){
  return cls === "restaurant" || cls === "coffee" || cls === "dessert" || cls === "nightlife";
}

function classAllowedForIntent(cls,intent){
  var id = intent && intent.id ? String(intent.id) : "general";

  if(id === "food" || id === "breakfast" || id === "dinner"){ return cls === "restaurant" || cls === "coffee" || cls === "dessert" || cls === "nightlife"; }
  if(id === "coffee"){ return cls === "coffee" || cls === "dessert"; }
  if(id === "dessert"){ return cls === "dessert" || cls === "coffee" || cls === "restaurant"; }
  if(id === "nightlife"){ return cls === "nightlife" || cls === "restaurant"; }
  if(id === "local_favorites" || id === "hidden_gems" || id === "general"){ return cls === "restaurant" || cls === "coffee" || cls === "dessert" || cls === "nightlife" || cls === "park" || cls === "outdoor_water" || cls === "golf" || cls === "shopping" || cls === "attraction"; }
  if(id === "golf"){ return cls === "golf"; }
  if(id === "lake" || id === "parks"){ return cls === "park" || cls === "outdoor_water"; }
  if(id === "shopping"){ return cls === "shopping"; }
  if(id === "family_fun"){ return cls === "park" || cls === "outdoor_water" || cls === "attraction" || cls === "restaurant" || cls === "dessert"; }

  return cls !== "admin" && cls !== "radio" && cls !== "government" && cls !== "school" && cls !== "worship" && cls !== "medical" && cls !== "utility" && cls !== "office" && cls !== "residential" && cls !== "road" && cls !== "unknown";
}

function hasAnyTerm(text,terms){
  var i;
  text = normText(text);
  terms = list(terms);
  for(i=0;i<terms.length;i++){
    if(text.indexOf(normText(terms[i])) > -1){ return true; }
  }
  return false;
}

function hasRegex(text,rx){
  try{ return rx.test(normText(text)); }catch(e){ return false; }
}

function normalizeCandidate(raw,options){
  var item = clone(raw || {});
  var coords = item.coordinates || item.center || null;

  item.label = clean(item.label || item.name || item.title || item.place_name || "Adventure stop");
  item.full = clean(item.full || item.address || item.formatted || item.description || item.place_name || "");
  item.lat = finite(Number(item.lat)) ? Number(item.lat) : (coords && finite(Number(coords[1])) ? Number(coords[1]) : null);
  item.lng = finite(Number(item.lng)) ? Number(item.lng) : (coords && finite(Number(coords[0])) ? Number(coords[0]) : null);
  item.category = clean(item.category || item.kind || item.type || "");
  item.source = clean(item.source || "scan-engine");
  item.query = clean((options && options.query) || item.query || "");

  return item;
}

function dedupeCandidates(items){
  var out = [];
  var i;
  var item;
  var key;
  var lat;
  var lng;

  for(i=0;i<list(items).length;i++){
    item = items[i];
    lat = finite(Number(item.lat)) ? Math.round(Number(item.lat) * 10000) / 10000 : "";
    lng = finite(Number(item.lng)) ? Math.round(Number(item.lng) * 10000) / 10000 : "";
    key = normText(String(item.placeId || item.googlePlaceId || item.mapboxId || "") + "|" + String(item.label || "") + "|" + String(item.full || "") + "|" + lat + "|" + lng);
    uniquePush(out,item,key);
  }

  return out;
}

/* =====================================================================
   REAL RESULTS AUTHORITY
===================================================================== */

function isAdminOrMapLabel(item){
  var cls = classifyPoi(item);
  return cls === "admin" || cls === "road";
}


function isNonConsumerJunk(item){
  var cls = classifyPoi(item);
  return cls === "radio" || cls === "government" || cls === "school" || cls === "worship" || cls === "medical" || cls === "utility" || cls === "office" || cls === "residential";
}


function looksVisitableConsumerPlace(item){
  var cls = classifyPoi(item);
  return cls === "restaurant" || cls === "coffee" || cls === "dessert" || cls === "nightlife" || cls === "park" || cls === "outdoor_water" || cls === "golf" || cls === "shopping" || cls === "attraction";
}


function applyConsumerDestinationGate(items,intent,options,report){
  var out = [];
  var i;
  var item;

  for(i=0;i<list(items).length;i++){
    item = items[i];

    item.poiClass = classifyPoi(item);

    if(isAdminOrMapLabel(item)){
      addRemoval(report,"consumerDestinationGate",item,"city/admin/map label");
      continue;
    }

    if(isNonConsumerJunk(item)){
      addRemoval(report,"consumerDestinationGate",item,"non-consumer POI: " + item.poiClass);
      continue;
    }

    if(item.poiClass === "lodging"){
      addRemoval(report,"consumerDestinationGate",item,"lodging/hotel is not an Adventure recommendation");
      continue;
    }

    if(!looksVisitableConsumerPlace(item)){
      addRemoval(report,"consumerDestinationGate",item,"not a visitable consumer destination: " + item.poiClass);
      continue;
    }

    item.consumerDestination = true;
    out.push(item);
  }

  if(report){ report.counts.afterConsumerDestinationGate = out.length; }
  return dedupeCandidates(out);
}

/* =====================================================================
   PIPELINE LAYERS
===================================================================== */

function pullCandidates(options,report){
  var candidates = [];
  var i;
  var raw;

  if(options && options.candidates && options.candidates.length){
    for(i=0;i<options.candidates.length;i++){ candidates.push(normalizeCandidate(options.candidates[i],options)); }
  }

  if(!candidates.length && window.DriveIQSearchCore && window.DriveIQSearchCore.search){
    try{
      raw = window.DriveIQSearchCore.search(options || {});
      if(raw && raw.length){
        for(i=0;i<raw.length;i++){ candidates.push(normalizeCandidate(raw[i],options)); }
      }
    }catch(searchErr){
      if(report){ report.notes.push("DriveIQSearchCore unavailable or failed."); }
    }
  }

  candidates = dedupeCandidates(candidates);
  if(report){ report.counts.pulled = candidates.length; }
  return candidates;
}

function applyPlaceTruth(items,options,report){
  var out = [];
  var i;
  var item;

  for(i=0;i<list(items).length;i++){
    item = items[i];

    if(!item.label || item.label === "Adventure stop"){
      addRemoval(report,"placeTruth",item,"missing usable name");
      continue;
    }

    if(!finite(Number(item.lat)) || !finite(Number(item.lng))){
      addRemoval(report,"placeTruth",item,"missing coordinates");
      continue;
    }

    item.placeTruth = true;
    out.push(item);
  }

  if(report){ report.counts.afterPlaceTruth = out.length; }
  return dedupeCandidates(out);
}

function categoryKindAllowed(item,profile){
  var txt = getPoiText(item);
  var kinds = list(profile.allowedKinds);
  var i;
  var k;

  if(!kinds.length){ return false; }

  for(i=0;i<kinds.length;i++){
    k = normText(kinds[i]);
    if(k && txt.indexOf(k) > -1){ return true; }
  }

  return false;
}


function isFoodIntent(intent){
  var id = intent && intent.id ? String(intent.id) : "";
  return id === "food" || id === "breakfast" || id === "dinner" || id === "dessert" || id === "coffee" || id === "nightlife";
}

function isLodgingPlace(item){
  return classifyPoi(item) === "lodging";
}


function isNamedRestaurantInsideLodging(item){
  var txt = getText(item);
  var name = getNameText(item);

  if(!isLodgingPlace(item)){ return false; }

  /* Allow only when the actual named POI is clearly the restaurant/bar itself,
     not the hotel property. */
  if(/\brestaurant\b|\bgrill\b|\bbar and grill\b|\bbistro\b|\bcafe\b|\bkitchen\b|\bsteakhouse\b|\bpizzeria\b|\bpub\b|\btavern\b|\bbakery\b|\bcoffee\b|\bice cream\b/.test(name)){
    return true;
  }

  if(/\binside\b|\bat\b|\blocated in\b/.test(txt) && /\brestaurant\b|\bgrill\b|\bbistro\b|\bcafe\b|\bkitchen\b|\bpub\b|\btavern\b/.test(txt)){
    return true;
  }

  return false;
}

function strictRejectForIntent(item,intent){
  var txt = getText(item);
  var id = intent && intent.id ? String(intent.id) : "";

  if(isFoodIntent(intent) && isLodgingPlace(item) && !isNamedRestaurantInsideLodging(item)){
    return "lodging/hotel is not a " + id + " recommendation";
  }

  if(id === "food" || id === "breakfast" || id === "dinner"){
    if(/\bgas station\b|\bconvenience store\b|\btruck stop\b/.test(txt) && !/\brestaurant\b|\bgrill\b|\bpizza\b|\bburger\b|\bcafe\b|\bcoffee\b|\bkitchen\b|\bdiner\b/.test(txt)){
      return "fuel/convenience POI is not a food recommendation";
    }
  }

  if(id === "coffee"){
    if(/\brestaurant\b|\bbar\b|\bpub\b|\bgrill\b/.test(txt) && !/\bcoffee\b|\bcafe\b|\bcafé\b|\bespresso\b|\broaster\b|\bbakery\b|\bdonut\b/.test(txt)){
      return "not a coffee/cafe destination";
    }
  }

  return "";
}

function applyCategoryTruth(items,intent,options,report){
  var profile = intent.profile || defaultProfile();
  var out = [];
  var i;
  var item;
  var txt;
  var hardMatch;
  var softMatch;
  var kindMatch;
  var blocked;
  var cls;
  var strictRejectReason;

  for(i=0;i<list(items).length;i++){
    item = items[i];
    txt = getPoiText(item);
    cls = item.poiClass || classifyPoi(item);
    item.poiClass = cls;

    if(!classAllowedForIntent(cls,intent)){
      addRemoval(report,"categoryTruth",item,"POI class " + cls + " not allowed for " + profile.label);
      continue;
    }

    strictRejectReason = strictRejectForIntent(item,intent);
    if(strictRejectReason){
      addRemoval(report,"categoryTruth",item,strictRejectReason);
      continue;
    }

    blocked = hasAnyTerm(txt,profile.blockedTerms);
    hardMatch = hasAnyTerm(txt,profile.hardTerms);
    softMatch = hasAnyTerm(txt,profile.softTerms);
    kindMatch = categoryKindAllowed(item,profile);

    /* Classifier is the first authority. Query text cannot create category truth.
       For Food IQ, a hotel named Best Western can never be rescued because cls=lodging
       was rejected above before hardTerms are checked. */
    if(blocked && !hardMatch){
      addRemoval(report,"categoryTruth",item,"blocked for " + profile.label);
      continue;
    }

    if(hardMatch){
      item.categoryTruth = 94;
    }else if(kindMatch && (intent.id === "local_favorites" || intent.id === "hidden_gems" || intent.id === "general")){
      item.categoryTruth = 78;
    }else if(softMatch && kindMatch){
      item.categoryTruth = 72;
    }else if((intent.id === "food" || intent.id === "breakfast" || intent.id === "dinner") && isFoodClass(cls)){
      item.categoryTruth = 82;
    }else{
      addRemoval(report,"categoryTruth",item,"failed POI/category allowlist for " + profile.label);
      continue;
    }

    out.push(item);
  }

  if(report){ report.counts.afterCategoryTruth = out.length; }
  return out;
}


function applyFreshnessTruth(items,intent,options,report){
  var out = [];
  var i;
  var item;
  var txt;
  var status;

  for(i=0;i<list(items).length;i++){
    item = items[i];
    txt = getText(item);
    status = normText(item.openStatus || item.status || item.businessStatus || "");

    if(status.indexOf("permanently closed") > -1 || status === "closed permanently"){
      addRemoval(report,"freshnessTruth",item,"permanently closed");
      continue;
    }

    if(txt.indexOf("permanently closed") > -1 || txt.indexOf("closed permanently") > -1){
      addRemoval(report,"freshnessTruth",item,"closed text detected");
      continue;
    }

    if(status.indexOf("temporarily closed") > -1){
      item.freshnessPenalty = -18;
      item.freshnessNote = "Temporarily closed signal";
    }else if(status.indexOf("open") > -1){
      item.freshnessBoost = 8;
    }

    out.push(item);
  }

  if(report){ report.counts.afterFreshnessTruth = out.length; }
  return out;
}

function itemDistance(item,center){
  var d;
  if(item && finite(Number(item.distMiles))){ return Number(item.distMiles); }
  if(item && finite(Number(item.distanceMiles))){ return Number(item.distanceMiles); }
  if(!center || !item){ return null; }

  d = miles(center.lat,center.lng,Number(item.lat),Number(item.lng));
  if(finite(d)){ item.distMiles = Math.round(d * 10) / 10; }
  return d;
}

function applyDistanceTruth(items,intent,options,report){
  var profile = intent.profile || defaultProfile();
  var center = (options && options.location) || null;
  var out = [];
  var i;
  var item;
  var d;
  var radius = Number(profile.radiusMiles || 25);
  var expanded = Number(profile.expandedRadiusMiles || radius);

  for(i=0;i<list(items).length;i++){
    item = items[i];
    d = itemDistance(item,center);

    if(finite(d)){
      item.distMiles = Math.round(d * 10) / 10;

      if(d > expanded){
        addRemoval(report,"distanceTruth",item,"outside expanded radius " + expanded + " mi");
        continue;
      }

      if(d > radius){
        item.distancePenalty = -10;
        item.expandedRadiusResult = true;
      }
    }

    out.push(item);
  }

  if(report){ report.counts.afterDistanceTruth = out.length; }
  return out;
}

function chainPenalty(item,profile){
  var txt = getText(item);
  if(profile && profile.allowChains){ return 0; }

  if(/\bstarbucks\b|\bmcdonald|wendy s|wendys|taco bell|subway|sonic|dunkin|chick fil a|cracker barrel|texas roadhouse|kfc\b/.test(txt)){ return -16; }
  return 0;
}

function scoreDistance(item,profile){
  var d = finite(Number(item.distMiles)) ? Number(item.distMiles) : null;
  var radius = Number(profile.radiusMiles || 25);
  var expanded = Number(profile.expandedRadiusMiles || radius);

  if(!finite(d)){ return 48; }
  if(d <= 1.5){ return 100; }
  if(d <= 4){ return 92; }
  if(d <= 8){ return 82; }
  if(d <= 15){ return 70; }
  if(d <= radius){ return 58; }
  if(d <= expanded){ return 42; }
  return 25;
}

function scoreRating(item){
  var rating = Number(item.rating || item.stars || 0);
  var reviews = Number(item.reviews || item.reviewCount || item.user_ratings_total || 0);
  var score = 50;

  if(finite(rating) && rating > 0){ score = Math.max(0,Math.min(100,(rating / 5) * 100)); }

  if(finite(reviews)){
    if(reviews >= 500){ score += 8; }
    else if(reviews >= 150){ score += 5; }
    else if(reviews >= 50){ score += 3; }
    else if(reviews > 0 && reviews < 10){ score -= 3; }
  }

  return Math.max(0,Math.min(100,score));
}

function scoreLocalFavorite(item){
  var txt = getText(item);
  var score = 45;

  if(item.localFavorite || item.isLocalFavorite){ score += 35; }
  if(/\blocal favorite|locals love|local hangout|neighborhood|family owned|independent|downtown|historic|main street|mom and pop|scratch\b/.test(txt)){ score += 22; }
  if(/\bstarbucks|mcdonald|walmart|target|best buy|subway|taco bell|kfc\b/.test(txt)){ score -= 16; }

  return Math.max(0,Math.min(100,score));
}

function scoreWorthDrive(item,intent){
  var txt = getText(item);
  var score = Number(item.worthScore || item.worthTheDriveScore || 52);

  if(/\bscenic|waterfront|lake|marina|live music|roaster|scratch|homemade|historic|patio|view|hidden gem|family owned|local\b/.test(txt)){ score += 18; }
  if(intent && intent.id === "coffee" && finite(Number(item.distMiles)) && Number(item.distMiles) > 18){ score -= 18; }
  if(intent && intent.id === "golf" && /\bpublic|links|championship|course|country club\b/.test(txt)){ score += 14; }
  if(intent && intent.id === "lake" && /\bmarina|boat ramp|beach|campground|waterfront|fishing\b/.test(txt)){ score += 18; }

  return Math.max(0,Math.min(100,score));
}

function scoreTaste(item,options){
  var taste = options && options.userTaste ? options.userTaste : null;
  var txt = getText(item);
  var score = 50;
  var i;
  var chips;

  if(!taste){ return score; }

  chips = taste.chips || taste.vibes || taste.preferences || [];
  for(i=0;i<list(chips).length;i++){
    if(txt.indexOf(normText(chips[i])) > -1){ score += 8; }
  }

  return Math.max(0,Math.min(100,score));
}

function calculateConfidence(item,intent,options){
  var confidence = Number(item.diqScore || 0);

  if(item.consumerDestination){ confidence += 5; }
  if(item.placeTruth){ confidence += 4; }
  if(Number(item.categoryTruth || 0) >= 80){ confidence += 5; }
  if(finite(Number(item.distMiles))){ confidence += 3; }
  if(item.placeId || item.googlePlaceId || item.mapboxId){ confidence += 4; }
  if(item.freshnessPenalty){ confidence -= 8; }
  if(item.distancePenalty){ confidence -= 4; }

  return Math.max(0,Math.min(100,Math.round(confidence)));
}

function scoreCandidates(items,intent,options,report){
  var profile = intent.profile || defaultProfile();
  var weights = profile.weights || {};
  var out = [];
  var i;
  var item;
  var score;
  var weightTotal;

  function w(name, fallback){ return Number(weights[name] || fallback || 0); }

  for(i=0;i<list(items).length;i++){
    item = items[i];

    score = 0;
    weightTotal = 0;

    function add(part,weight){
      score += Number(part || 0) * Number(weight || 0);
      weightTotal += Number(weight || 0);
    }

    add(Number(item.categoryTruth || 50),w("categoryTruth",20));
    add(scoreDistance(item,profile),w("distance",12));
    add(scoreRating(item),w("rating",12));
    add(scoreLocalFavorite(item),w("localFavorite",14));
    add(scoreWorthDrive(item,intent),w("worthDrive",14));
    add(scoreTaste(item,options),w("taste",8));

    if(w("vibeMatch",0)){ add(scoreTaste(item,options),w("vibeMatch",0)); }
    if(w("quality",0)){ add(scoreRating(item),w("quality",0)); }
    if(w("recreation",0)){ add(scoreWorthDrive(item,intent),w("recreation",0)); }
    if(w("liveSignal",0)){ add(scoreWorthDrive(item,intent),w("liveSignal",0)); }
    if(w("district",0)){ add(scoreLocalFavorite(item),w("district",0)); }
    if(w("familyFit",0)){ add(scoreTaste(item,options),w("familyFit",0)); }

    if(weightTotal <= 0){ weightTotal = 1; }

    item.diqScore = Math.round(score / weightTotal);
    item.diqScore += chainPenalty(item,profile);
    item.diqScore += Number(item.freshnessBoost || 0);
    item.diqScore += Number(item.freshnessPenalty || 0);
    item.diqScore += Number(item.distancePenalty || 0);
    item.diqScore = Math.max(0,Math.min(100,item.diqScore));
    item.confidence = calculateConfidence(item,intent,options);

    if(item.confidence < Number(profile.confidenceThreshold || 55)){
      addRemoval(report,"scoring",item,"below confidence threshold");
      continue;
    }

    out.push(item);
  }

  out.sort(function(a,b){ return Number(b.diqScore || 0) - Number(a.diqScore || 0); });
  if(report){ report.counts.afterScoring = out.length; }
  return out;
}

function varietyType(item,intent){
  var txt = getText(item);

  if(/\bcoffee|cafe|espresso|roaster\b/.test(txt)){ return "coffee"; }
  if(/\bbbq|barbecue|smokehouse\b/.test(txt)){ return "bbq"; }
  if(/\bpizza\b/.test(txt)){ return "pizza"; }
  if(/\bmexican|taco\b/.test(txt)){ return "mexican"; }
  if(/\bseafood|catfish|fish\b/.test(txt)){ return "seafood"; }
  if(/\bburger|wings|sandwich|chicken\b/.test(txt)){ return "casual_food"; }
  if(/\bsteak|italian|sushi|bistro\b/.test(txt)){ return "dinner"; }
  if(/\bbakery|ice cream|dessert|donut|cookie\b/.test(txt)){ return "treats"; }
  if(/\bbar|pub|brewery|cocktail\b/.test(txt)){ return "nightlife"; }
  if(/\bpark|trail|greenway|nature\b/.test(txt)){ return "parks"; }
  if(/\bgolf|course|country club\b/.test(txt)){ return "golf"; }
  if(/\bshop|boutique|market|mall|store\b/.test(txt)){ return "shopping"; }
  if(/\bmuseum|gallery|theater|zoo|arcade|bowling\b/.test(txt)){ return "attraction"; }

  return normText(item.category || item.kind || item.type || "general");
}

function applyVariety(items,intent,options,report){
  var out = [];
  var deferred = [];
  var seenTypes = {};
  var maxPerType = 3;
  var i;
  var item;
  var type;
  var requestedMax = Number(
    options && (
      options.targetResults ||
      options.maximumResults ||
      options.maxResults
    ) || 0
  );
  var profileMax = Number((intent.profile && intent.profile.maxResults) || 8);
  var max = Math.max(1, requestedMax || profileMax);

  /*
     Variety is a ranking preference, never a volume-destruction gate.
     Lead with a diverse mix, then append additional trustworthy places until
     the caller's requested target is reached. This lets Adventure request 20
     without losing valid restaurants merely because several share one type.
  */
  for(i=0;i<list(items).length;i++){
    item = items[i];
    type = varietyType(item,intent);

    if(!seenTypes[type]){ seenTypes[type] = 0; }

    if(seenTypes[type] >= maxPerType){
      deferred.push(item);
      continue;
    }

    seenTypes[type] += 1;
    out.push(item);

    if(out.length >= max){ break; }
  }

  for(i=0;i<deferred.length && out.length<max;i++){
    out.push(deferred[i]);
  }

  if(report){ report.counts.afterVariety = out.length; }
  return out;
}

function buildReason(item,intent,options){
  var bits = [];
  var d = finite(Number(item.distMiles)) ? Number(item.distMiles) : null;

  if(intent && intent.profile && intent.profile.label){ bits.push(intent.profile.label); }
  if(Number(item.diqScore || 0) >= 86){ bits.push("high-confidence pick"); }
  else if(item.localFavorite || item.isLocalFavorite){ bits.push("local favorite signal"); }
  if(finite(d)){ bits.push(String(Math.round(d * 10) / 10) + " mi away"); }
  if(item.freshnessBoost){ bits.push("open/freshness signal"); }

  return bits.slice(0,3).join(" • ");
}

function userFacingCategory(item,intent){
  var txt = getPoiText(item);
  var cls = item && item.poiClass ? item.poiClass : classifyPoi(item);

  if(cls === "coffee"){ return "Coffee"; }
  if(cls === "dessert"){ return "Treats"; }
  if(cls === "restaurant"){ return "Restaurant"; }
  if(cls === "nightlife"){ return "Nightlife"; }
  if(cls === "park"){ return "Park"; }
  if(cls === "outdoor_water"){ return "Outdoor"; }
  if(cls === "golf"){ return "Golf"; }
  if(cls === "shopping"){ return "Shopping"; }
  if(cls === "attraction"){ return "Attraction"; }

  if(/\bcoffee|cafe|espresso|roaster\b/.test(txt)){ return "Coffee"; }
  if(/\bbbq|barbecue|smokehouse\b/.test(txt)){ return "BBQ"; }
  if(/\bpizza\b/.test(txt)){ return "Pizza"; }
  if(/\bmexican|taco\b/.test(txt)){ return "Mexican"; }
  if(/\bseafood|catfish|fish\b/.test(txt)){ return "Seafood"; }
  if(/\brestaurant|grill|diner|kitchen|bistro|burger|sandwich|wings|steak|sushi\b/.test(txt)){ return "Restaurant"; }
  if(/\bbakery|ice cream|dessert|donut|cookie\b/.test(txt)){ return "Treats"; }
  if(/\bbar|pub|brewery|cocktail\b/.test(txt)){ return "Nightlife"; }
  if(/\bpark|trail|greenway|nature\b/.test(txt)){ return "Park"; }
  if(/\bgolf|course|country club\b/.test(txt)){ return "Golf"; }
  if(/\bshop|boutique|market|mall|store\b/.test(txt)){ return "Shopping"; }
  if(/\bmuseum|gallery|theater|zoo|arcade|bowling\b/.test(txt)){ return "Attraction"; }
  return intent && intent.profile ? intent.profile.label : "Place";
}


/* =====================================================================
   V3.3 FINAL RENDER AUTHORITY
   Nothing becomes a pin/card/top pick unless it still passes the
   selected category's POI classification truth immediately before render.
===================================================================== */

function isKnownHotelName(item){
  var txt = getPoiText(item);
  var name = getNameText(item);
  var both = normText(txt + " " + name);

  return /\bbest western\b|\bhampton inn\b|\bholiday inn\b|\bla quinta\b|\bcomfort inn\b|\bquality inn\b|\bsuper 8\b|\bdays inn\b|\bmotel 6\b|\bred roof inn\b|\bmarriott\b|\bhilton\b|\bhyatt\b|\bsheraton\b|\bwyndham\b|\bdrury inn\b|\becono lodge\b|\bsleep inn\b|\btravelodge\b|\bamericas best value\b|\bextended stay\b|\bhomewood suites\b|\bfairfield inn\b|\bcourtyard\b|\bresidence inn\b|\bspringhill suites\b/.test(both) ||
    /\bhotel\b|\bmotel\b|\binn\b|\bsuites\b|\blodging\b|\blodge\b|\bresort\b|\bguesthouse\b|\bbnb\b/.test(both);
}

function finalAuthorityRejectReason(item,intent){
  var id = intent && intent.id ? String(intent.id) : "general";
  var cls = item && item.poiClass ? String(item.poiClass) : classifyPoi(item);
  var txt = getPoiText(item);
  var name = getNameText(item);

  if(!item){ return "missing item"; }

  if(cls === "admin" || cls === "road"){ return "final block: city/admin/road"; }
  if(cls === "radio"){ return "final block: radio station"; }
  if(cls === "government" || cls === "school" || cls === "worship" || cls === "medical" || cls === "utility" || cls === "office" || cls === "residential"){
    return "final block: non-consumer " + cls;
  }

  if(cls === "lodging" || isKnownHotelName(item)){
    return "final block: lodging/hotel";
  }

  if(!classAllowedForIntent(cls,intent)){
    return "final block: " + cls + " not allowed for " + id;
  }

  if((id === "food" || id === "breakfast" || id === "dinner") && !isFoodClass(cls)){
    return "final block: not food class";
  }

  if(id === "coffee" && !(cls === "coffee" || cls === "dessert")){
    return "final block: not coffee/cafe class";
  }

  if(id === "local_favorites" || id === "hidden_gems" || id === "general"){
    if(/^city of\b/.test(name) || /^village of\b/.test(name) || /^town of\b/.test(name) || /\b(administrative|boundary|postcode|locality|municipality)\b/.test(txt)){
      return "final block: map/admin label";
    }
  }

  return "";
}

function applyFinalRenderAuthority(items,intent,options,report){
  var out = [];
  var i;
  var item;
  var reason;

  for(i=0;i<list(items).length;i++){
    item = items[i];
    item.poiClass = item.poiClass || classifyPoi(item);
    reason = finalAuthorityRejectReason(item,intent);

    if(reason){
      addRemoval(report,"finalAuthority",item,reason);
      continue;
    }

    item.finalAuthority = true;
    out.push(item);
  }

  if(report){ report.counts.afterFinalAuthority = out.length; }
  return out;
}

function finalizeResults(items,intent,options,report){
  var out = [];
  var i;
  var item;

  for(i=0;i<list(items).length;i++){
    item = clone(items[i]);
    item.scanEngineBuild = BUILD;
    item.recommendationProfile = intent.profile.label;
    item.recommendedBecause = buildReason(item,intent,options);
    item.poiClass = item.poiClass || classifyPoi(item);
    item.userFacingCategory = userFacingCategory(item,intent);
    item.isRealResult = true;
    out.push(item);
  }

  return out;
}

/* =====================================================================
   PUBLIC API
===================================================================== */

function scan(options){
  var startedAt = Date.now();
  var intent = resolveIntent(options || {});
  var report = newReport(options || {},intent);
  var candidates;
  var consumerTruth;
  var placeTruth;
  var categoryTruth;
  var freshnessTruth;
  var distanceTruth;
  var scored;
  var varied;
  var finalResults;

  options = options || {};

  candidates = pullCandidates(options,report);
  consumerTruth = applyConsumerDestinationGate(candidates,intent,options,report);
  placeTruth = applyPlaceTruth(consumerTruth,options,report);
  categoryTruth = applyCategoryTruth(placeTruth,intent,options,report);
  freshnessTruth = applyFreshnessTruth(categoryTruth,intent,options,report);
  distanceTruth = applyDistanceTruth(freshnessTruth,intent,options,report);
  scored = scoreCandidates(distanceTruth,intent,options,report);
  varied = applyVariety(scored,intent,options,report);
  var finalAuthority = applyFinalRenderAuthority(varied,intent,options,report);
  finalResults = finalizeResults(finalAuthority,intent,options,report);

  finishReport(report,startedAt,finalResults,options);

  return {
    ok:true,
    build:BUILD,
    intent:intent.id,
    profile:intent.profile,
    results:finalResults,
    report:report
  };
}

function enableDebug(){
  try{ localStorage.setItem(DEBUG_KEY,"1"); }catch(e){}
  window.DRIVE_IQ_SCAN_DEBUG = true;
  return true;
}

function disableDebug(){
  try{ localStorage.removeItem(DEBUG_KEY); }catch(e){}
  window.DRIVE_IQ_SCAN_DEBUG = false;
  return true;
}

function lastReport(){ return safeJsonGet(LAST_REPORT_KEY); }

window.DriveIQScanEngine = {
  build:BUILD,
  profiles:CATEGORY_PROFILES,
  scan:scan,
  resolveIntent:resolveIntent,
  enableDebug:enableDebug,
  disableDebug:disableDebug,
  lastReport:lastReport
};

try{ console.log("🧠 DriveIQScanEngine loaded",BUILD); }catch(e){}




/* =====================================================================
   DRIVE IQ — SCAN ENGINE V4
   ENTITY IQ + DISTRICT IQ FOUNDATION
   - Classifies results as Place IQ or District IQ before final render.
   - Adds high-confidence district registry for known destination clusters.
   - Lets districts rank confidently for matching broad/discovery intents.
   - Keeps Food/Coffee/Golf truth strict.
   ES5 SAFE
===================================================================== */

var DIQ_V4_ENTITY_BUILD = "V4_ENTITY_IQ_DISTRICT_IQ_FOUNDATION";

var DIQ_V4_DISTRICT_REGISTRY = [
  { id:"harborwalk_village_destin", label:"HarborWalk Village", aliases:["harborwalk village","harbor walk village","destin harborwalk","destin harbor walk","harborwalk destin","harbor walk destin"], city:"Destin", state:"FL", lat:30.3935, lng:-86.5104, districtType:"adventure", tags:["waterfront","entertainment","nightlife","food","shopping","activities","live music","boating"], authority:99, experienceCount:18, summary:"Waterfront entertainment district with restaurants, bars, shopping, live music, and harbor activities.", topPlaceNames:["AJ's Seafood & Oyster Bar","Harry T's","Margaritaville","Coyote Ugly","Tailfins","Dewey Destin's","Harbor Docks","Boogies Watersports"], bestFor:["Local Favorites IQ","Nightlife IQ","Waterfront","Things To Do","Adventure IQ"] },
  { id:"destin_commons", label:"Destin Commons", aliases:["destin commons","the destin commons"], city:"Destin", state:"FL", lat:30.3909, lng:-86.4271, districtType:"shopping", tags:["shopping","retail","restaurants","movies","family","entertainment","coffee"], authority:96, experienceCount:45, summary:"Retail-first shopping district with stores, restaurants, coffee, and entertainment.", topPlaceNames:["Bass Pro Shops","AMC Destin Commons","Belk","H&M","Uncle Buck's FishBowl","Starbucks"], bestFor:["Shopping IQ","Family Fun IQ","Rainy Day","Retail"] },
  { id:"silo_square_southaven", label:"Silo Square", aliases:["silo square","silo square southaven","silo square ms"], city:"Southaven", state:"MS", lat:34.9619, lng:-89.9907, districtType:"local", tags:["local","restaurants","coffee","events","shops","patios","community"], authority:93, experienceCount:12, summary:"Walkable local district with restaurants, coffee, events, shops, patios, and community gathering spots.", topPlaceNames:["Local restaurants","Coffee","Community events","Boutiques","Patios"], bestFor:["Local Favorites IQ","Food IQ","Coffee IQ","Community"] },
  { id:"beale_street_memphis", label:"Beale Street", aliases:["beale street","beale st","beale street memphis"], city:"Memphis", state:"TN", lat:35.1398, lng:-90.0524, districtType:"entertainment", tags:["nightlife","live music","bars","food","tourism","walkable","iconic"], authority:98, experienceCount:25, summary:"Iconic entertainment district known for live music, nightlife, restaurants, and Memphis energy.", topPlaceNames:["B.B. King's Blues Club","Rum Boogie Cafe","Silky O'Sullivan's","A. Schwab","Handy Park"], bestFor:["Nightlife IQ","Live Music","Local Favorites IQ","Things To Do"] },
  { id:"broadway_nashville", label:"Broadway Nashville", aliases:["broadway nashville","lower broadway","broadway downtown nashville","honky tonk highway"], city:"Nashville", state:"TN", lat:36.1611, lng:-86.7789, districtType:"entertainment", tags:["nightlife","live music","bars","food","tourism","walkable","iconic"], authority:99, experienceCount:35, summary:"High-energy entertainment district with honky-tonks, live music, rooftop bars, restaurants, and downtown Nashville energy.", topPlaceNames:["Tootsie's Orchid Lounge","The Stage","Robert's Western World","Acme Feed & Seed","Assembly Food Hall"], bestFor:["Nightlife IQ","Live Music","Local Favorites IQ","Things To Do"] },
  { id:"the_square_oxford", label:"The Square", aliases:["the square oxford","oxford square","historic square oxford"], city:"Oxford", state:"MS", lat:34.3668, lng:-89.5192, districtType:"local", tags:["restaurants","shopping","coffee","nightlife","local","walkable","college town"], authority:95, experienceCount:20, summary:"Walkable local district with restaurants, boutiques, coffee, bookstores, nightlife, and Oxford character.", topPlaceNames:["City Grocery","Ajax Diner","Square Books","Boure","Proud Larry's"], bestFor:["Local Favorites IQ","Food IQ","Shopping IQ","Nightlife IQ"] },
  { id:"downtown_gatlinburg", label:"Downtown Gatlinburg", aliases:["downtown gatlinburg","gatlinburg strip","the strip gatlinburg","gatlinburg downtown"], city:"Gatlinburg", state:"TN", lat:35.7110, lng:-83.5186, districtType:"adventure", tags:["attractions","food","shopping","family","walkable","tourism","mountains"], authority:97, experienceCount:40, summary:"Walkable mountain-town district with attractions, food, shops, family activities, and Smoky Mountain energy.", topPlaceNames:["Anakeesta","Ripley's Aquarium","Ole Smoky","The Village Shops","SkyPark"], bestFor:["Family Fun IQ","Local Favorites IQ","Shopping IQ","Things To Do"] },
  { id:"disney_springs", label:"Disney Springs", aliases:["disney springs","downtown disney orlando"], city:"Lake Buena Vista", state:"FL", lat:28.3702, lng:-81.5197, districtType:"adventure", tags:["shopping","restaurants","entertainment","family","nightlife","tourism","walkable"], authority:99, experienceCount:80, summary:"Large entertainment, dining, and shopping district with restaurants, shops, shows, and family experiences.", topPlaceNames:["The Boathouse","World of Disney","Gideon's Bakehouse","Raglan Road","House of Blues"], bestFor:["Shopping IQ","Family Fun IQ","Dinner IQ","Things To Do"] },
  { id:"the_wharf_orange_beach", label:"The Wharf", aliases:["the wharf orange beach","the wharf","orange beach wharf"], city:"Orange Beach", state:"AL", lat:30.2946, lng:-87.6289, districtType:"adventure", tags:["waterfront","restaurants","shopping","events","amphitheater","family","nightlife"], authority:96, experienceCount:25, summary:"Waterfront adventure district with restaurants, shops, events, concerts, activities, and marina energy.", topPlaceNames:["The Wharf Amphitheater","Villaggio Grille","YoHo Rum & Tacos","Ferris Wheel","Marina"], bestFor:["Local Favorites IQ","Nightlife IQ","Shopping IQ","Family Fun IQ"] }
];

function diqV4DistrictText(item){
  return normText(String(item && item.label || "") + " " + String(item && item.name || "") + " " + String(item && item.full || "") + " " + String(item && item.address || "") + " " + String(item && item.category || "") + " " + String(item && item.kind || "") + " " + String(item && item.type || "") + " " + String(item && item.matchLabel || ""));
}

function diqV4IntentAllowsDistrict(intent,districtType){
  var id = intent && intent.id ? String(intent.id) : "general";
  if(id === "coffee" || id === "breakfast" || id === "dinner" || id === "dessert" || id === "food" || id === "golf"){ return false; }
  if(id === "shopping"){ return districtType === "shopping" || districtType === "local" || districtType === "adventure"; }
  if(id === "nightlife"){ return districtType === "entertainment" || districtType === "adventure" || districtType === "waterfront"; }
  if(id === "lake" || id === "parks"){ return districtType === "waterfront" || districtType === "adventure" || districtType === "beach"; }
  return id === "general" || id === "local_favorites" || id === "hidden_gems" || id === "family_fun";
}

function diqV4DistrictPrimaryLabel(district,intent){
  var id = intent && intent.id ? String(intent.id) : "general";
  var type = district && district.districtType ? String(district.districtType) : "adventure";
  var tags = district && district.tags ? normText(district.tags.join(" ")) : "";
  if(id === "shopping"){ return "Shopping District"; }
  if(id === "nightlife"){ return "Entertainment District"; }
  if((id === "lake" || id === "parks") && tags.indexOf("waterfront") > -1){ return "Waterfront District"; }
  if(type === "shopping"){ return "Shopping District"; }
  if(type === "entertainment"){ return "Entertainment District"; }
  if(type === "local"){ return "Local District"; }
  if(type === "waterfront"){ return "Waterfront District"; }
  if(type === "beach"){ return "Beach District"; }
  if(type === "arts"){ return "Arts District"; }
  return "Adventure District";
}

function diqV4DistrictIntentBoost(district,intent){
  var id = intent && intent.id ? String(intent.id) : "general";
  var type = district && district.districtType ? String(district.districtType) : "adventure";
  var tags = district && district.tags ? normText(district.tags.join(" ")) : "";
  var boost = 0;
  if(id === "shopping"){
    if(type === "shopping"){ boost += 28; }
    if(tags.indexOf("shopping") > -1 || tags.indexOf("retail") > -1){ boost += 12; }
    if(type === "entertainment"){ boost -= 14; }
  }else if(id === "nightlife"){
    if(type === "entertainment" || type === "adventure"){ boost += 24; }
    if(tags.indexOf("nightlife") > -1 || tags.indexOf("live music") > -1 || tags.indexOf("bars") > -1){ boost += 16; }
    if(type === "shopping"){ boost -= 10; }
  }else if(id === "local_favorites" || id === "hidden_gems" || id === "general"){
    if(type === "adventure" || type === "local" || type === "entertainment"){ boost += 22; }
    if(tags.indexOf("restaurants") > -1 || tags.indexOf("food") > -1 || tags.indexOf("events") > -1 || tags.indexOf("waterfront") > -1){ boost += 12; }
  }else if(id === "family_fun"){
    if(tags.indexOf("family") > -1 || tags.indexOf("attractions") > -1 || tags.indexOf("entertainment") > -1){ boost += 22; }
  }else if(id === "lake" || id === "parks"){
    if(tags.indexOf("waterfront") > -1 || tags.indexOf("beach") > -1 || tags.indexOf("activities") > -1){ boost += 18; }
  }
  return boost;
}

function diqV4FindRegistryDistrict(item,options){
  var txt = diqV4DistrictText(item);
  var i, j, d, alias, best = null, bestScore = 0, dist, center = options && options.location ? options.location : null;
  for(i=0;i<DIQ_V4_DISTRICT_REGISTRY.length;i++){
    d = DIQ_V4_DISTRICT_REGISTRY[i];
    for(j=0;j<list(d.aliases).length;j++){
      alias = normText(d.aliases[j]);
      if(!alias){ continue; }
      if(txt === alias){ bestScore = 1000; best = d; }
      else if(txt.indexOf(alias) > -1 && 780 > bestScore){ bestScore = 780; best = d; }
      else if(alias.indexOf(txt) > -1 && txt.length >= 6 && 620 > bestScore){ bestScore = 620; best = d; }
    }
    if(center && finite(Number(center.lat)) && finite(Number(center.lng))){
      dist = miles(center.lat,center.lng,d.lat,d.lng);
      if(finite(dist) && dist <= 2 && txt.indexOf(normText(d.city)) > -1 && 540 > bestScore){ bestScore = 540; best = d; }
    }
  }
  return bestScore >= 540 ? best : null;
}

function diqV4LooksLikeGenericContainer(item){
  var txt = diqV4DistrictText(item);
  return /\bshopping center\b|\bshopping centre\b|\bmall\b|\bplaza\b|\bretail center\b|\bretail centre\b|\boutlets?\b|\bvillage\b|\bcommons\b|\btown center\b|\btown centre\b|\bmarketplace\b|\bmarket place\b|\bboardwalk\b|\bharborwalk\b|\bwharf\b|\bdowntown\b|\bsquare\b|\bdistrict\b/.test(txt);
}

function diqV4InferDistrictType(item,registry){
  var txt = diqV4DistrictText(item);
  if(registry && registry.districtType){ return registry.districtType; }
  if(/\boutlet|mall|shopping center|shopping centre|retail|commons|marketplace|market place\b/.test(txt)){ return "shopping"; }
  if(/\bbar|nightlife|live music|broadway|beale|entertainment|honky tonk|music venue\b/.test(txt)){ return "entertainment"; }
  if(/\bharbor|harbour|waterfront|wharf|marina|boardwalk|beach\b/.test(txt)){ return "adventure"; }
  if(/\bdowntown|square|main street|historic|silo square\b/.test(txt)){ return "local"; }
  return "adventure";
}

function diqV4ThumbCategory(label,item){
  var txt = normText(label + " " + String(item && item.districtTags ? item.districtTags.join(" ") : ""));
  if(/\bcoffee|cafe|brew|starbucks\b/.test(txt)){ return "Coffee"; }
  if(/\bbar|ugly|tailfins|pub|music|lounge|honky|blues\b/.test(txt)){ return "Nightlife"; }
  if(/\bshop|belk|h m|bass pro|world of disney|books|boutique\b/.test(txt)){ return "Shopping"; }
  if(/\bwatersports|marina|aquarium|anakeesta|skypark|amphitheater|ferris|boogies\b/.test(txt)){ return "Activity"; }
  return "Food";
}

function diqV4DistrictThumbnailCards(item){
  var names = item && item.topPlaceNames ? item.topPlaceNames : [];
  var out = [];
  var i, label;
  for(i=0;i<list(names).length && i<6;i++){
    label = clean(names[i]);
    if(!label){ continue; }
    out.push({ label:label, entityType:"place_preview", parentDistrictId:item.districtId || "", category:diqV4ThumbCategory(label,item), recommendedBecause:"Top experience inside " + clean(item.label || "this district") });
  }
  return out;
}

function diqV4DistrictFromItem(item,intent,options,registry){
  var district = registry || {};
  var out = clone(item || {});
  var districtType = diqV4InferDistrictType(item,registry);
  var scoreBase = Number((registry && registry.authority) || 76);
  var intentBoost = diqV4DistrictIntentBoost({districtType:districtType,tags:(registry && registry.tags) || []},intent);
  out.entityType = "district";
  out.entityClass = "district";
  out.districtId = clean(district.id || normText(out.label || out.name || "district"));
  out.districtType = districtType;
  out.districtLabel = diqV4DistrictPrimaryLabel({districtType:districtType,tags:(registry && registry.tags) || []},intent);
  out.districtTags = district.tags && district.tags.slice ? district.tags.slice(0) : [];
  out.experienceCount = Number(district.experienceCount || out.experienceCount || 8);
  out.districtSummary = clean(district.summary || out.description || "A multi-stop destination with several experiences nearby.");
  out.topPlaceNames = district.topPlaceNames && district.topPlaceNames.slice ? district.topPlaceNames.slice(0,8) : [];
  out.bestFor = district.bestFor && district.bestFor.slice ? district.bestFor.slice(0,8) : [];
  out.label = clean(district.label || out.label || out.name || "Adventure District");
  out.full = clean(out.full || ((district.city && district.state) ? district.city + ", " + district.state : ""));
  out.lat = finite(Number(district.lat)) ? Number(district.lat) : out.lat;
  out.lng = finite(Number(district.lng)) ? Number(district.lng) : out.lng;
  out.poiClass = "district";
  out.consumerDestination = true;
  out.placeTruth = true;
  out.categoryTruth = Math.max(Number(out.categoryTruth || 0), 94);
  out.districtAuthority = Math.max(0,Math.min(100,scoreBase));
  out.districtIntentBoost = intentBoost;
  out.districtConfidence = Math.max(0,Math.min(100,Math.round(scoreBase + (intentBoost * 0.35))));
  out.userFacingCategory = out.districtLabel;
  out.recommendationProfile = intent && intent.profile ? intent.profile.label : "Adventure IQ";
  out.recommendedBecause = out.districtLabel + " • " + out.experienceCount + " experiences inside";
  out.districtThumbnailCards = diqV4DistrictThumbnailCards(out);
  out.isRealResult = true;
  return out;
}

function diqV4EntityCounts(items){
  var counts = { place:0, district:0 };
  var i;
  for(i=0;i<list(items).length;i++){
    if(items[i] && items[i].entityType === "district"){ counts.district += 1; }
    else{ counts.place += 1; }
  }
  return counts;
}

function diqV4ApplyEntityIQ(items,intent,options,report){
  var out = [];
  var i, item, registry, isDistrictCandidate, districtType;
  for(i=0;i<list(items).length;i++){
    item = items[i];
    registry = diqV4FindRegistryDistrict(item,options || {});
    isDistrictCandidate = !!registry || diqV4LooksLikeGenericContainer(item);
    districtType = registry ? registry.districtType : diqV4InferDistrictType(item,null);
    if(isDistrictCandidate && diqV4IntentAllowsDistrict(intent,districtType)){
      out.push(diqV4DistrictFromItem(item,intent,options || {},registry));
    }else{
      item.entityType = "place";
      item.entityClass = "place";
      out.push(item);
    }
  }
  if(report){
    report.counts.afterEntityIQ = out.length;
    report.entityCounts = diqV4EntityCounts(out);
  }
  return out;
}

function diqV4DistrictScore(item,intent){
  var score = Number(item.diqScore || 0);
  if(item && item.entityType === "district"){
    score += Number(item.districtAuthority || 0) * 0.18;
    score += Number(item.districtIntentBoost || 0);
    score += Math.min(14, Number(item.experienceCount || 0) * 0.45);
    if(item.districtType === "shopping" && intent && intent.id === "shopping"){ score += 16; }
    if((item.districtType === "entertainment" || item.districtType === "adventure") && intent && intent.id === "nightlife"){ score += 14; }
    if((item.districtType === "adventure" || item.districtType === "local") && intent && (intent.id === "local_favorites" || intent.id === "general" || intent.id === "hidden_gems")){ score += 14; }
  }
  return Math.max(0,Math.min(100,Math.round(score)));
}

function diqV4UpgradeDistrictScores(items,intent,report){
  var i, item;
  for(i=0;i<list(items).length;i++){
    item = items[i];
    if(item && item.entityType === "district"){
      item.diqScore = diqV4DistrictScore(item,intent);
      item.confidence = Math.max(Number(item.confidence || 0), Number(item.districtConfidence || 0), 88);
      item.recommendedBecause = item.districtLabel + " • " + item.experienceCount + " experiences inside";
    }
  }
  items.sort(function(a,b){
    var ae = a && a.entityType === "district" ? 1 : 0;
    var be = b && b.entityType === "district" ? 1 : 0;
    if(Number(b.diqScore || 0) !== Number(a.diqScore || 0)){ return Number(b.diqScore || 0) - Number(a.diqScore || 0); }
    return be - ae;
  });
  if(report){ report.counts.afterDistrictScoring = items.length; }
  return items;
}

var diqV4PriorClassAllowedForIntent = classAllowedForIntent;
classAllowedForIntent = function(cls,intent){
  if(cls === "district"){ return diqV4IntentAllowsDistrict(intent,"adventure"); }
  return diqV4PriorClassAllowedForIntent(cls,intent);
};

var diqV4PriorFinalAuthorityRejectReason = finalAuthorityRejectReason;
finalAuthorityRejectReason = function(item,intent){
  if(item && item.entityType === "district"){
    if(!diqV4IntentAllowsDistrict(intent,item.districtType || "adventure")){
      return "final block: district not allowed for narrow " + (intent && intent.id ? intent.id : "intent");
    }
    return "";
  }
  return diqV4PriorFinalAuthorityRejectReason(item,intent);
};

var diqV4PriorFinalizeResults = finalizeResults;
finalizeResults = function(items,intent,options,report){
  var out = diqV4PriorFinalizeResults(items,intent,options,report);
  var i;
  for(i=0;i<list(out).length;i++){
    if(out[i] && out[i].entityType === "district"){
      out[i].scanEngineBuild = DIQ_V4_ENTITY_BUILD;
      out[i].userFacingCategory = out[i].districtLabel || "District";
      out[i].recommendedBecause = out[i].districtLabel + " • " + out[i].experienceCount + " experiences inside";
      out[i].districtThumbnailCards = diqV4DistrictThumbnailCards(out[i]);
    }else if(out[i]){
      out[i].entityType = out[i].entityType || "place";
      out[i].entityClass = out[i].entityClass || "place";
    }
  }
  return out;
};

scan = function(options){
  var startedAt = Date.now();
  var intent = resolveIntent(options || {});
  var report = newReport(options || {},intent);
  var candidates, consumerTruth, placeTruth, categoryTruth, freshnessTruth, distanceTruth, entityTruth, scored, districtScored, varied, finalAuthority, finalResults;

  options = options || {};
  candidates = pullCandidates(options,report);
  consumerTruth = applyConsumerDestinationGate(candidates,intent,options,report);
  placeTruth = applyPlaceTruth(consumerTruth,options,report);
  categoryTruth = applyCategoryTruth(placeTruth,intent,options,report);
  freshnessTruth = applyFreshnessTruth(categoryTruth,intent,options,report);
  distanceTruth = applyDistanceTruth(freshnessTruth,intent,options,report);
  entityTruth = diqV4ApplyEntityIQ(distanceTruth,intent,options,report);
  scored = scoreCandidates(entityTruth,intent,options,report);
  districtScored = diqV4UpgradeDistrictScores(scored,intent,report);
  varied = applyVariety(districtScored,intent,options,report);
  finalAuthority = applyFinalRenderAuthority(varied,intent,options,report);
  finalResults = finalizeResults(finalAuthority,intent,options,report);
  finishReport(report,startedAt,finalResults,options);

  return { ok:true, build:DIQ_V4_ENTITY_BUILD, intent:intent.id, profile:intent.profile, results:finalResults, report:report };
};

try{
  window.DriveIQScanEngine.scan = scan;
  window.DriveIQScanEngine.build = DIQ_V4_ENTITY_BUILD;
  window.DriveIQScanEngine.entityIQ = {
    build:DIQ_V4_ENTITY_BUILD,
    districts:DIQ_V4_DISTRICT_REGISTRY,
    classify:function(item,intentId){
      var intent = { id:intentId || "general", profile:defaultProfile() };
      var registry = diqV4FindRegistryDistrict(item || {},{});
      if(registry || diqV4LooksLikeGenericContainer(item || {})){ return diqV4DistrictFromItem(item || {},intent,{},registry); }
      item = clone(item || {});
      item.entityType = "place";
      item.entityClass = "place";
      return item;
    }
  };
  console.log("🧠 DriveIQScanEngine V4 Entity IQ + District IQ loaded", DIQ_V4_ENTITY_BUILD);
}catch(eV4){
  try{ console.warn("DriveIQScanEngine V4 failed to attach",eV4); }catch(eV4b){}
}




/* =====================================================================
   DRIVE IQ — SCAN ENGINE V4.1
   ENTITY INJECTION IQ
   - Injects known nearby District IQ entities into the candidate pool
     before Place Truth / Category Truth / Ranking.
   - Fixes the issue where HarborWalk cannot rank if provider pull never
     includes HarborWalk as a candidate.
   - Keeps narrow scans strict: Food/Coffee/Golf do not get district injection.
   - ES5-safe. No optional chaining. No arrow functions. No template literals.
===================================================================== */

var DIQ_V41_ENTITY_INJECTION_BUILD = "V4_1_ENTITY_INJECTION_IQ";

function diqV41IntentAllowsEntityInjection(intent) {
  var id = intent && intent.id ? String(intent.id) : "general";
  if (id === "food" || id === "coffee" || id === "breakfast" || id === "dinner" || id === "dessert" || id === "golf") { return false; }
  return id === "general" || id === "local_favorites" || id === "hidden_gems" || id === "shopping" || id === "nightlife" || id === "family_fun" || id === "parks" || id === "lake";
}

function diqV41RegistryList() {
  if (typeof DIQ_V4_DISTRICT_REGISTRY !== "undefined" && DIQ_V4_DISTRICT_REGISTRY && DIQ_V4_DISTRICT_REGISTRY.length) {
    return DIQ_V4_DISTRICT_REGISTRY;
  }
  return [];
}

function diqV41DistrictTypeAllowed(intent, district) {
  var id = intent && intent.id ? String(intent.id) : "general";
  var type = district && district.districtType ? String(district.districtType) : "adventure";
  var tags = district && district.tags ? normText(district.tags.join(" ")) : "";

  if (!diqV41IntentAllowsEntityInjection(intent)) { return false; }

  if (id === "shopping") {
    return type === "shopping" || type === "local" || tags.indexOf("shopping") > -1 || tags.indexOf("retail") > -1;
  }

  if (id === "nightlife") {
    return type === "entertainment" || type === "adventure" || tags.indexOf("nightlife") > -1 || tags.indexOf("live music") > -1 || tags.indexOf("bars") > -1;
  }

  if (id === "parks" || id === "lake") {
    return tags.indexOf("waterfront") > -1 || tags.indexOf("beach") > -1 || tags.indexOf("activities") > -1 || type === "adventure";
  }

  if (id === "family_fun") {
    return tags.indexOf("family") > -1 || tags.indexOf("entertainment") > -1 || tags.indexOf("attractions") > -1 || type === "adventure" || type === "shopping";
  }

  return true;
}

function diqV41InjectionRadius(intent, options) {
  var id = intent && intent.id ? String(intent.id) : "general";
  var profile = intent && intent.profile ? intent.profile : defaultProfile();
  var base = Number(profile.expandedRadiusMiles || profile.radiusMiles || 60);

  if (id === "shopping") { return Math.max(base, 45); }
  if (id === "nightlife") { return Math.max(base, 55); }
  if (id === "local_favorites" || id === "hidden_gems" || id === "general") { return Math.max(base, 70); }
  if (id === "family_fun") { return Math.max(base, 60); }
  if (id === "parks" || id === "lake") { return Math.max(base, 85); }

  return base;
}

function diqV41Center(options) {
  var loc = options && options.location ? options.location : null;
  if (loc && finite(Number(loc.lat)) && finite(Number(loc.lng))) {
    return { lat:Number(loc.lat), lng:Number(loc.lng) };
  }

  if (options && options.destination && finite(Number(options.destination.lat)) && finite(Number(options.destination.lng))) {
    return { lat:Number(options.destination.lat), lng:Number(options.destination.lng) };
  }

  return null;
}

function diqV41DistrictItem(entity, intent, options, distanceMiles) {
  var item;

  if (typeof diqV4DistrictFromItem === "function") {
    item = diqV4DistrictFromItem({
      label:entity.label,
      full:(entity.city && entity.state) ? entity.city + ", " + entity.state : "",
      lat:Number(entity.lat),
      lng:Number(entity.lng),
      kind:"district",
      category:entity.districtType || "district",
      resultType:"entity_iq_injected_district"
    }, intent, options || {}, entity);
  } else {
    item = {
      label:String(entity.label || "District"),
      full:String((entity.city || "") + (entity.state ? ", " + entity.state : "")),
      lat:Number(entity.lat),
      lng:Number(entity.lng),
      kind:"district",
      category:String(entity.districtType || "district"),
      resultType:"entity_iq_injected_district",
      entityType:"district",
      entityClass:"district",
      districtId:String(entity.id || ""),
      districtType:String(entity.districtType || "adventure"),
      districtLabel:String(entity.districtType || "Adventure District"),
      districtTags:entity.tags || [],
      experienceCount:Number(entity.experienceCount || 0),
      topPlaceNames:entity.topPlaceNames || [],
      districtSummary:String(entity.summary || ""),
      districtAuthority:Number(entity.authority || 90),
      consumerDestination:true,
      placeTruth:true,
      categoryTruth:94
    };
  }

  item.source = "entity_iq_injection";
  item.injectedEntity = true;
  item.resultType = "entity_iq_injected_district";
  item.entityType = "district";
  item.entityClass = "district";
  item.poiClass = "district";
  item.distMiles = finite(Number(distanceMiles)) ? Math.round(Number(distanceMiles) * 10) / 10 : item.distMiles;
  item.distanceMiles = item.distMiles;
  item.categoryTruth = Math.max(Number(item.categoryTruth || 0), 94);
  item.consumerDestination = true;
  item.placeTruth = true;
  item.diqScore = Math.max(Number(item.diqScore || 0), Number(entity.authority || 90));
  item.confidence = Math.max(Number(item.confidence || 0), 90);
  item.recommendedBecause = String(item.districtLabel || "District") + " • " + String(item.experienceCount || 0) + " experiences inside";

  return item;
}

function diqV41InjectEntityCandidates(candidates, intent, options, report) {
  var out = (candidates || []).slice(0);
  var registry = diqV41RegistryList();
  var center = diqV41Center(options || {});
  var radius = diqV41InjectionRadius(intent, options || {});
  var injected = [];
  var i;
  var entity;
  var d;
  var item;

  if (!center || !diqV41IntentAllowsEntityInjection(intent)) {
    if (report) {
      report.counts.afterEntityInjection = out.length;
      report.entityInjection = { injected:0, reason:!center ? "missing_center" : "narrow_intent" };
    }
    return out;
  }

  for (i = 0; i < registry.length; i++) {
    entity = registry[i];
    if (!entity || !finite(Number(entity.lat)) || !finite(Number(entity.lng))) { continue; }
    if (!diqV41DistrictTypeAllowed(intent, entity)) { continue; }

    d = miles(center.lat, center.lng, Number(entity.lat), Number(entity.lng));
    if (!finite(Number(d))) { continue; }
    if (Number(d) > radius) { continue; }

    item = diqV41DistrictItem(entity, intent, options || {}, d);
    injected.push(item);
    out.push(item);
  }

  out = dedupeCandidates(out);

  if (report) {
    report.counts.afterEntityInjection = out.length;
    report.entityInjection = {
      build:DIQ_V41_ENTITY_INJECTION_BUILD,
      injected:injected.length,
      radiusMiles:radius,
      names:(function(){
        var names = [];
        var j;
        for (j = 0; j < injected.length; j++) { names.push(injected[j].label); }
        return names;
      })()
    };
    if (injected.length) {
      report.notes.push("Entity Injection IQ added known District IQ destinations before ranking.");
    }
  }

  return out;
}

/* Districts are visitable consumer destinations for broad/discovery scans. */
var diqV41PriorLooksVisitableConsumerPlace = looksVisitableConsumerPlace;
looksVisitableConsumerPlace = function(item) {
  if (item && item.entityType === "district") { return true; }
  return diqV41PriorLooksVisitableConsumerPlace(item);
};

/* Districts should pass the consumer gate when already injected/classified. */
var diqV41PriorIsAdminOrMapLabel = isAdminOrMapLabel;
isAdminOrMapLabel = function(item) {
  if (item && item.entityType === "district") { return false; }
  return diqV41PriorIsAdminOrMapLabel(item);
};

var diqV41PriorIsNonConsumerJunk = isNonConsumerJunk;
isNonConsumerJunk = function(item) {
  if (item && item.entityType === "district") { return false; }
  return diqV41PriorIsNonConsumerJunk(item);
};

/* Make classifyPoi aware of Entity IQ before the generic "district/admin" text trap. */
var diqV41PriorClassifyPoi = classifyPoi;
classifyPoi = function(item) {
  if (item && item.entityType === "district") { return "district"; }
  if (item && item.resultType === "entity_iq_injected_district") { return "district"; }
  if (item && item.districtId) { return "district"; }
  return diqV41PriorClassifyPoi(item);
};

/* Allow injected district class only for broad/discovery intents. */
var diqV41PriorClassAllowedForIntent = classAllowedForIntent;
classAllowedForIntent = function(cls, intent) {
  if (cls === "district") {
    return diqV41IntentAllowsEntityInjection(intent);
  }
  return diqV41PriorClassAllowedForIntent(cls, intent);
};

/* Keep district results from being rejected by final authority. */
var diqV41PriorFinalAuthorityRejectReason = finalAuthorityRejectReason;
finalAuthorityRejectReason = function(item, intent) {
  if (item && item.entityType === "district") {
    if (!diqV41IntentAllowsEntityInjection(intent)) {
      return "final block: district not allowed for narrow intent";
    }
    return "";
  }
  return diqV41PriorFinalAuthorityRejectReason(item, intent);
};

/* Replace scan pipeline with V4.1 injection inserted immediately after provider pull. */
scan = function(options) {
  var startedAt = Date.now();
  var intent = resolveIntent(options || {});
  var report = newReport(options || {}, intent);
  var candidates;
  var injectedCandidates;
  var consumerTruth;
  var placeTruth;
  var categoryTruth;
  var freshnessTruth;
  var distanceTruth;
  var entityTruth;
  var scored;
  var districtScored;
  var varied;
  var finalAuthority;
  var finalResults;

  options = options || {};

  candidates = pullCandidates(options, report);
  injectedCandidates = diqV41InjectEntityCandidates(candidates, intent, options, report);
  consumerTruth = applyConsumerDestinationGate(injectedCandidates, intent, options, report);
  placeTruth = applyPlaceTruth(consumerTruth, options, report);
  categoryTruth = applyCategoryTruth(placeTruth, intent, options, report);
  freshnessTruth = applyFreshnessTruth(categoryTruth, intent, options, report);
  distanceTruth = applyDistanceTruth(freshnessTruth, intent, options, report);

  if (typeof diqV4ApplyEntityIQ === "function") {
    entityTruth = diqV4ApplyEntityIQ(distanceTruth, intent, options, report);
  } else {
    entityTruth = distanceTruth;
  }

  scored = scoreCandidates(entityTruth, intent, options, report);

  if (typeof diqV4UpgradeDistrictScores === "function") {
    districtScored = diqV4UpgradeDistrictScores(scored, intent, report);
  } else {
    districtScored = scored;
  }

  varied = applyVariety(districtScored, intent, options, report);
  finalAuthority = applyFinalRenderAuthority(varied, intent, options, report);
  finalResults = finalizeResults(finalAuthority, intent, options, report);

  finishReport(report, startedAt, finalResults, options);

  return {
    ok:true,
    build:DIQ_V41_ENTITY_INJECTION_BUILD,
    intent:intent.id,
    profile:intent.profile,
    results:finalResults,
    report:report
  };
};

try {
  window.DriveIQScanEngine.scan = scan;
  window.DriveIQScanEngine.build = DIQ_V41_ENTITY_INJECTION_BUILD;
  window.DriveIQScanEngine.entityInjectionIQ = {
    build:DIQ_V41_ENTITY_INJECTION_BUILD,
    inject:diqV41InjectEntityCandidates,
    radius:diqV41InjectionRadius
  };
  console.log("🧠 DriveIQScanEngine V4.1 Entity Injection IQ loaded", DIQ_V41_ENTITY_INJECTION_BUILD);
} catch (eV41Attach) {
  try { console.warn("DriveIQScanEngine V4.1 failed to attach", eV41Attach); } catch (eV41Warn) {}
}




/* =====================================================================
   DRIVE IQ — SCAN ENGINE V4.2
   DISTRICT DENSITY IQ + EXPERIENCE GRAPH FOUNDATION

   Builds on V4.1 Entity Injection IQ:
   - Adds density profiles to known districts.
   - Scores districts by Authority + Density + Experience + Intent Match.
   - Adds experience category counts for Adventure Mode cards.
   - Adds lifestyle-ready district lanes for future personalization.
   - Keeps Food/Coffee/Dinner/Golf scans strict: districts do not hijack narrow place scans.
   ES5 SAFE
===================================================================== */

var DIQ_V42_BUILD = "V4_2_DISTRICT_DENSITY_IQ_EXPERIENCE_GRAPH";

var DIQ_V42_DENSITY_PROFILES = {
  harborwalk_village_destin:{
    restaurantCount:18,
    coffeeCount:3,
    nightlifeCount:12,
    shoppingCount:11,
    activityCount:16,
    attractionCount:8,
    waterfrontCount:12,
    liveMusicCount:5,
    familyCount:8,
    densityScore:98,
    experienceScore:99,
    walkabilityScore:94,
    popularityScore:99,
    districtAuthorityScore:99,
    lifestyleLanes:{
      spring_break:["Nightlife","Live Music","Boat Rentals","Waterfront Bars","Late Eats"],
      family:["Dolphin Cruises","Ice Cream","Family Dining","Shopping","Harbor Walk"],
      date_night:["Waterfront Dining","Sunset","Cocktails","Evening Walk","Live Music"],
      local:["Seafood","Harbor Views","Less Touristy Food","Coffee","Events"]
    }
  },
  destin_commons:{
    restaurantCount:14,
    coffeeCount:6,
    nightlifeCount:2,
    shoppingCount:65,
    activityCount:5,
    attractionCount:4,
    waterfrontCount:0,
    liveMusicCount:1,
    familyCount:12,
    densityScore:95,
    experienceScore:88,
    walkabilityScore:91,
    popularityScore:96,
    districtAuthorityScore:96,
    lifestyleLanes:{
      spring_break:["Retail","Movies","Quick Eats","Coffee","Group Shopping"],
      family:["Shopping","Movies","Kid-Friendly Food","Bass Pro","Rainy Day"],
      date_night:["Dinner","Movie","Shopping Walk","Dessert"],
      local:["Shopping","Coffee","Errands","Lunch","Movies"]
    }
  },
  silo_square_southaven:{
    restaurantCount:8,
    coffeeCount:2,
    nightlifeCount:2,
    shoppingCount:5,
    activityCount:4,
    attractionCount:2,
    waterfrontCount:0,
    liveMusicCount:2,
    familyCount:6,
    densityScore:82,
    experienceScore:84,
    walkabilityScore:92,
    popularityScore:88,
    districtAuthorityScore:93,
    lifestyleLanes:{
      spring_break:["Food","Patios","Events","Local Hangout"],
      family:["Food","Events","Coffee","Community"],
      date_night:["Dinner","Patio","Coffee","Evening Walk"],
      local:["Coffee","Restaurants","Events","Patios","Boutiques"]
    }
  },
  beale_street_memphis:{
    restaurantCount:18,
    coffeeCount:2,
    nightlifeCount:22,
    shoppingCount:6,
    activityCount:10,
    attractionCount:9,
    waterfrontCount:1,
    liveMusicCount:20,
    familyCount:4,
    densityScore:96,
    experienceScore:99,
    walkabilityScore:92,
    popularityScore:99,
    districtAuthorityScore:98,
    lifestyleLanes:{
      spring_break:["Live Music","Bars","Late Eats","Dancing","Iconic Stops"],
      family:["Music History","Food","Photos","Daytime Walk"],
      date_night:["Live Music","Dinner","Cocktails","Walkable Nightlife"],
      local:["Music","Events","Food","Game Night"]
    }
  },
  broadway_nashville:{
    restaurantCount:24,
    coffeeCount:4,
    nightlifeCount:35,
    shoppingCount:8,
    activityCount:14,
    attractionCount:10,
    waterfrontCount:1,
    liveMusicCount:30,
    familyCount:5,
    densityScore:99,
    experienceScore:100,
    walkabilityScore:94,
    popularityScore:100,
    districtAuthorityScore:99,
    lifestyleLanes:{
      spring_break:["Honky Tonks","Live Music","Rooftops","Late Eats","Group Energy"],
      family:["Daytime Music","Food Halls","Photos","Walkable Stops"],
      date_night:["Rooftop Dinner","Live Music","Cocktails","Downtown Walk"],
      local:["Shows","Food Hall","Events","Rooftops"]
    }
  },
  the_square_oxford:{
    restaurantCount:14,
    coffeeCount:4,
    nightlifeCount:7,
    shoppingCount:12,
    activityCount:5,
    attractionCount:5,
    waterfrontCount:0,
    liveMusicCount:4,
    familyCount:5,
    densityScore:89,
    experienceScore:91,
    walkabilityScore:95,
    popularityScore:94,
    districtAuthorityScore:95,
    lifestyleLanes:{
      spring_break:["Food","Nightlife","Shopping","College Town Energy"],
      family:["Food","Bookstores","Shops","Walkable Square"],
      date_night:["Dinner","Dessert","Bookstore Walk","Cocktails"],
      local:["Lunch","Coffee","Square Books","Events","Patios"]
    }
  },
  downtown_gatlinburg:{
    restaurantCount:28,
    coffeeCount:6,
    nightlifeCount:8,
    shoppingCount:35,
    activityCount:40,
    attractionCount:30,
    waterfrontCount:3,
    liveMusicCount:6,
    familyCount:30,
    densityScore:97,
    experienceScore:99,
    walkabilityScore:90,
    popularityScore:99,
    districtAuthorityScore:97,
    lifestyleLanes:{
      spring_break:["Attractions","Food","Shopping","Mountain Energy"],
      family:["Aquarium","Anakeesta","Candy Shops","Family Dining","SkyPark"],
      date_night:["Mountain Views","Dinner","Moonshine Tastings","Evening Walk"],
      local:["Less Crowded Food","Coffee","Scenic Walk","Shops"]
    }
  },
  disney_springs:{
    restaurantCount:45,
    coffeeCount:10,
    nightlifeCount:10,
    shoppingCount:80,
    activityCount:35,
    attractionCount:25,
    waterfrontCount:8,
    liveMusicCount:8,
    familyCount:45,
    densityScore:100,
    experienceScore:100,
    walkabilityScore:96,
    popularityScore:100,
    districtAuthorityScore:99,
    lifestyleLanes:{
      spring_break:["Shopping","Food","Nightlife","Dessert","Entertainment"],
      family:["World of Disney","Dessert","Family Dining","Entertainment","Shopping"],
      date_night:["Dinner","Waterfront Walk","Dessert","Live Music","Cocktails"],
      local:["Dinner","Shopping","Dessert","Shows"]
    }
  },
  the_wharf_orange_beach:{
    restaurantCount:12,
    coffeeCount:3,
    nightlifeCount:7,
    shoppingCount:15,
    activityCount:16,
    attractionCount:10,
    waterfrontCount:10,
    liveMusicCount:8,
    familyCount:14,
    densityScore:92,
    experienceScore:95,
    walkabilityScore:91,
    popularityScore:94,
    districtAuthorityScore:96,
    lifestyleLanes:{
      spring_break:["Concerts","Nightlife","Waterfront","Food","Activities"],
      family:["Ferris Wheel","Events","Restaurants","Shopping","Marina"],
      date_night:["Waterfront Dinner","Concert","Evening Walk","Cocktails"],
      local:["Events","Dinner","Marina","Shopping","Music"]
    }
  }
};

function diqV42Clamp(v){
  v = Number(v || 0);
  if(!finite(v)){ v = 0; }
  return Math.max(0,Math.min(100,Math.round(v)));
}

function diqV42DistrictProfile(item){
  var id = item && item.districtId ? String(item.districtId) : "";
  if(id && DIQ_V42_DENSITY_PROFILES[id]){ return DIQ_V42_DENSITY_PROFILES[id]; }
  return null;
}

function diqV42IntentMatchScore(item,intent){
  var id = intent && intent.id ? String(intent.id) : "general";
  var type = item && item.districtType ? String(item.districtType) : "adventure";
  var tags = item && item.districtTags ? normText(item.districtTags.join(" ")) : "";
  var score = 55;

  if(id === "shopping"){
    if(type === "shopping"){ score += 35; }
    if(tags.indexOf("shopping") > -1 || tags.indexOf("retail") > -1){ score += 20; }
    if(type === "entertainment"){ score -= 18; }
  }else if(id === "nightlife"){
    if(type === "entertainment" || type === "adventure"){ score += 30; }
    if(tags.indexOf("nightlife") > -1 || tags.indexOf("live music") > -1 || tags.indexOf("bars") > -1){ score += 22; }
    if(type === "shopping"){ score -= 12; }
  }else if(id === "family_fun"){
    if(tags.indexOf("family") > -1 || tags.indexOf("attractions") > -1 || tags.indexOf("entertainment") > -1){ score += 28; }
  }else if(id === "local_favorites" || id === "hidden_gems" || id === "general"){
    if(type === "adventure" || type === "local" || type === "entertainment"){ score += 24; }
    if(tags.indexOf("restaurants") > -1 || tags.indexOf("food") > -1 || tags.indexOf("events") > -1 || tags.indexOf("waterfront") > -1){ score += 16; }
  }else if(id === "lake" || id === "parks"){
    if(tags.indexOf("waterfront") > -1 || tags.indexOf("beach") > -1 || tags.indexOf("activities") > -1){ score += 24; }
  }

  return diqV42Clamp(score);
}

function diqV42ExperienceCountTotal(profile){
  if(!profile){ return 0; }
  return Number(profile.restaurantCount || 0) +
    Number(profile.coffeeCount || 0) +
    Number(profile.nightlifeCount || 0) +
    Number(profile.shoppingCount || 0) +
    Number(profile.activityCount || 0) +
    Number(profile.attractionCount || 0);
}

function diqV42ApplyDistrictDensity(item,intent){
  var profile;
  var intentMatch;
  var score;
  var experienceTotal;

  if(!item || item.entityType !== "district"){ return item; }

  profile = diqV42DistrictProfile(item);
  if(!profile){ return item; }

  intentMatch = diqV42IntentMatchScore(item,intent);
  experienceTotal = diqV42ExperienceCountTotal(profile);

  item.districtDensityIQ = {
    restaurantCount:Number(profile.restaurantCount || 0),
    coffeeCount:Number(profile.coffeeCount || 0),
    nightlifeCount:Number(profile.nightlifeCount || 0),
    shoppingCount:Number(profile.shoppingCount || 0),
    activityCount:Number(profile.activityCount || 0),
    attractionCount:Number(profile.attractionCount || 0),
    waterfrontCount:Number(profile.waterfrontCount || 0),
    liveMusicCount:Number(profile.liveMusicCount || 0),
    familyCount:Number(profile.familyCount || 0),
    densityScore:diqV42Clamp(profile.densityScore),
    experienceScore:diqV42Clamp(profile.experienceScore),
    walkabilityScore:diqV42Clamp(profile.walkabilityScore),
    popularityScore:diqV42Clamp(profile.popularityScore),
    districtAuthorityScore:diqV42Clamp(profile.districtAuthorityScore),
    intentMatchScore:intentMatch,
    experienceTotal:experienceTotal
  };

  item.districtLifestyleLanes = profile.lifestyleLanes || {};
  item.experienceCount = Math.max(Number(item.experienceCount || 0), experienceTotal);
  item.districtExperienceCounts = {
    restaurants:Number(profile.restaurantCount || 0),
    coffee:Number(profile.coffeeCount || 0),
    nightlife:Number(profile.nightlifeCount || 0),
    shopping:Number(profile.shoppingCount || 0),
    activities:Number(profile.activityCount || 0),
    attractions:Number(profile.attractionCount || 0),
    waterfront:Number(profile.waterfrontCount || 0),
    liveMusic:Number(profile.liveMusicCount || 0),
    family:Number(profile.familyCount || 0)
  };

  score =
    (Number(profile.districtAuthorityScore || 0) * 0.30) +
    (Number(profile.densityScore || 0) * 0.23) +
    (Number(profile.experienceScore || 0) * 0.22) +
    (intentMatch * 0.18) +
    (Number(profile.walkabilityScore || 0) * 0.07);

  item.districtDensityScore = diqV42Clamp(profile.densityScore);
  item.districtExperienceScore = diqV42Clamp(profile.experienceScore);
  item.districtIntentMatchScore = intentMatch;
  item.districtCompositeScore = diqV42Clamp(score);

  item.diqScore = Math.max(Number(item.diqScore || 0), item.districtCompositeScore);
  item.confidence = Math.max(Number(item.confidence || 0), item.districtCompositeScore, 90);

  item.recommendedBecause =
    String(item.districtLabel || "District") +
    " • " +
    String(item.experienceCount || 0) +
    " experiences • " +
    "Density " +
    String(item.districtDensityScore || 0);

  return item;
}

function diqV42DensityReason(item){
  var c = item && item.districtExperienceCounts ? item.districtExperienceCounts : null;
  var bits = [];
  if(!c){ return ""; }

  if(Number(c.restaurants || 0) > 0){ bits.push(String(c.restaurants) + " food"); }
  if(Number(c.nightlife || 0) > 0){ bits.push(String(c.nightlife) + " nightlife"); }
  if(Number(c.shopping || 0) > 0){ bits.push(String(c.shopping) + " shops"); }
  if(Number(c.activities || 0) > 0){ bits.push(String(c.activities) + " activities"); }
  if(Number(c.liveMusic || 0) > 0){ bits.push(String(c.liveMusic) + " music"); }

  return bits.slice(0,4).join(" • ");
}

function diqV42UpgradeDistrictDensity(items,intent,report){
  var out = [];
  var i;
  var item;
  var districtCount = 0;

  for(i=0;i<list(items).length;i++){
    item = items[i];
    if(item && item.entityType === "district"){
      districtCount += 1;
      item = diqV42ApplyDistrictDensity(item,intent);
      item.densityReason = diqV42DensityReason(item);
    }
    out.push(item);
  }

  out.sort(function(a,b){
    var ad = a && a.entityType === "district" ? Number(a.districtCompositeScore || a.diqScore || 0) : Number(a.diqScore || 0);
    var bd = b && b.entityType === "district" ? Number(b.districtCompositeScore || b.diqScore || 0) : Number(b.diqScore || 0);
    return bd - ad;
  });

  if(report){
    report.counts.afterDistrictDensityIQ = out.length;
    report.districtDensityIQ = {
      districtCount:districtCount,
      build:DIQ_V42_BUILD
    };
  }

  return out;
}

/* Re-run public scan pipeline with District Density IQ inserted after
   district scoring and before variety/final render. */
scan = function(options){
  var startedAt = Date.now();
  var intent = resolveIntent(options || {});
  var report = newReport(options || {},intent);
  var candidates;
  var injectedCandidates;
  var consumerTruth;
  var placeTruth;
  var categoryTruth;
  var freshnessTruth;
  var distanceTruth;
  var entityTruth;
  var scored;
  var districtScored;
  var densityScored;
  var varied;
  var finalAuthority;
  var finalResults;

  options = options || {};

  candidates = pullCandidates(options,report);

  if(typeof diqV41InjectEntities === "function"){
    injectedCandidates = diqV41InjectEntities(candidates,intent,options,report);
  }else{
    injectedCandidates = candidates;
  }

  consumerTruth = applyConsumerDestinationGate(injectedCandidates,intent,options,report);
  placeTruth = applyPlaceTruth(consumerTruth,options,report);
  categoryTruth = applyCategoryTruth(placeTruth,intent,options,report);
  freshnessTruth = applyFreshnessTruth(categoryTruth,intent,options,report);
  distanceTruth = applyDistanceTruth(freshnessTruth,intent,options,report);
  entityTruth = diqV4ApplyEntityIQ(distanceTruth,intent,options,report);
  scored = scoreCandidates(entityTruth,intent,options,report);
  districtScored = diqV4UpgradeDistrictScores(scored,intent,report);
  densityScored = diqV42UpgradeDistrictDensity(districtScored,intent,report);
  varied = applyVariety(densityScored,intent,options,report);
  finalAuthority = applyFinalRenderAuthority(varied,intent,options,report);
  finalResults = finalizeResults(finalAuthority,intent,options,report);

  finishReport(report,startedAt,finalResults,options);

  return {
    ok:true,
    build:DIQ_V42_BUILD,
    intent:intent.id,
    profile:intent.profile,
    results:finalResults,
    report:report
  };
};

try{
  window.DriveIQScanEngine.scan = scan;
  window.DriveIQScanEngine.build = DIQ_V42_BUILD;
  window.DriveIQScanEngine.districtDensityIQ = {
    build:DIQ_V42_BUILD,
    profiles:DIQ_V42_DENSITY_PROFILES,
    apply:diqV42ApplyDistrictDensity
  };
  console.log("🧠 DriveIQScanEngine V4.2 District Density IQ loaded", DIQ_V42_BUILD);
}catch(eV42){
  try{ console.warn("DriveIQScanEngine V4.2 failed to attach",eV42); }catch(eV42b){}
}




/* =====================================================================
   DRIVE IQ — SCAN ENGINE V4.2.1
   DISTRICT IQ PLUMBING FIX

   WHY:
   V4.2 Density IQ was loaded, but the live scan could still behave like
   Place IQ because the V4.2 scan override checked the wrong injection
   function name and injected districts could still be lost in the
   category truth stage.

   FIXES:
   - Calls the real V4.1 injection function: diqV41InjectEntityCandidates.
   - Lets injected District IQ entities pass Category Truth for broad scans.
   - Preserves district card metadata through finalizeResults.
   - Keeps Food/Coffee/Dinner/Dessert/Golf strict.
   ES5 SAFE
===================================================================== */

var DIQ_V421_BUILD = "V4_2_1_DISTRICT_IQ_PLUMBING_FIX";

function diqV421IsDistrict(item){
  return !!(item && (item.entityType === "district" || item.resultType === "entity_iq_injected_district" || item.poiClass === "district" || item.districtId));
}

function diqV421AllowDistrict(intent,item){
  if(typeof diqV41IntentAllowsEntityInjection === "function"){
    return diqV41IntentAllowsEntityInjection(intent);
  }
  if(typeof diqV4IntentAllowsDistrict === "function"){
    return diqV4IntentAllowsDistrict(intent,item && item.districtType ? item.districtType : "adventure");
  }
  return false;
}

/* Districts are already curated authority records. Do not make them prove
   category truth by matching provider POI keywords like normal businesses. */
var diqV421PriorApplyCategoryTruth = applyCategoryTruth;
applyCategoryTruth = function(items,intent,options,report){
  var districts = [];
  var normal = [];
  var merged;
  var i;
  var item;

  for(i=0;i<list(items).length;i++){
    item = items[i];
    if(diqV421IsDistrict(item)){
      if(diqV421AllowDistrict(intent,item)){
        item.poiClass = "district";
        item.categoryTruth = Math.max(Number(item.categoryTruth || 0), 96);
        item.consumerDestination = true;
        item.placeTruth = true;
        item.categoryTruthBypass = "curated District IQ authority";
        districts.push(item);
      }else{
        addRemoval(report,"categoryTruth",item,"district blocked for narrow intent");
      }
    }else{
      normal.push(item);
    }
  }

  merged = diqV421PriorApplyCategoryTruth(normal,intent,options,report);

  for(i=0;i<districts.length;i++){
    merged.push(districts[i]);
  }

  if(report){ report.counts.afterCategoryTruth = merged.length; }
  return dedupeCandidates(merged);
};

/* Keep districts from being variety-capped as generic shopping. */
var diqV421PriorVarietyType = varietyType;
varietyType = function(item,intent){
  if(diqV421IsDistrict(item)){
    return "district_" + String(item.districtType || "adventure");
  }
  return diqV421PriorVarietyType(item,intent);
};

/* Preserve district metadata after finalizeResults clones and rewrites
   generic recommendation copy. */
var diqV421PriorFinalizeResults = finalizeResults;
finalizeResults = function(items,intent,options,report){
  var out = diqV421PriorFinalizeResults(items,intent,options,report);
  var i;
  var item;
  var density;

  for(i=0;i<list(out).length;i++){
    item = out[i];
    if(diqV421IsDistrict(item)){
      density = item.districtDensityIQ || {};
      item.scanEngineBuild = DIQ_V421_BUILD;
      item.entityType = "district";
      item.entityClass = "district";
      item.poiClass = "district";
      item.userFacingCategory = item.districtLabel || "District";
      item.isRealResult = true;
      item.finalAuthority = true;
      item.recommendedBecause =
        String(item.districtLabel || "District") +
        " • " +
        String(item.experienceCount || (density.experienceTotal || 0)) +
        " experiences inside" +
        (item.districtDensityScore ? " • Density " + String(item.districtDensityScore) : "");
    }
  }

  return out;
};

/* Final public scan pipeline with correct entity injection function. */
scan = function(options){
  var startedAt = Date.now();
  var intent = resolveIntent(options || {});
  var report = newReport(options || {},intent);
  var candidates;
  var injectedCandidates;
  var consumerTruth;
  var placeTruth;
  var categoryTruth;
  var freshnessTruth;
  var distanceTruth;
  var entityTruth;
  var scored;
  var districtScored;
  var densityScored;
  var varied;
  var finalAuthority;
  var finalResults;

  options = options || {};

  candidates = pullCandidates(options,report);

  if(typeof diqV41InjectEntityCandidates === "function"){
    injectedCandidates = diqV41InjectEntityCandidates(candidates,intent,options,report);
  }else if(typeof diqV41InjectEntities === "function"){
    injectedCandidates = diqV41InjectEntities(candidates,intent,options,report);
  }else{
    injectedCandidates = candidates;
    if(report){
      report.entityInjection = { injected:0, reason:"missing_injection_function", build:DIQ_V421_BUILD };
      report.notes.push("District IQ injection function was not found.");
    }
  }

  consumerTruth = applyConsumerDestinationGate(injectedCandidates,intent,options,report);
  placeTruth = applyPlaceTruth(consumerTruth,options,report);
  categoryTruth = applyCategoryTruth(placeTruth,intent,options,report);
  freshnessTruth = applyFreshnessTruth(categoryTruth,intent,options,report);
  distanceTruth = applyDistanceTruth(freshnessTruth,intent,options,report);

  if(typeof diqV4ApplyEntityIQ === "function"){
    entityTruth = diqV4ApplyEntityIQ(distanceTruth,intent,options,report);
  }else{
    entityTruth = distanceTruth;
  }

  scored = scoreCandidates(entityTruth,intent,options,report);

  if(typeof diqV4UpgradeDistrictScores === "function"){
    districtScored = diqV4UpgradeDistrictScores(scored,intent,report);
  }else{
    districtScored = scored;
  }

  if(typeof diqV42UpgradeDistrictDensity === "function"){
    densityScored = diqV42UpgradeDistrictDensity(districtScored,intent,report);
  }else{
    densityScored = districtScored;
  }

  varied = applyVariety(densityScored,intent,options,report);
  finalAuthority = applyFinalRenderAuthority(varied,intent,options,report);
  finalResults = finalizeResults(finalAuthority,intent,options,report);

  finishReport(report,startedAt,finalResults,options);

  if(report){
    report.build = DIQ_V421_BUILD;
    report.version = "v4_2_1_district_iq_plumbing_fix";
    try{ safeJsonSet(LAST_REPORT_KEY,report); }catch(e421r){}
  }

  return {
    ok:true,
    build:DIQ_V421_BUILD,
    intent:intent.id,
    profile:intent.profile,
    results:finalResults,
    report:report
  };
};

try{
  window.DriveIQScanEngine.scan = scan;
  window.DriveIQScanEngine.build = DIQ_V421_BUILD;
  window.DriveIQScanEngine.districtPlumbingFix = {
    build:DIQ_V421_BUILD,
    injectionFunction:"diqV41InjectEntityCandidates",
    categoryTruthBypass:"curated District IQ authority for broad scans"
  };
  console.log("🧠 DriveIQScanEngine V4.2.1 District IQ plumbing fix loaded", DIQ_V421_BUILD);
}catch(e421){
  try{ console.warn("DriveIQScanEngine V4.2.1 failed to attach",e421); }catch(e421b){}
}




/* =====================================================================
   DRIVE IQ — SCAN ENGINE V4.2.2
   EXPLICIT ENTITY IQ QUERY AUTHORITY

   WHY:
   District IQ should not depend only on proximity injection. When the user
   actually types a known district/entity name like HarborWalk Village,
   Destin Commons, Silo Square, Beale Street, Broadway Nashville,
   Disney Springs, or The Wharf, the canonical Entity IQ record needs to
   enter the candidate pool and outrank generic autocomplete/admin matches.

   FIXES:
   - Detects exact/alias query matches against the District IQ registry.
   - Injects the matched canonical district even when no provider candidate
     or nearby-radius injection exists.
   - Keeps narrow category scans strict unless forceEntityIQ is passed.
   - Marks explicit matches so they sort above generic provider matches.
   ES5 SAFE
===================================================================== */

var DIQ_V422_BUILD = "V4_2_2_EXPLICIT_ENTITY_QUERY_AUTHORITY";

function diqV422QueryText(options){
  var destination = options && options.destination ? options.destination : null;
  return normText(
    String(options && options.query || "") + " " +
    String(options && options.searchText || "") + " " +
    String(options && options.rawQuery || "") + " " +
    String(destination && (destination.label || destination.name || destination.full || destination.address) || "")
  );
}

function diqV422IsNarrowCategoryQuery(q){
  q = normText(q);
  return /\b(coffee|cafe|espresso|breakfast|brunch|dinner|food|restaurant|restaurants|dessert|ice cream|bakery|donut|golf|course|driving range)\b/.test(q);
}

function diqV422AliasExactOrStrongMatch(q,alias){
  var a = normText(alias);
  var before;
  var after;

  if(!q || !a){ return false; }
  if(q === a){ return true; }

  /* Strong destination-style matches such as "harborwalk village destin" or
     "the wharf orange beach" should count, but category searches like
     "coffee at harborwalk village" should remain place/category scans. */
  if(q.indexOf(a) > -1){
    before = clean(q.substring(0,q.indexOf(a)));
    after = clean(q.substring(q.indexOf(a) + a.length));
    if(!diqV422IsNarrowCategoryQuery(before + " " + after)){ return true; }
  }

  return false;
}

function diqV422FindExplicitDistricts(options){
  var q = diqV422QueryText(options || {});
  var registry = diqV41RegistryList ? diqV41RegistryList() : [];
  var out = [];
  var i;
  var j;
  var d;
  var aliases;

  if(!q || !registry.length){ return out; }

  for(i=0;i<registry.length;i++){
    d = registry[i];
    if(!d){ continue; }
    aliases = [d.label || ""];
    if(d.aliases && d.aliases.length){
      for(j=0;j<d.aliases.length;j++){ aliases.push(d.aliases[j]); }
    }
    for(j=0;j<aliases.length;j++){
      if(diqV422AliasExactOrStrongMatch(q,aliases[j])){
        out.push(d);
        break;
      }
    }
  }

  return out;
}

function diqV422CenterForDistance(options){
  if(typeof diqV41Center === "function"){
    return diqV41Center(options || {});
  }
  return options && options.location ? options.location : null;
}

function diqV422BuildExplicitDistrictItem(entity,intent,options){
  var center = diqV422CenterForDistance(options || {});
  var d = null;
  var item;

  if(center && finite(Number(center.lat)) && finite(Number(center.lng)) && finite(Number(entity.lat)) && finite(Number(entity.lng))){
    d = miles(Number(center.lat),Number(center.lng),Number(entity.lat),Number(entity.lng));
  }

  item = diqV41DistrictItem(entity,intent,options || {},d);
  item.source = "entity_iq_explicit_query";
  item.explicitEntityQueryMatch = true;
  item.entityAuthority = 100;
  item.districtAuthority = Math.max(Number(item.districtAuthority || 0),100);
  item.districtIntentBoost = Math.max(Number(item.districtIntentBoost || 0),30);
  item.diqScore = Math.max(Number(item.diqScore || 0),99);
  item.confidence = Math.max(Number(item.confidence || 0),96);
  item.recommendedBecause = String(item.districtLabel || "District") + " • Canonical Entity IQ match • " + String(item.experienceCount || 0) + " experiences inside";
  return item;
}

var diqV422PriorInjectEntityCandidates = diqV41InjectEntityCandidates;
diqV41InjectEntityCandidates = function(candidates,intent,options,report){
  var out = diqV422PriorInjectEntityCandidates(candidates,intent,options,report);
  var matches = diqV422FindExplicitDistricts(options || {});
  var allow = diqV421AllowDistrict ? diqV421AllowDistrict(intent,{districtType:"adventure"}) : diqV41IntentAllowsEntityInjection(intent);
  var injected = [];
  var i;
  var item;

  if(!matches.length){ return out; }

  if(!allow && !(options && options.forceEntityIQ === true)){
    if(report){
      report.explicitEntityIQ = {
        build:DIQ_V422_BUILD,
        matched:matches.length,
        injected:0,
        reason:"narrow_intent_kept_strict"
      };
      report.notes.push("Explicit Entity IQ match found, but narrow category truth kept the scan place-specific.");
    }
    return out;
  }

  for(i=0;i<matches.length;i++){
    item = diqV422BuildExplicitDistrictItem(matches[i],intent,options || {});
    injected.push(item);
    out.push(item);
  }

  out = dedupeCandidates(out);

  if(report){
    report.counts.afterExplicitEntityIQ = out.length;
    report.explicitEntityIQ = {
      build:DIQ_V422_BUILD,
      matched:matches.length,
      injected:injected.length,
      names:(function(){
        var names = [];
        var j;
        for(j=0;j<injected.length;j++){ names.push(injected[j].label); }
        return names;
      })()
    };
    report.notes.push("Explicit Entity IQ injected canonical district records for recognized query text.");
  }

  return out;
};

/* Explicit Entity IQ matches should sort above generic provider/autocomplete
   candidates before density and final authority run. */
var diqV422PriorUpgradeDistrictScores = diqV4UpgradeDistrictScores;
diqV4UpgradeDistrictScores = function(items,intent,report){
  var out = diqV422PriorUpgradeDistrictScores(items,intent,report);
  var i;
  var item;

  for(i=0;i<list(out).length;i++){
    item = out[i];
    if(item && item.explicitEntityQueryMatch){
      item.diqScore = Math.max(Number(item.diqScore || 0),99);
      item.confidence = Math.max(Number(item.confidence || 0),96);
      item.entityAuthority = 100;
    }
  }

  out.sort(function(a,b){
    var ae = a && a.explicitEntityQueryMatch ? 1 : 0;
    var be = b && b.explicitEntityQueryMatch ? 1 : 0;
    if(ae !== be){ return be - ae; }
    return Number(b && b.diqScore || 0) - Number(a && a.diqScore || 0);
  });

  return out;
};

var diqV422PriorScan = scan;
scan = function(options){
  var result = diqV422PriorScan(options || {});
  if(result){
    result.build = DIQ_V422_BUILD;
    if(result.report){
      result.report.build = DIQ_V422_BUILD;
      result.report.version = "v4_2_2_explicit_entity_query_authority";
      try{ safeJsonSet(LAST_REPORT_KEY,result.report); }catch(e422s){}
    }
  }
  return result;
};

try{
  window.DriveIQScanEngine.scan = scan;
  window.DriveIQScanEngine.build = DIQ_V422_BUILD;
  window.DriveIQScanEngine.explicitEntityIQ = {
    build:DIQ_V422_BUILD,
    find:diqV422FindExplicitDistricts,
    description:"Canonical Entity IQ records outrank generic autocomplete when query text matches a known district."
  };
  console.log("🧠 DriveIQScanEngine V4.2.2 Explicit Entity IQ Query Authority loaded",DIQ_V422_BUILD);
}catch(e422){
  try{ console.warn("DriveIQScanEngine V4.2.2 failed to attach",e422); }catch(e422b){}
}



/* =====================================================================
   DRIVE IQ — SCAN ENGINE V4.3
   ENTITY REGISTRY V1 + DISTRICT MEMBERSHIP ENGINE

   PURPOSE:
   - Own canonical district entities instead of hoping providers return them.
   - Inject nearby destination districts as first-class recommendations.
   - Attach nearby businesses to their parent district.
   - Return district experience payloads Adventure Mode can render:
     districtMemberPlaces, districtExperienceCounts, topPlaceNames.
   - Preserve narrow intent trust: Food/Coffee/Dinner/Dessert/Golf still
     return individual places, not district cards.

   ES5 SAFE
===================================================================== */

var DIQ_V43_ENTITY_REGISTRY_BUILD = "V4_3_ENTITY_REGISTRY_V1_DISTRICT_MEMBERSHIP";

var DIQ_V43_ENTITY_REGISTRY = [
  {
    id:"harborwalk_village_destin",
    label:"HarborWalk Village",
    city:"Destin",
    state:"FL",
    lat:30.3935,
    lng:-86.5104,
    radiusFeet:1800,
    entityType:"district",
    districtType:"adventure",
    districtLabel:"Waterfront Entertainment District",
    authority:99,
    tags:["waterfront","restaurants","nightlife","shopping","activities","live music","boating","harbor"],
    summary:"Walkable waterfront destination with restaurants, bars, shops, live music, boat tours, and harbor views.",
    contains:["aj's","ajs","harry t","margaritaville","coyote ugly","tailfins","harbor docks","boogies","dolphin cruise","destin harbor","harborwalk","harbor walk","emerald grande","crab island"],
    topPlaceNames:["AJ's Seafood & Oyster Bar","Harry T's","Margaritaville","Coyote Ugly","Tailfins","Harbor Docks","Boogies Watersports","Destin Harbor"],
    experienceCounts:{ restaurants:18, nightlife:12, shopping:11, activities:16, coffee:3, attractions:8, waterfront:12, liveMusic:5 }
  },
  {
    id:"destin_commons",
    label:"Destin Commons",
    city:"Destin",
    state:"FL",
    lat:30.3909,
    lng:-86.4271,
    radiusFeet:1800,
    entityType:"district",
    districtType:"shopping",
    districtLabel:"Shopping District",
    authority:96,
    tags:["shopping","retail","restaurants","movies","family","coffee","entertainment"],
    summary:"Retail-first shopping and entertainment district with stores, restaurants, coffee, movies, and family activities.",
    contains:["destin commons","bass pro","amc","belk","h&m","h m","uncle buck","starbucks","kilwins","world of beer"],
    topPlaceNames:["Bass Pro Shops","AMC Destin Commons","Belk","H&M","Uncle Buck's FishBowl","Starbucks"],
    experienceCounts:{ restaurants:14, nightlife:2, shopping:65, activities:5, coffee:6, attractions:4, waterfront:0, liveMusic:1 }
  },
  {
    id:"silo_square_southaven",
    label:"Silo Square",
    city:"Southaven",
    state:"MS",
    lat:34.9619,
    lng:-89.9907,
    radiusFeet:1400,
    entityType:"district",
    districtType:"local",
    districtLabel:"Local District",
    authority:93,
    tags:["local","restaurants","coffee","events","shops","patios","community"],
    summary:"Walkable local district with restaurants, coffee, events, shops, patios, and community gathering spots.",
    contains:["silo square","silo","southaven square"],
    topPlaceNames:["Local restaurants","Coffee","Community events","Boutiques","Patios"],
    experienceCounts:{ restaurants:8, nightlife:2, shopping:5, activities:4, coffee:2, attractions:2, waterfront:0, liveMusic:2 }
  },
  {
    id:"beale_street_memphis",
    label:"Beale Street",
    city:"Memphis",
    state:"TN",
    lat:35.1398,
    lng:-90.0524,
    radiusFeet:1700,
    entityType:"district",
    districtType:"entertainment",
    districtLabel:"Entertainment District",
    authority:98,
    tags:["nightlife","live music","bars","restaurants","tourism","walkable","iconic"],
    summary:"Iconic entertainment district with live music, restaurants, bars, and Memphis energy.",
    contains:["beale","bb king","b.b. king","rum boogie","silky","handy park","a schwab"],
    topPlaceNames:["B.B. King's Blues Club","Rum Boogie Cafe","Silky O'Sullivan's","A. Schwab","Handy Park"],
    experienceCounts:{ restaurants:18, nightlife:22, shopping:6, activities:10, coffee:2, attractions:9, waterfront:1, liveMusic:20 }
  },
  {
    id:"broadway_nashville",
    label:"Broadway Nashville",
    city:"Nashville",
    state:"TN",
    lat:36.1611,
    lng:-86.7789,
    radiusFeet:2000,
    entityType:"district",
    districtType:"entertainment",
    districtLabel:"Entertainment District",
    authority:99,
    tags:["nightlife","live music","bars","restaurants","rooftops","tourism","walkable","iconic"],
    summary:"High-energy downtown district with honky-tonks, rooftops, live music, restaurants, and Nashville nightlife.",
    contains:["broadway","lower broadway","tootsies","tootsie's","roberts western","robert's western","honky tonk","acme feed","assembly food hall","the stage","kid rock"],
    topPlaceNames:["Tootsie's Orchid Lounge","The Stage","Robert's Western World","Acme Feed & Seed","Assembly Food Hall"],
    experienceCounts:{ restaurants:24, nightlife:35, shopping:8, activities:14, coffee:4, attractions:10, waterfront:1, liveMusic:30 }
  },
  {
    id:"the_square_oxford",
    label:"The Square",
    city:"Oxford",
    state:"MS",
    lat:34.3668,
    lng:-89.5192,
    radiusFeet:1500,
    entityType:"district",
    districtType:"local",
    districtLabel:"Local District",
    authority:95,
    tags:["restaurants","shopping","coffee","nightlife","bookstores","walkable","college town"],
    summary:"Walkable Oxford district with restaurants, boutiques, coffee, bookstores, nightlife, and local character.",
    contains:["square books","city grocery","ajax","boure","proud larry","the square","oxford square"],
    topPlaceNames:["City Grocery","Ajax Diner","Square Books","Boure","Proud Larry's"],
    experienceCounts:{ restaurants:14, nightlife:7, shopping:12, activities:5, coffee:4, attractions:5, waterfront:0, liveMusic:4 }
  },
  {
    id:"downtown_gatlinburg",
    label:"Downtown Gatlinburg",
    city:"Gatlinburg",
    state:"TN",
    lat:35.7110,
    lng:-83.5186,
    radiusFeet:2600,
    entityType:"district",
    districtType:"adventure",
    districtLabel:"Mountain Adventure District",
    authority:97,
    tags:["attractions","food","shopping","family","walkable","tourism","mountains"],
    summary:"Walkable mountain-town district with attractions, food, shopping, family activities, and Smoky Mountain energy.",
    contains:["gatlinburg strip","downtown gatlinburg","anakeesta","ripley's","ripleys","ole smoky","skypark","village shops"],
    topPlaceNames:["Anakeesta","Ripley's Aquarium","Ole Smoky","The Village Shops","SkyPark"],
    experienceCounts:{ restaurants:28, nightlife:8, shopping:35, activities:40, coffee:6, attractions:30, waterfront:3, liveMusic:6 }
  },
  {
    id:"disney_springs",
    label:"Disney Springs",
    city:"Lake Buena Vista",
    state:"FL",
    lat:28.3702,
    lng:-81.5197,
    radiusFeet:3400,
    entityType:"district",
    districtType:"adventure",
    districtLabel:"Dining & Shopping District",
    authority:99,
    tags:["shopping","restaurants","entertainment","family","nightlife","walkable","tourism"],
    summary:"Large dining, entertainment, and shopping district with restaurants, shops, shows, dessert, and family experiences.",
    contains:["disney springs","world of disney","gideon's","gideons","raglan road","house of blues","the boathouse","sprinkles"],
    topPlaceNames:["The Boathouse","World of Disney","Gideon's Bakehouse","Raglan Road","House of Blues"],
    experienceCounts:{ restaurants:45, nightlife:10, shopping:80, activities:35, coffee:10, attractions:25, waterfront:8, liveMusic:8 }
  },
  {
    id:"the_wharf_orange_beach",
    label:"The Wharf",
    city:"Orange Beach",
    state:"AL",
    lat:30.2946,
    lng:-87.6289,
    radiusFeet:2200,
    entityType:"district",
    districtType:"adventure",
    districtLabel:"Waterfront Adventure District",
    authority:96,
    tags:["waterfront","restaurants","shopping","events","amphitheater","family","nightlife","marina"],
    summary:"Waterfront adventure district with restaurants, shopping, events, concerts, activities, and marina energy.",
    contains:["the wharf","wharf amphitheater","villaggio","yoho","yo ho","ferris wheel","orange beach marina"],
    topPlaceNames:["The Wharf Amphitheater","Villaggio Grille","YoHo Rum & Tacos","Ferris Wheel","Marina"],
    experienceCounts:{ restaurants:12, nightlife:7, shopping:15, activities:16, coffee:3, attractions:10, waterfront:10, liveMusic:8 }
  }
];

function diqV43FeetToMiles(ft){ return Number(ft || 0) / 5280; }

function diqV43IsNarrowPlaceIntent(intent){
  var id = intent && intent.id ? String(intent.id) : "general";
  return id === "food" || id === "coffee" || id === "breakfast" || id === "dinner" || id === "dessert" || id === "golf";
}

function diqV43EntityAllowedForIntent(entity,intent){
  var id = intent && intent.id ? String(intent.id) : "general";
  var type = entity && entity.districtType ? String(entity.districtType) : "adventure";

  if(diqV43IsNarrowPlaceIntent(intent)){ return false; }
  if(id === "shopping"){ return type === "shopping" || type === "local" || type === "adventure"; }
  if(id === "nightlife"){ return type === "entertainment" || type === "adventure"; }
  if(id === "parks" || id === "lake"){ return type === "adventure" || type === "local"; }

  return id === "general" || id === "local_favorites" || id === "hidden_gems" || id === "family_fun";
}

function diqV43Center(options){
  if(options && options.location && finite(Number(options.location.lat)) && finite(Number(options.location.lng))){
    return { lat:Number(options.location.lat), lng:Number(options.location.lng) };
  }
  if(options && options.center && finite(Number(options.center.lat)) && finite(Number(options.center.lng))){
    return { lat:Number(options.center.lat), lng:Number(options.center.lng) };
  }
  if(options && options.destination && finite(Number(options.destination.lat)) && finite(Number(options.destination.lng))){
    return { lat:Number(options.destination.lat), lng:Number(options.destination.lng) };
  }
  return null;
}

function diqV43EntityDistance(entity,center){
  if(!entity || !center){ return null; }
  return miles(center.lat,center.lng,Number(entity.lat),Number(entity.lng));
}

function diqV43ItemText(item){
  return normText(
    String(item && item.label || "") + " " +
    String(item && item.name || "") + " " +
    String(item && item.full || "") + " " +
    String(item && item.address || "") + " " +
    String(item && item.category || "") + " " +
    String(item && item.kind || "") + " " +
    String(item && item.type || "") + " " +
    String(item && item.userFacingCategory || "")
  );
}

function diqV43TextMatchesEntity(item,entity){
  var txt = diqV43ItemText(item);
  var terms = entity && entity.contains ? entity.contains : [];
  var i;
  var term;
  if(!txt){ return false; }

  for(i=0;i<terms.length;i++){
    term = normText(terms[i]);
    if(term && txt.indexOf(term) > -1){ return true; }
  }
  return false;
}

function diqV43ItemInsideEntity(item,entity){
  var d;
  var radius;
  if(!item || !entity){ return false; }

  if(diqV43TextMatchesEntity(item,entity)){ return true; }

  if(!finite(Number(item.lat)) || !finite(Number(item.lng))){ return false; }
  d = miles(Number(item.lat),Number(item.lng),Number(entity.lat),Number(entity.lng));
  radius = diqV43FeetToMiles(entity.radiusFeet || 1200);

  return finite(Number(d)) && Number(d) <= radius;
}

function diqV43MemberCategory(item){
  var cls = item && item.poiClass ? String(item.poiClass) : classifyPoi(item);
  if(cls === "restaurant"){ return "restaurants"; }
  if(cls === "coffee"){ return "coffee"; }
  if(cls === "dessert"){ return "dessert"; }
  if(cls === "nightlife"){ return "nightlife"; }
  if(cls === "shopping"){ return "shopping"; }
  if(cls === "attraction"){ return "activities"; }
  if(cls === "park" || cls === "outdoor_water"){ return "activities"; }
  return "places";
}

function diqV43CompactMember(item){
  return {
    label:clean(item && (item.label || item.name) || "Place"),
    full:clean(item && item.full || ""),
    lat:finite(Number(item && item.lat)) ? Number(item.lat) : null,
    lng:finite(Number(item && item.lng)) ? Number(item.lng) : null,
    category:userFacingCategory(item || {}, { profile:{ label:"Place" } }),
    poiClass:clean(item && item.poiClass || classifyPoi(item || {})),
    rating:item && item.rating ? item.rating : null,
    reviews:item && (item.reviews || item.reviewCount || item.user_ratings_total) ? (item.reviews || item.reviewCount || item.user_ratings_total) : null,
    image:item && (item.image || item.photo || item.photoUrl || item.thumbnail) ? (item.image || item.photo || item.photoUrl || item.thumbnail) : "",
    distMiles:finite(Number(item && item.distMiles)) ? Number(item.distMiles) : null
  };
}

function diqV43BuildCounts(entity,members){
  var base = clone(entity && entity.experienceCounts ? entity.experienceCounts : {});
  var i;
  var key;

  if(!base.restaurants){ base.restaurants = 0; }
  if(!base.nightlife){ base.nightlife = 0; }
  if(!base.shopping){ base.shopping = 0; }
  if(!base.activities){ base.activities = 0; }
  if(!base.coffee){ base.coffee = 0; }
  if(!base.attractions){ base.attractions = 0; }
  if(!base.waterfront){ base.waterfront = 0; }
  if(!base.liveMusic){ base.liveMusic = 0; }

  for(i=0;i<list(members).length;i++){
    key = diqV43MemberCategory(members[i]);
    if(!base[key]){ base[key] = 0; }
    base[key] += 1;
  }

  return base;
}

function diqV43TopNames(entity,members){
  var names = [];
  var seen = {};
  var i;
  var name;

  for(i=0;i<list(members).length;i++){
    name = clean(members[i] && (members[i].label || members[i].name) || "");
    if(name && !seen[normText(name)]){
      seen[normText(name)] = true;
      names.push(name);
    }
    if(names.length >= 6){ return names; }
  }

  for(i=0;i<list(entity && entity.topPlaceNames).length;i++){
    name = clean(entity.topPlaceNames[i]);
    if(name && !seen[normText(name)]){
      seen[normText(name)] = true;
      names.push(name);
    }
    if(names.length >= 6){ break; }
  }

  return names;
}

function diqV43ExperienceTotal(counts){
  var total = 0;
  total += Number(counts && counts.restaurants || 0);
  total += Number(counts && counts.nightlife || 0);
  total += Number(counts && counts.shopping || 0);
  total += Number(counts && counts.activities || 0);
  total += Number(counts && counts.coffee || 0);
  total += Number(counts && counts.attractions || 0);
  return total;
}

function diqV43DistrictScore(entity,intent,members,counts,distanceMiles){
  var id = intent && intent.id ? String(intent.id) : "general";
  var score = Number(entity.authority || 90);
  var memberBonus = Math.min(18, list(members).length * 4);
  var distanceBonus = 0;

  if(finite(Number(distanceMiles))){
    if(Number(distanceMiles) <= 1){ distanceBonus = 12; }
    else if(Number(distanceMiles) <= 5){ distanceBonus = 8; }
    else if(Number(distanceMiles) <= 15){ distanceBonus = 4; }
  }

  score += memberBonus + distanceBonus;

  if(id === "shopping" && entity.districtType === "shopping"){ score += 15; }
  if(id === "nightlife" && (entity.districtType === "entertainment" || entity.districtType === "adventure")){ score += 15; }
  if((id === "local_favorites" || id === "general" || id === "hidden_gems") && (entity.districtType === "adventure" || entity.districtType === "local" || entity.districtType === "entertainment")){ score += 12; }

  if(counts && Number(counts.waterfront || 0) > 0){ score += 4; }
  if(counts && Number(counts.liveMusic || 0) > 0){ score += 3; }

  return Math.max(0,Math.min(100,Math.round(score)));
}

function diqV43MakeDistrictEntity(entity,intent,options,members,distanceMiles){
  var compact = [];
  var i;
  var counts = diqV43BuildCounts(entity,members);
  var topNames = diqV43TopNames(entity,members);
  var score = diqV43DistrictScore(entity,intent,members,counts,distanceMiles);

  for(i=0;i<list(members).length && i<8;i++){
    compact.push(diqV43CompactMember(members[i]));
  }

  return {
    label:String(entity.label || "District"),
    full:String((entity.city || "") + (entity.state ? ", " + entity.state : "")),
    lat:Number(entity.lat),
    lng:Number(entity.lng),
    kind:"district",
    category:String(entity.districtLabel || entity.districtType || "District"),
    source:"entity_registry_v1",
    resultType:"entity_registry_district",
    entityType:"district",
    entityClass:"district",
    districtId:String(entity.id || ""),
    districtType:String(entity.districtType || "adventure"),
    districtLabel:String(entity.districtLabel || "Adventure District"),
    districtSummary:String(entity.summary || ""),
    districtTags:entity.tags || [],
    districtMemberPlaces:compact,
    districtMemberCount:list(members).length,
    districtExperienceCounts:counts,
    districtThumbnailCards:(function(){
      var out = [];
      var j;
      for(j=0;j<topNames.length;j++){
        out.push({
          label:topNames[j],
          entityType:"place_preview",
          parentDistrictId:String(entity.id || ""),
          category:"Top Pick",
          recommendedBecause:"Top experience inside " + String(entity.label || "this district")
        });
      }
      return out;
    })(),
    topPlaceNames:topNames,
    experienceCount:Math.max(Number(entity.experienceCount || 0), diqV43ExperienceTotal(counts)),
    distMiles:finite(Number(distanceMiles)) ? Math.round(Number(distanceMiles) * 10) / 10 : null,
    distanceMiles:finite(Number(distanceMiles)) ? Math.round(Number(distanceMiles) * 10) / 10 : null,
    districtAuthority:Number(entity.authority || 90),
    categoryTruth:98,
    consumerDestination:true,
    placeTruth:true,
    poiClass:"district",
    diqScore:score,
    confidence:Math.max(90,score),
    finalAuthority:true,
    isRealResult:true,
    recommendedBecause:String(entity.districtLabel || "District") + " • " + String(topNames.length || compact.length || entity.experienceCount || 0) + " top experiences previewed"
  };
}

function diqV43NearbyEntities(options,intent){
  var center = diqV43Center(options || {});
  var out = [];
  var radius = 35;
  var i;
  var entity;
  var d;

  if(!center){ return out; }

  for(i=0;i<DIQ_V43_ENTITY_REGISTRY.length;i++){
    entity = DIQ_V43_ENTITY_REGISTRY[i];
    if(!diqV43EntityAllowedForIntent(entity,intent)){ continue; }
    d = diqV43EntityDistance(entity,center);
    if(finite(Number(d)) && Number(d) <= radius){
      entity._distanceMiles = Number(d);
      out.push(entity);
    }
  }

  out.sort(function(a,b){ return Number(a._distanceMiles || 999) - Number(b._distanceMiles || 999); });
  return out;
}

function diqV43BuildDistrictEntities(items,intent,options,report){
  var entities = diqV43NearbyEntities(options || {},intent);
  var districtItems = [];
  var i;
  var j;
  var entity;
  var members;
  var item;
  var districtItem;

  if(diqV43IsNarrowPlaceIntent(intent)){
    if(report){ report.entityRegistryV1 = { build:DIQ_V43_ENTITY_REGISTRY_BUILD, injected:0, reason:"narrow_intent" }; }
    return [];
  }

  for(i=0;i<entities.length;i++){
    entity = entities[i];
    members = [];

    for(j=0;j<list(items).length;j++){
      item = items[j];
      if(item && item.entityType === "district"){ continue; }
      if(diqV43ItemInsideEntity(item,entity)){ members.push(item); }
    }

    districtItem = diqV43MakeDistrictEntity(entity,intent,options,members,entity._distanceMiles);
    districtItems.push(districtItem);
  }

  if(report){
    report.entityRegistryV1 = {
      build:DIQ_V43_ENTITY_REGISTRY_BUILD,
      injected:districtItems.length,
      names:(function(){
        var names = [];
        var k;
        for(k=0;k<districtItems.length;k++){ names.push(districtItems[k].label); }
        return names;
      })()
    };
    if(districtItems.length){ report.notes.push("Entity Registry V1 injected canonical destination districts."); }
  }

  return districtItems;
}

var diqV43PriorClassifyPoi = classifyPoi;
classifyPoi = function(item){
  if(item && (item.entityType === "district" || item.resultType === "entity_registry_district" || item.districtId)){ return "district"; }
  return diqV43PriorClassifyPoi(item);
};

var diqV43PriorLooksVisitableConsumerPlace = looksVisitableConsumerPlace;
looksVisitableConsumerPlace = function(item){
  if(item && (item.entityType === "district" || item.resultType === "entity_registry_district")){ return true; }
  return diqV43PriorLooksVisitableConsumerPlace(item);
};

var diqV43PriorIsAdminOrMapLabel = isAdminOrMapLabel;
isAdminOrMapLabel = function(item){
  if(item && (item.entityType === "district" || item.resultType === "entity_registry_district")){ return false; }
  return diqV43PriorIsAdminOrMapLabel(item);
};

var diqV43PriorIsNonConsumerJunk = isNonConsumerJunk;
isNonConsumerJunk = function(item){
  if(item && (item.entityType === "district" || item.resultType === "entity_registry_district")){ return false; }
  return diqV43PriorIsNonConsumerJunk(item);
};

var diqV43PriorClassAllowedForIntent = classAllowedForIntent;
classAllowedForIntent = function(cls,intent){
  if(cls === "district"){ return !diqV43IsNarrowPlaceIntent(intent); }
  return diqV43PriorClassAllowedForIntent(cls,intent);
};

var diqV43PriorApplyCategoryTruth = applyCategoryTruth;
applyCategoryTruth = function(items,intent,options,report){
  var districts = [];
  var normal = [];
  var merged;
  var i;
  var item;

  for(i=0;i<list(items).length;i++){
    item = items[i];
    if(item && item.entityType === "district"){
      if(!diqV43IsNarrowPlaceIntent(intent)){
        item.poiClass = "district";
        item.categoryTruth = 98;
        item.consumerDestination = true;
        item.placeTruth = true;
        districts.push(item);
      }else{
        addRemoval(report,"categoryTruth",item,"district blocked for narrow intent");
      }
    }else{
      normal.push(item);
    }
  }

  merged = diqV43PriorApplyCategoryTruth(normal,intent,options,report);
  for(i=0;i<districts.length;i++){ merged.push(districts[i]); }
  if(report){ report.counts.afterCategoryTruth = merged.length; }
  return dedupeCandidates(merged);
};

var diqV43PriorFinalAuthorityRejectReason = finalAuthorityRejectReason;
finalAuthorityRejectReason = function(item,intent){
  if(item && item.entityType === "district"){
    if(diqV43IsNarrowPlaceIntent(intent)){ return "final block: district blocked for narrow intent"; }
    return "";
  }
  return diqV43PriorFinalAuthorityRejectReason(item,intent);
};

var diqV43PriorVarietyType = varietyType;
varietyType = function(item,intent){
  if(item && item.entityType === "district"){ return "district_" + String(item.districtType || "adventure"); }
  return diqV43PriorVarietyType(item,intent);
};

var diqV43PriorFinalizeResults = finalizeResults;
finalizeResults = function(items,intent,options,report){
  var out = diqV43PriorFinalizeResults(items,intent,options,report);
  var i;
  var item;

  for(i=0;i<list(out).length;i++){
    item = out[i];
    if(item && item.entityType === "district"){
      item.scanEngineBuild = DIQ_V43_ENTITY_REGISTRY_BUILD;
      item.poiClass = "district";
      item.userFacingCategory = item.districtLabel || "District";
      item.isRealResult = true;
      item.finalAuthority = true;
      item.recommendedBecause =
        String(item.districtLabel || "District") +
        " • " +
        String(item.experienceCount || 0) +
        " experiences" +
        (item.topPlaceNames && item.topPlaceNames.length ? " • Top picks previewed" : "");
    }
  }

  return out;
};

scan = function(options){
  var startedAt = Date.now();
  var intent = resolveIntent(options || {});
  var report = newReport(options || {},intent);
  var candidates;
  var registryDistricts;
  var injectedCandidates;
  var consumerTruth;
  var placeTruth;
  var categoryTruth;
  var freshnessTruth;
  var distanceTruth;
  var entityTruth;
  var scored;
  var districtScored;
  var densityScored;
  var varied;
  var finalAuthority;
  var finalResults;

  options = options || {};

  candidates = pullCandidates(options,report);
  registryDistricts = diqV43BuildDistrictEntities(candidates,intent,options,report);
  injectedCandidates = dedupeCandidates(candidates.concat(registryDistricts));

  if(typeof diqV41InjectEntityCandidates === "function"){
    injectedCandidates = diqV41InjectEntityCandidates(injectedCandidates,intent,options,report);
  }

  consumerTruth = applyConsumerDestinationGate(injectedCandidates,intent,options,report);
  placeTruth = applyPlaceTruth(consumerTruth,options,report);
  categoryTruth = applyCategoryTruth(placeTruth,intent,options,report);
  freshnessTruth = applyFreshnessTruth(categoryTruth,intent,options,report);
  distanceTruth = applyDistanceTruth(freshnessTruth,intent,options,report);

  if(typeof diqV4ApplyEntityIQ === "function"){
    entityTruth = diqV4ApplyEntityIQ(distanceTruth,intent,options,report);
  }else{
    entityTruth = distanceTruth;
  }

  scored = scoreCandidates(entityTruth,intent,options,report);

  if(typeof diqV4UpgradeDistrictScores === "function"){
    districtScored = diqV4UpgradeDistrictScores(scored,intent,report);
  }else{
    districtScored = scored;
  }

  if(typeof diqV42UpgradeDistrictDensity === "function"){
    densityScored = diqV42UpgradeDistrictDensity(districtScored,intent,report);
  }else{
    densityScored = districtScored;
  }

  varied = applyVariety(densityScored,intent,options,report);
  finalAuthority = applyFinalRenderAuthority(varied,intent,options,report);
  finalResults = finalizeResults(finalAuthority,intent,options,report);
  finishReport(report,startedAt,finalResults,options);

  if(report){
    report.build = DIQ_V43_ENTITY_REGISTRY_BUILD;
    report.version = "v4_3_entity_registry_v1_district_membership";
    try{ safeJsonSet(LAST_REPORT_KEY,report); }catch(eV43Report){}
  }

  return {
    ok:true,
    build:DIQ_V43_ENTITY_REGISTRY_BUILD,
    intent:intent.id,
    profile:intent.profile,
    results:finalResults,
    report:report
  };
};

try{
  window.DriveIQScanEngine.scan = scan;
  window.DriveIQScanEngine.build = DIQ_V43_ENTITY_REGISTRY_BUILD;
  window.DriveIQScanEngine.entityRegistryV1 = {
    build:DIQ_V43_ENTITY_REGISTRY_BUILD,
    registry:DIQ_V43_ENTITY_REGISTRY,
    nearby:diqV43NearbyEntities,
    buildDistricts:diqV43BuildDistrictEntities
  };
  console.log("🧠 DriveIQScanEngine V4.3 Entity Registry V1 loaded", DIQ_V43_ENTITY_REGISTRY_BUILD);
}catch(eV43){
  try{ console.warn("DriveIQScanEngine V4.3 failed to attach",eV43); }catch(eV43b){}
}


}());


/* =====================================================================
   DRIVE IQ — SCAN ENGINE V5 INTELLIGENCE ARCHITECTURE
   Safe overlay on top of V4.3.

   V5 GOALS:
   - Preserve the working V4.3 pipeline and truth rules.
   - Make Entity IQ the public result authority.
   - Add generated District IQ from dense candidate/result clusters.
   - Normalize every returned recommendation object.
   - Use one canonical photo authority: diqPhotoUrl.
   - Add Explainability IQ + separate confidence channels.
   - ES5-safe. No optional chaining. No arrow functions. No template literals.
===================================================================== */
(function(){
"use strict";

if(window.__DRIVE_IQ_SCAN_ENGINE_V5_INTELLIGENCE_ARCHITECTURE__){ return; }
window.__DRIVE_IQ_SCAN_ENGINE_V5_INTELLIGENCE_ARCHITECTURE__ = true;

var DIQ_V5_BUILD = "V5_INTELLIGENCE_ARCHITECTURE_ENTITY_DISTRICT_PHOTO_EXPLAINABILITY";
var DIQ_V5_LAST_REPORT_KEY = "diq_scan_engine_v5_last_report_v1";
var DIQ_V5_PRIOR_ENGINE = window.DriveIQScanEngine || null;
var DIQ_V5_PRIOR_SCAN = DIQ_V5_PRIOR_ENGINE && DIQ_V5_PRIOR_ENGINE.scan ? DIQ_V5_PRIOR_ENGINE.scan : null;
var DIQ_V5_PRIOR_BUILD = DIQ_V5_PRIOR_ENGINE && DIQ_V5_PRIOR_ENGINE.build ? String(DIQ_V5_PRIOR_ENGINE.build) : "unknown";

function v5Clean(v){ return String(v || "").replace(/\s+/g," ").trim(); }
function v5Lower(v){ return String(v || "").toLowerCase(); }
function v5Norm(v){ return v5Lower(v).replace(/[^a-z0-9]+/g," ").replace(/\s+/g," ").trim(); }
function v5Finite(v){ return typeof v === "number" && isFinite(v); }
function v5List(v){ return v && v.length ? v : []; }
function v5Clone(obj){ try{ return JSON.parse(JSON.stringify(obj || {})); }catch(e){ return {}; } }
function v5NowIso(){ try{ return new Date().toISOString(); }catch(e){ return ""; } }
function v5SetJson(k,v){ try{ localStorage.setItem(k,JSON.stringify(v)); }catch(e){} }
function v5GetJson(k){ try{ return JSON.parse(localStorage.getItem(k) || "null"); }catch(e){ return null; } }

function v5Miles(lat1,lng1,lat2,lng2){
  var R = 3958.8;
  var dLat;
  var dLng;
  var a1;
  var a2;
  var a;
  lat1 = Number(lat1); lng1 = Number(lng1); lat2 = Number(lat2); lng2 = Number(lng2);
  if(!v5Finite(lat1) || !v5Finite(lng1) || !v5Finite(lat2) || !v5Finite(lng2)){ return null; }
  dLat = (lat2 - lat1) * Math.PI / 180;
  dLng = (lng2 - lng1) * Math.PI / 180;
  a1 = lat1 * Math.PI / 180;
  a2 = lat2 * Math.PI / 180;
  a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.sin(dLng/2) * Math.sin(dLng/2) * Math.cos(a1) * Math.cos(a2);
  return R * 2 * Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
}

function v5Text(item){
  var cats = "";
  try{
    if(item && item.categories && typeof item.categories === "object"){
      cats = item.categories.join ? item.categories.join(" ") : JSON.stringify(item.categories);
    }else{
      cats = String(item && item.categories || "");
    }
  }catch(e){ cats = ""; }
  return v5Norm(
    String(item && item.label || "") + " " +
    String(item && item.name || "") + " " +
    String(item && item.full || "") + " " +
    String(item && item.address || "") + " " +
    String(item && item.formatted || "") + " " +
    String(item && item.category || "") + " " +
    cats + " " +
    String(item && item.kind || "") + " " +
    String(item && item.type || "") + " " +
    String(item && item.resultType || "") + " " +
    String(item && item.matchLabel || "")
  );
}

function v5Name(item){
  return v5Clean(String(item && (item.label || item.name || item.title || item.place_name) || ""));
}

function v5PhotoUrl(item){
  var keys = [
    "diqPhotoUrl","photoUrl","photo_url","heroPhoto","hero_photo","officialPhoto","official_photo",
    "businessPhoto","business_photo","thumbnailUrl","thumbnail_url","imageUrl","image_url","pictureUrl",
    "picture_url","mediaUrl","media_url","photo","image","thumbnail","preview","src","url"
  ];
  var i;
  var k;
  var v;
  var low;
  if(!item){ return ""; }
  for(i=0;i<keys.length;i++){
    k = keys[i];
    v = item[k];
    if(typeof v === "string" && v5Clean(v)){
      low = v5Lower(v);
      if(low.indexOf("http") === 0 || low.indexOf("data:image") === 0){ return v5Clean(v); }
    }
    if(v && typeof v === "object"){
      if(typeof v.url === "string" && v5Clean(v.url)){ return v5Clean(v.url); }
      if(typeof v.src === "string" && v5Clean(v.src)){ return v5Clean(v.src); }
    }
  }
  if(item.gallery && item.gallery.length){
    for(i=0;i<item.gallery.length;i++){
      v = item.gallery[i];
      if(v && typeof v.url === "string" && v5Clean(v.url)){ return v5Clean(v.url); }
      if(typeof v === "string" && v5Clean(v)){ return v5Clean(v); }
    }
  }
  if(item.photos && item.photos.length){
    for(i=0;i<item.photos.length;i++){
      v = item.photos[i];
      if(v && typeof v.url === "string" && v5Clean(v.url)){ return v5Clean(v.url); }
      if(v && typeof v.src === "string" && v5Clean(v.src)){ return v5Clean(v.src); }
      if(typeof v === "string" && v5Clean(v)){ return v5Clean(v); }
    }
  }
  return "";
}

function v5Center(options,items){
  var center = options && options.location ? options.location : null;
  var lat = 0;
  var lng = 0;
  var count = 0;
  var i;
  var item;
  if(center && v5Finite(Number(center.lat)) && v5Finite(Number(center.lng))){ return { lat:Number(center.lat), lng:Number(center.lng), source:"options.location" }; }
  if(options && options.destination && v5Finite(Number(options.destination.lat)) && v5Finite(Number(options.destination.lng))){
    return { lat:Number(options.destination.lat), lng:Number(options.destination.lng), source:"options.destination" };
  }
  for(i=0;i<v5List(items).length;i++){
    item = items[i];
    if(item && v5Finite(Number(item.lat)) && v5Finite(Number(item.lng))){
      lat += Number(item.lat); lng += Number(item.lng); count += 1;
    }
  }
  if(count){ return { lat:lat/count, lng:lng/count, source:"result_cluster" }; }
  return null;
}

function v5IntentId(options,base){
  var id = base && base.intent ? String(base.intent) : "";
  var q;
  if(id){ return id; }
  if(DIQ_V5_PRIOR_ENGINE && DIQ_V5_PRIOR_ENGINE.resolveIntent){
    try{
      id = DIQ_V5_PRIOR_ENGINE.resolveIntent(options || {}).id;
      if(id){ return id; }
    }catch(e0){}
  }
  q = v5Norm((options && (options.intent || options.category || options.query)) || "");
  if(/\bcoffee|cafe|espresso|latte|roaster\b/.test(q)){ return "coffee"; }
  if(/\bbreakfast|brunch\b/.test(q)){ return "breakfast"; }
  if(/\bdinner|date night|supper\b/.test(q)){ return "dinner"; }
  if(/\bdessert|ice cream|sweet|treat|bakery|donut|cupcake\b/.test(q)){ return "dessert"; }
  if(/\bgolf|country club|driving range|links\b/.test(q)){ return "golf"; }
  if(/\blake|reservoir|marina|boat ramp|campground|waterfront|fishing\b/.test(q)){ return "lake"; }
  if(/\bnightlife|bar|pub|brewery|live music|cocktail|lounge\b/.test(q)){ return "nightlife"; }
  if(/\bpark|trail|outdoor|scenic|nature|playground\b/.test(q)){ return "parks"; }
  if(/\bshopping|shop|boutique|market|antique|vintage|gift|bookstore|outlet\b/.test(q)){ return "shopping"; }
  if(/\bhidden gem|hidden gems\b/.test(q)){ return "hidden_gems"; }
  if(/\blocal favorite|local favorites|local faves|hangout\b/.test(q)){ return "local_favorites"; }
  if(/\bfamily|kids|arcade|bowling|museum|zoo\b/.test(q)){ return "family_fun"; }
  if(/\bfood|restaurant|lunch|pizza|burger|taco|bbq|barbecue|seafood|sushi|wings\b/.test(q)){ return "food"; }
  return "general";
}

function v5ProfileLabel(id){
  if(DIQ_V5_PRIOR_ENGINE && DIQ_V5_PRIOR_ENGINE.profiles && DIQ_V5_PRIOR_ENGINE.profiles[id]){
    return DIQ_V5_PRIOR_ENGINE.profiles[id].label || id;
  }
  if(id === "local_favorites"){ return "Local Favorites IQ"; }
  if(id === "hidden_gems"){ return "Hidden Gems IQ"; }
  if(id === "family_fun"){ return "Family Fun IQ"; }
  if(id === "parks"){ return "Parks IQ"; }
  if(id === "lake"){ return "Lake IQ"; }
  if(id === "general"){ return "Adventure IQ"; }
  return id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g," ");
}

function v5AllowDistrictForIntent(intentId,options){
  if(options && options.includeDistrictContext === true){ return true; }
  if(intentId === "food" || intentId === "coffee" || intentId === "breakfast" || intentId === "dinner" || intentId === "dessert" || intentId === "golf"){
    return false;
  }
  return intentId === "general" || intentId === "local_favorites" || intentId === "hidden_gems" || intentId === "shopping" || intentId === "nightlife" || intentId === "family_fun" || intentId === "parks" || intentId === "lake";
}

function v5DistrictTypeAllowed(intentId,district,options){
  var type = district && district.districtType ? String(district.districtType) : "adventure";
  var tags = district && district.tags ? v5Norm(district.tags.join(" ")) : "";
  if(!v5AllowDistrictForIntent(intentId,options)){ return false; }
  if(intentId === "shopping"){ return type === "shopping" || type === "local" || type === "adventure" || tags.indexOf("shopping") > -1 || tags.indexOf("retail") > -1; }
  if(intentId === "nightlife"){ return type === "entertainment" || type === "adventure" || tags.indexOf("nightlife") > -1 || tags.indexOf("live music") > -1 || tags.indexOf("bars") > -1; }
  if(intentId === "lake" || intentId === "parks"){ return type === "waterfront" || type === "adventure" || type === "beach" || tags.indexOf("waterfront") > -1 || tags.indexOf("park") > -1; }
  return true;
}

function v5Registry(){
  var reg = [];
  if(DIQ_V5_PRIOR_ENGINE && DIQ_V5_PRIOR_ENGINE.entityRegistryV1 && DIQ_V5_PRIOR_ENGINE.entityRegistryV1.registry && DIQ_V5_PRIOR_ENGINE.entityRegistryV1.registry.length){
    reg = reg.concat(DIQ_V5_PRIOR_ENGINE.entityRegistryV1.registry);
  }
  if(DIQ_V5_PRIOR_ENGINE && DIQ_V5_PRIOR_ENGINE.entityIQ && DIQ_V5_PRIOR_ENGINE.entityIQ.districts && DIQ_V5_PRIOR_ENGINE.entityIQ.districts.length){
    reg = reg.concat(DIQ_V5_PRIOR_ENGINE.entityIQ.districts);
  }
  if(DIQ_V5_PRIOR_ENGINE && DIQ_V5_PRIOR_ENGINE.districtRegistry && DIQ_V5_PRIOR_ENGINE.districtRegistry.length){
    reg = reg.concat(DIQ_V5_PRIOR_ENGINE.districtRegistry);
  }
  return v5Dedupe(reg);
}

function v5Dedupe(items){
  var out = [];
  var seen = {};
  var i;
  var item;
  var key;
  for(i=0;i<v5List(items).length;i++){
    item = items[i];
    if(!item){ continue; }
    key = v5Norm(String(item.districtId || item.entityId || item.id || item.placeId || "") + "|" + String(item.label || item.name || "") + "|" + String(item.lat || "") + "|" + String(item.lng || ""));
    if(!key){ key = "idx_" + i; }
    if(seen[key]){ continue; }
    seen[key] = true;
    out.push(item);
  }
  return out;
}

function v5IsDistrict(item){
  return !!(item && (item.entityType === "district" || item.entityClass === "district" || item.poiClass === "district" || item.resultType === "entity_iq_injected_district" || item.isGeneratedDistrict || item.districtId));
}

function v5EntityType(item){
  var txt = v5Text(item);
  if(v5IsDistrict(item)){ return "district"; }
  if(/\bneighborhood|neighbourhood|downtown|main street|historic square|town square\b/.test(txt)){ return "neighborhood"; }
  if(/\bbeach|shore|coast|gulf\b/.test(txt)){ return "beach"; }
  if(/\blake|reservoir|marina|boat ramp|waterfront|fishing\b/.test(txt)){ return "lake"; }
  if(/\bstate park|national park|park|trail|greenway|nature|wildlife|recreation\b/.test(txt)){ return "park"; }
  if(/\bscenic|overlook|viewpoint|waterfall|mountain|canyon\b/.test(txt)){ return "scenic_area"; }
  return "place";
}

function v5PoiClass(item){
  var txt = v5Text(item);
  if(v5IsDistrict(item)){ return "district"; }
  if(/\bbest western|hampton inn|holiday inn|la quinta|comfort inn|quality inn|super 8|days inn|motel|hotel|inn|suites|lodging|resort\b/.test(txt)){ return "lodging"; }
  if(/\bradio station|\bfm\b|\bam\b|broadcast|tower|antenna\b/.test(txt)){ return "radio"; }
  if(/\bcity hall|government|courthouse|police|fire station|post office|county office\b/.test(txt)){ return "government"; }
  if(/\bschool|elementary|middle school|high school|college|university\b/.test(txt)){ return "school"; }
  if(/\bchurch|temple|mosque|cemetery|funeral\b/.test(txt)){ return "worship"; }
  if(/^city of\b/.test(v5Norm(v5Name(item))) || /\badministrative|boundary|postcode|municipality|county|locality\b/.test(txt)){ return "admin"; }
  if(/\bcoffee|cafe|café|espresso|roaster|coffeehouse\b/.test(txt)){ return "coffee"; }
  if(/\bbakery|donut|cupcake|cookie|ice cream|dessert|chocolate|sweet|treat\b/.test(txt)){ return "dessert"; }
  if(/\brestaurant|grill|grille|bbq|barbecue|pizza|burger|taco|seafood|diner|steak|sandwich|wings|sushi|kitchen|eatery|bistro|smokehouse|catfish|chicken\b/.test(txt)){ return "restaurant"; }
  if(/\bbar|pub|brewery|cocktail|live music|nightlife|lounge|tavern\b/.test(txt)){ return "nightlife"; }
  if(/\bgolf|course|country club|links|driving range\b/.test(txt)){ return "golf"; }
  if(/\bshop|store|boutique|market|mall|antique|vintage|gift|bookstore|outlet\b/.test(txt)){ return "shopping"; }
  if(/\bmuseum|zoo|arcade|bowling|theater|theatre|music venue|gallery|attraction|entertainment\b/.test(txt)){ return "attraction"; }
  if(/\bpark|trail|greenway|playground|nature|wildlife|scenic|recreation\b/.test(txt)){ return "park"; }
  if(/\blake|reservoir|marina|boat ramp|campground|beach|waterfront|fishing\b/.test(txt)){ return "outdoor_water"; }
  if(/\broad|street|highway|route|intersection|address\b/.test(txt)){ return "road"; }
  return "unknown";
}

function v5RegistryDistrictResult(district,center,intentId,options,candidates){
  var out = v5Clone(district);
  var d = center ? v5Miles(center.lat,center.lng,Number(out.lat),Number(out.lng)) : null;
  var children = v5AttachChildren(out,candidates || [],0.7,10);
  out.label = v5Clean(out.label || out.name || "District");
  out.name = out.label;
  out.full = v5Clean(out.summary || out.full || out.city || "Worth exploring district");
  out.category = v5DistrictCategory(out,intentId);
  out.userFacingCategory = out.category;
  out.entityType = "district";
  out.entityClass = "district";
  out.poiClass = "district";
  out.districtId = v5Clean(out.districtId || out.id || v5Norm(out.label).replace(/\s+/g,"_"));
  out.entityId = out.districtId;
  out.resultType = out.resultType || "v5_registry_district";
  out.isProtectedEntity = true;
  out.isRegistryDistrict = true;
  out.isGeneratedDistrict = false;
  out.consumerDestination = true;
  out.placeTruth = true;
  out.categoryTruth = Math.max(Number(out.categoryTruth || 0),96);
  out.diqScore = Math.max(Number(out.diqScore || 0),92);
  out.confidence = Math.max(Number(out.confidence || 0),92);
  out.entityConfidence = Math.max(Number(out.entityConfidence || 0),98);
  out.categoryConfidence = 94;
  out.freshnessConfidence = 80;
  out.photoConfidence = v5PhotoUrl(out) ? 80 : 35;
  out.placeConfidence = 98;
  out.recommendationConfidence = out.confidence;
  out.children = children;
  out.childCount = children.length || Number(out.experienceCount || 0);
  out.experienceCount = Number(out.experienceCount || out.childCount || 0);
  out.distMiles = v5Finite(d) ? Math.round(d * 10) / 10 : out.distMiles;
  out.diqPhotoUrl = v5PhotoUrl(out);
  out.recommendedBecause = v5DistrictReason(out,intentId,children);
  out.whyRecommended = out.recommendedBecause;
  out.v5Explain = {
    entity:"Known District Registry match",
    category:"Protected District IQ entity for " + v5ProfileLabel(intentId),
    distance:v5Finite(d) ? "Registry district is " + String(Math.round(d * 10) / 10) + " mi from scan center" : "No scan center distance available",
    children:children.length ? "Attached " + children.length + " nearby child places" : "No child places available yet",
    photo:out.diqPhotoUrl ? "Canonical diqPhotoUrl present" : "No canonical district photo yet"
  };
  out.recommendationObjectVersion = "v2";
  out.scanEngineBuild = DIQ_V5_BUILD;
  return out;
}

function v5DistrictCategory(district,intentId){
  var type = district && district.districtType ? String(district.districtType) : "adventure";
  var tags = district && district.tags ? v5Norm(district.tags.join(" ")) : "";
  if(intentId === "shopping"){ return "Shopping District"; }
  if(intentId === "nightlife"){ return "Entertainment District"; }
  if((intentId === "lake" || intentId === "parks") && tags.indexOf("waterfront") > -1){ return "Waterfront District"; }
  if(type === "shopping"){ return "Shopping District"; }
  if(type === "entertainment"){ return "Entertainment District"; }
  if(type === "local"){ return "Local District"; }
  if(type === "waterfront"){ return "Waterfront District"; }
  if(type === "beach"){ return "Beach District"; }
  return "Adventure District";
}

function v5DistrictReason(district,intentId,children){
  var bits = [];
  bits.push(v5DistrictCategory(district,intentId));
  if(district && district.tags && district.tags.length){ bits.push(v5Clean(district.tags.slice(0,3).join(" • "))); }
  if(children && children.length){ bits.push(String(children.length) + " nearby experiences"); }
  else if(district && district.experienceCount){ bits.push(String(district.experienceCount) + " experience signals"); }
  return bits.slice(0,3).join(" • ");
}

function v5RegistryDistricts(intentId,options,candidates,results){
  var out = [];
  var reg = v5Registry();
  var center = v5Center(options,candidates && candidates.length ? candidates : results);
  var queryText = v5Norm(String(options && (options.query || options.intent || options.category) || "") + " " + String(options && options.destination && options.destination.label || ""));
  var i;
  var j;
  var d;
  var district;
  var alias;
  var exact;
  var near;
  if(!v5AllowDistrictForIntent(intentId,options)){ return out; }
  for(i=0;i<reg.length;i++){
    district = reg[i];
    if(!district || !v5Finite(Number(district.lat)) || !v5Finite(Number(district.lng))){ continue; }
    if(!v5DistrictTypeAllowed(intentId,district,options)){ continue; }
    d = center ? v5Miles(center.lat,center.lng,Number(district.lat),Number(district.lng)) : null;
    exact = false;
    if(queryText){
      if(v5Norm(district.label || "") && queryText.indexOf(v5Norm(district.label || "")) > -1){ exact = true; }
      for(j=0;j<v5List(district.aliases).length;j++){
        alias = v5Norm(district.aliases[j]);
        if(alias && (queryText.indexOf(alias) > -1 || alias.indexOf(queryText) > -1)){ exact = true; }
      }
      if(district.city && queryText.indexOf(v5Norm(district.city)) > -1){ exact = true; }
    }
    near = v5Finite(d) && d <= Number(options && options.districtRadiusMiles || 10);
    if(exact || near){ out.push(v5RegistryDistrictResult(district,center,intentId,options,candidates)); }
  }
  return v5Dedupe(out);
}

function v5IsConsumerPlace(item){
  var cls = v5PoiClass(item);
  if(cls === "lodging" || cls === "radio" || cls === "government" || cls === "school" || cls === "worship" || cls === "admin" || cls === "road" || cls === "unknown"){
    return false;
  }
  return cls === "restaurant" || cls === "coffee" || cls === "dessert" || cls === "nightlife" || cls === "park" || cls === "outdoor_water" || cls === "golf" || cls === "shopping" || cls === "attraction";
}

function v5AttachChildren(district,items,radius,max){
  var out = [];
  var i;
  var item;
  var d;
  radius = Number(radius || 0.7);
  max = Number(max || 10);
  for(i=0;i<v5List(items).length;i++){
    item = items[i];
    if(!item || v5IsDistrict(item) || !v5IsConsumerPlace(item)){ continue; }
    if(!v5Finite(Number(item.lat)) || !v5Finite(Number(item.lng))){ continue; }
    d = v5Miles(Number(district.lat),Number(district.lng),Number(item.lat),Number(item.lng));
    if(v5Finite(d) && d <= radius){
      item = v5Clone(item);
      item.distFromDistrictMiles = Math.round(d * 10) / 10;
      item.diqPhotoUrl = v5PhotoUrl(item);
      item.entityType = v5EntityType(item);
      item.poiClass = v5PoiClass(item);
      out.push(item);
      if(out.length >= max){ break; }
    }
  }
  return out;
}

function v5GeneratedDistricts(intentId,options,candidates,results){
  var pool = v5Dedupe((candidates || []).concat(results || []));
  var out = [];
  var i;
  var j;
  var item;
  var other;
  var cluster;
  var d;
  var types;
  var typeCount;
  var bestCluster = null;
  var centerLat;
  var centerLng;
  var score;
  var minChildren = Number(options && options.generatedDistrictMinChildren || 5);
  var radius = Number(options && options.generatedDistrictRadiusMiles || 0.55);
  if(!v5AllowDistrictForIntent(intentId,options)){ return out; }
  if(pool.length < minChildren){ return out; }

  for(i=0;i<pool.length;i++){
    item = pool[i];
    if(!item || !v5IsConsumerPlace(item) || !v5Finite(Number(item.lat)) || !v5Finite(Number(item.lng))){ continue; }
    cluster = [];
    types = {};
    for(j=0;j<pool.length;j++){
      other = pool[j];
      if(!other || !v5IsConsumerPlace(other) || !v5Finite(Number(other.lat)) || !v5Finite(Number(other.lng))){ continue; }
      d = v5Miles(Number(item.lat),Number(item.lng),Number(other.lat),Number(other.lng));
      if(v5Finite(d) && d <= radius){
        cluster.push(other);
        types[v5PoiClass(other)] = true;
      }
    }
    typeCount = 0;
    for(j in types){ if(types.hasOwnProperty(j)){ typeCount += 1; } }
    score = cluster.length * 12 + typeCount * 18;
    if(cluster.length >= minChildren && typeCount >= 2 && (!bestCluster || score > bestCluster.score)){
      bestCluster = { center:item, children:cluster, score:score, typeCount:typeCount };
    }
  }

  if(!bestCluster){ return out; }
  centerLat = 0; centerLng = 0;
  for(i=0;i<bestCluster.children.length;i++){
    centerLat += Number(bestCluster.children[i].lat);
    centerLng += Number(bestCluster.children[i].lng);
  }
  centerLat = centerLat / bestCluster.children.length;
  centerLng = centerLng / bestCluster.children.length;

  item = {
    id:"generated_district_" + String(Math.round(centerLat * 10000)) + "_" + String(Math.round(centerLng * 10000)),
    districtId:"generated_district_" + String(Math.round(centerLat * 10000)) + "_" + String(Math.round(centerLng * 10000)),
    entityId:"generated_district_" + String(Math.round(centerLat * 10000)) + "_" + String(Math.round(centerLng * 10000)),
    label:v5GeneratedDistrictLabel(options,intentId,bestCluster.children),
    name:v5GeneratedDistrictLabel(options,intentId,bestCluster.children),
    full:"Drive IQ found a dense cluster of places worth exploring near this scan area.",
    lat:centerLat,
    lng:centerLng,
    entityType:"district",
    entityClass:"district",
    poiClass:"district",
    districtType:v5GeneratedDistrictType(bestCluster.children),
    resultType:"v5_generated_district",
    isGeneratedDistrict:true,
    isProtectedEntity:true,
    consumerDestination:true,
    placeTruth:true,
    categoryTruth:88,
    diqScore:88,
    confidence:86,
    entityConfidence:84,
    categoryConfidence:82,
    freshnessConfidence:70,
    photoConfidence:35,
    placeConfidence:82,
    recommendationConfidence:86,
    childCount:bestCluster.children.length,
    experienceCount:bestCluster.children.length,
    children:bestCluster.children.slice(0,12),
    category:v5GeneratedDistrictCategory(intentId,bestCluster.children),
    userFacingCategory:v5GeneratedDistrictCategory(intentId,bestCluster.children),
    diqPhotoUrl:"",
    recommendedBecause:v5GeneratedDistrictCategory(intentId,bestCluster.children) + " • " + String(bestCluster.children.length) + " nearby experiences • Generated by District IQ",
    whyRecommended:v5GeneratedDistrictCategory(intentId,bestCluster.children) + " • " + String(bestCluster.children.length) + " nearby experiences • Generated by District IQ",
    v5Explain:{
      entity:"Generated District IQ created this district from a dense result cluster",
      category:"Cluster had " + String(bestCluster.typeCount) + " experience types",
      children:"Cluster contained " + String(bestCluster.children.length) + " child places inside " + String(radius) + " mi",
      photo:"No canonical district photo yet; renderer may use category fallback"
    },
    recommendationObjectVersion:"v2",
    scanEngineBuild:DIQ_V5_BUILD
  };
  out.push(item);
  return out;
}

function v5GeneratedDistrictLabel(options,intentId,children){
  var dest = options && options.destination && (options.destination.label || options.destination.name) ? v5Clean(options.destination.label || options.destination.name) : "";
  var query = v5Norm(options && options.query || "");
  if(dest){ return dest + " District"; }
  if(query.indexOf("downtown") > -1){ return "Downtown District"; }
  if(intentId === "shopping"){ return "Shopping District"; }
  if(intentId === "nightlife"){ return "Entertainment District"; }
  if(intentId === "family_fun"){ return "Family Fun District"; }
  return "Worth Exploring District";
}

function v5GeneratedDistrictType(children){
  var counts = {};
  var i;
  var cls;
  for(i=0;i<v5List(children).length;i++){
    cls = v5PoiClass(children[i]);
    counts[cls] = (counts[cls] || 0) + 1;
  }
  if((counts.nightlife || 0) >= 2){ return "entertainment"; }
  if((counts.shopping || 0) >= 2){ return "shopping"; }
  if((counts.outdoor_water || 0) >= 2){ return "waterfront"; }
  return "local";
}

function v5GeneratedDistrictCategory(intentId,children){
  var type = v5GeneratedDistrictType(children);
  if(intentId === "shopping" || type === "shopping"){ return "Shopping District"; }
  if(intentId === "nightlife" || type === "entertainment"){ return "Entertainment District"; }
  if(type === "waterfront"){ return "Waterfront District"; }
  return "Generated District";
}

function v5ConfidenceChannels(item){
  var entityType = item && item.entityType ? String(item.entityType) : v5EntityType(item);
  var cls = item && item.poiClass ? String(item.poiClass) : v5PoiClass(item);
  var categoryTruth = Number(item && item.categoryTruth || 0);
  var confidence = Number(item && item.confidence || item && item.diqScore || 0);
  var hasPhoto = !!v5PhotoUrl(item);
  var placeConfidence = 50;
  var entityConfidence = 70;
  var categoryConfidence = categoryTruth || 62;
  var freshnessConfidence = 72;
  var photoConfidence = hasPhoto ? 86 : 35;
  if(entityType === "district"){ placeConfidence = item && item.isGeneratedDistrict ? 82 : 98; entityConfidence = item && item.isGeneratedDistrict ? 84 : 98; }
  else if(cls !== "unknown" && cls !== "admin" && cls !== "road"){ placeConfidence = 82; entityConfidence = 78; }
  if(item && item.placeId || item && item.googlePlaceId || item && item.mapboxId){ placeConfidence += 8; }
  if(item && item.freshnessPenalty){ freshnessConfidence -= 20; }
  if(item && item.freshnessBoost){ freshnessConfidence += 10; }
  if(categoryConfidence > 100){ categoryConfidence = 100; }
  if(placeConfidence > 100){ placeConfidence = 100; }
  if(entityConfidence > 100){ entityConfidence = 100; }
  if(freshnessConfidence > 100){ freshnessConfidence = 100; }
  return {
    place:Math.max(0,Math.round(placeConfidence)),
    category:Math.max(0,Math.round(categoryConfidence)),
    freshness:Math.max(0,Math.round(freshnessConfidence)),
    photo:Math.max(0,Math.round(photoConfidence)),
    entity:Math.max(0,Math.round(entityConfidence)),
    overall:Math.max(0,Math.min(100,Math.round(confidence || ((placeConfidence + categoryConfidence + freshnessConfidence + entityConfidence) / 4))))
  };
}

function v5NormalizeRecommendation(item,intentId,options,index){
  var out = v5Clone(item || {});
  var channels;
  out.label = v5Clean(out.label || out.name || out.title || "Recommendation");
  out.name = v5Clean(out.name || out.label);
  out.full = v5Clean(out.full || out.address || out.formatted || out.description || "");
  out.entityType = out.entityType || v5EntityType(out);
  out.entityClass = out.entityClass || out.entityType;
  out.poiClass = out.poiClass || v5PoiClass(out);
  out.category = v5Clean(out.category || out.userFacingCategory || (out.entityType === "district" ? "District" : v5ProfileLabel(intentId)));
  out.userFacingCategory = v5Clean(out.userFacingCategory || out.category);
  out.diqPhotoUrl = v5PhotoUrl(out);
  out.recommendationProfile = v5ProfileLabel(intentId);
  out.scanEngineBuild = DIQ_V5_BUILD;
  out.priorScanEngineBuild = v5Clean(out.priorScanEngineBuild || DIQ_V5_PRIOR_BUILD);
  out.recommendationObjectVersion = "v2";
  out.resultRank = index + 1;
  out.isRealResult = out.entityType === "district" ? true : (out.isRealResult !== false);
  out.confidenceChannels = v5ConfidenceChannels(out);
  out.placeConfidence = out.confidenceChannels.place;
  out.categoryConfidence = out.confidenceChannels.category;
  out.freshnessConfidence = out.confidenceChannels.freshness;
  out.photoConfidence = out.confidenceChannels.photo;
  out.entityConfidence = out.confidenceChannels.entity;
  out.recommendationConfidence = out.confidenceChannels.overall;
  out.confidence = Math.max(Number(out.confidence || 0),out.recommendationConfidence);
  if(!out.recommendedBecause){ out.recommendedBecause = v5Reason(out,intentId); }
  out.whyRecommended = out.whyRecommended || out.recommendedBecause;
  if(!out.v5Explain){
    out.v5Explain = {
      entity:"Entity IQ classified this as " + out.entityType,
      category:"POI class " + out.poiClass + " for " + v5ProfileLabel(intentId),
      photo:out.diqPhotoUrl ? "Canonical diqPhotoUrl present" : "No canonical photo yet",
      rank:"Returned as rank #" + String(index + 1)
    };
  }
  return out;
}

function v5Reason(item,intentId){
  var bits = [];
  if(item && item.entityType === "district"){ bits.push(item.userFacingCategory || "District IQ"); }
  else { bits.push(v5ProfileLabel(intentId)); }
  if(item && Number(item.diqScore || 0) >= 86){ bits.push("high-confidence pick"); }
  if(item && item.distMiles !== null && v5Finite(Number(item.distMiles))){ bits.push(String(Math.round(Number(item.distMiles) * 10) / 10) + " mi away"); }
  if(item && item.childCount){ bits.push(String(item.childCount) + " nearby experiences"); }
  if(item && item.diqPhotoUrl){ bits.push("photo-ready"); }
  return bits.slice(0,3).join(" • ");
}

function v5MergeAndRank(baseResults,districts,intentId,options){
  var merged = v5Dedupe((districts || []).concat(baseResults || []));
  var allowDistrict = v5AllowDistrictForIntent(intentId,options);
  merged.sort(function(a,b){
    var ad = v5IsDistrict(a) ? 1 : 0;
    var bd = v5IsDistrict(b) ? 1 : 0;
    var as = Number(a && (a.diqScore || a.confidence || 0) || 0);
    var bs = Number(b && (b.diqScore || b.confidence || 0) || 0);
    var ac = Number(a && (a.recommendationConfidence || a.confidence || 0) || 0);
    var bc = Number(b && (b.recommendationConfidence || b.confidence || 0) || 0);
    if(allowDistrict && ad !== bd){ return bd - ad; }
    if(Math.abs(bs - as) > 5){ return bs - as; }
    if(Math.abs(bc - ac) > 5){ return bc - ac; }
    return 0;
  });
  return merged;
}

function v5BuildReport(options,base,intentId,baseResults,districts,finalResults,startedAt){
  var report = base && base.report ? v5Clone(base.report) : {};
  var i;
  report.build = DIQ_V5_BUILD;
  report.priorBuild = DIQ_V5_PRIOR_BUILD;
  report.version = "v5_intelligence_architecture";
  report.ts = report.ts || v5NowIso();
  report.query = v5Clean((options && options.query) || report.query || "");
  report.intent = intentId;
  report.profile = v5ProfileLabel(intentId);
  report.engineTimeMs = Math.max(0,Date.now() - Number(startedAt || Date.now()));
  if(!report.counts){ report.counts = {}; }
  report.counts.v5BaseReturned = baseResults ? baseResults.length : 0;
  report.counts.v5DistrictsAdded = districts ? districts.length : 0;
  report.counts.v5Returned = finalResults ? finalResults.length : 0;
  report.entityCounts = { place:0, district:0, neighborhood:0, lake:0, beach:0, park:0, scenic_area:0 };
  report.v5Districts = [];
  report.v5Explainability = [];
  for(i=0;i<v5List(finalResults).length;i++){
    if(report.entityCounts[finalResults[i].entityType] === undefined){ report.entityCounts[finalResults[i].entityType] = 0; }
    report.entityCounts[finalResults[i].entityType] += 1;
    if(finalResults[i].entityType === "district"){
      report.v5Districts.push({ name:finalResults[i].label, generated:!!finalResults[i].isGeneratedDistrict, childCount:Number(finalResults[i].childCount || 0), confidence:Number(finalResults[i].recommendationConfidence || finalResults[i].confidence || 0) });
    }
    if(report.v5Explainability.length < 12){
      report.v5Explainability.push({
        rank:i+1,
        name:finalResults[i].label,
        entityType:finalResults[i].entityType,
        poiClass:finalResults[i].poiClass,
        why:finalResults[i].whyRecommended || finalResults[i].recommendedBecause || "",
        confidence:finalResults[i].confidenceChannels || null
      });
    }
  }
  if(!report.notes){ report.notes = []; }
  report.notes.push("V5 normalized all recommendations to Recommendation Object V2 with diqPhotoUrl authority.");
  if(districts && districts.length){ report.notes.push("V5 District IQ added " + String(districts.length) + " protected district recommendation(s)."); }
  v5SetJson(DIQ_V5_LAST_REPORT_KEY,report);
  return report;
}

function v5Scan(options){
  var startedAt = Date.now();
  var base;
  var baseResults;
  var candidatePool;
  var intentId;
  var registryDistricts;
  var generatedDistricts;
  var districts;
  var merged;
  var finalResults = [];
  var report;
  var max;
  var i;

  options = options || {};
  if(!DIQ_V5_PRIOR_SCAN){
    return { ok:false, build:DIQ_V5_BUILD, error:"Prior DriveIQScanEngine.scan is unavailable", results:[], report:null };
  }

  base = DIQ_V5_PRIOR_SCAN(options);
  baseResults = base && base.results ? base.results : [];
  candidatePool = v5Dedupe((options.candidates || []).concat(baseResults || []));
  intentId = v5IntentId(options,base || {});

  registryDistricts = v5RegistryDistricts(intentId,options,candidatePool,baseResults);
  generatedDistricts = v5GeneratedDistricts(intentId,options,candidatePool,baseResults);
  districts = v5Dedupe(registryDistricts.concat(generatedDistricts));
  merged = v5MergeAndRank(baseResults,districts,intentId,options);

  max = Number(options.targetResults || options.maxResults || (base && base.profile && base.profile.maxResults) || 10);
  if(max < 4){ max = 4; }
  if(max > 24){ max = 24; }

  for(i=0;i<merged.length && finalResults.length<max;i++){
    finalResults.push(v5NormalizeRecommendation(merged[i],intentId,options,finalResults.length));
  }

  report = v5BuildReport(options,base,intentId,baseResults,districts,finalResults,startedAt);

  return {
    ok:true,
    build:DIQ_V5_BUILD,
    priorBuild:DIQ_V5_PRIOR_BUILD,
    intent:intentId,
    profile:base && base.profile ? base.profile : { id:intentId, label:v5ProfileLabel(intentId) },
    results:finalResults,
    report:report
  };
}

function v5LastReport(){ return v5GetJson(DIQ_V5_LAST_REPORT_KEY); }

function v5Classify(item,intentId){
  var out = v5NormalizeRecommendation(item || {},intentId || "general",{},0);
  return out;
}

window.DriveIQScanEngine = window.DriveIQScanEngine || {};
window.DriveIQScanEngine.build = DIQ_V5_BUILD;
window.DriveIQScanEngine.priorBuild = DIQ_V5_PRIOR_BUILD;
window.DriveIQScanEngine.scan = v5Scan;
window.DriveIQScanEngine.lastV5Report = v5LastReport;
window.DriveIQScanEngine.recommendationObjectVersion = "v2";
window.DriveIQScanEngine.photoAuthority = "diqPhotoUrl";
window.DriveIQScanEngine.entityIQ = window.DriveIQScanEngine.entityIQ || {};
window.DriveIQScanEngine.entityIQ.build = DIQ_V5_BUILD;
window.DriveIQScanEngine.entityIQ.classify = v5Classify;
window.DriveIQScanEngine.entityIQ.registry = v5Registry;
window.DriveIQScanEngine.districtIQ = {
  build:DIQ_V5_BUILD,
  registry:v5Registry,
  generated:v5GeneratedDistricts,
  isDistrict:v5IsDistrict
};
window.DriveIQScanEngine.explainabilityIQ = {
  build:DIQ_V5_BUILD,
  lastReport:v5LastReport
};

try{
  console.log("🧠 DriveIQScanEngine V5 loaded",{
    build:DIQ_V5_BUILD,
    priorBuild:DIQ_V5_PRIOR_BUILD,
    photoAuthority:"diqPhotoUrl",
    recommendationObjectVersion:"v2"
  });
}catch(eLog){}

}());


/* =====================================================================
   DRIVE IQ — SCAN ENGINE V5.1 SINGLE PUBLIC AUTHORITY
   ENTITY IQ + DISTRICT IQ GUARANTEED HANDOFF

   This final authority intentionally does not call any legacy district
   injection helper. It owns one deterministic public scan handoff:

   1. Run the proven place-truth pipeline once.
   2. Resolve the selected intent from explicit category/intent authority.
   3. Resolve the actual scan center.
   4. Build verified district entities directly from the canonical registry.
   5. Merge districts and places before the visible result limit.
   6. Protect district identity through the final Recommendation Object.
   7. Publish diagnostics that prove whether districts entered the result set.

   ES5 SAFE
===================================================================== */
(function(){
"use strict";

if(window.__DRIVE_IQ_SCAN_ENGINE_V5_1_SINGLE_PUBLIC_AUTHORITY__){ return; }
window.__DRIVE_IQ_SCAN_ENGINE_V5_1_SINGLE_PUBLIC_AUTHORITY__ = true;

var V51_BUILD = "V5_1_SINGLE_PUBLIC_AUTHORITY_ENTITY_DISTRICT";
var V51_REPORT_KEY = "diq_scan_engine_v51_last_report";
var V51_PRIOR_ENGINE = window.DriveIQScanEngine || null;
var V51_PRIOR_SCAN = V51_PRIOR_ENGINE && typeof V51_PRIOR_ENGINE.scan === "function"
  ? V51_PRIOR_ENGINE.scan
  : null;
var V51_PRIOR_BUILD = V51_PRIOR_ENGINE && V51_PRIOR_ENGINE.build
  ? String(V51_PRIOR_ENGINE.build)
  : "unknown";

function v51Clean(v){ return String(v || "").replace(/\s+/g," ").trim(); }
function v51Norm(v){
  return String(v || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g," ")
    .replace(/\s+/g," ")
    .trim();
}
function v51Finite(v){ return typeof v === "number" && isFinite(v); }
function v51Clone(v){
  try{ return JSON.parse(JSON.stringify(v || {})); }
  catch(e){ return {}; }
}
function v51List(v){ return v && v.length ? v : []; }
function v51SetJson(k,v){
  try{ localStorage.setItem(k,JSON.stringify(v)); }catch(e){}
}
function v51GetJson(k){
  try{ return JSON.parse(localStorage.getItem(k) || "null"); }
  catch(e){ return null; }
}
function v51Miles(lat1,lng1,lat2,lng2){
  var R = 3958.8;
  var dLat;
  var dLng;
  var a1;
  var a2;
  var a;

  lat1 = Number(lat1);
  lng1 = Number(lng1);
  lat2 = Number(lat2);
  lng2 = Number(lng2);

  if(!v51Finite(lat1) || !v51Finite(lng1) ||
     !v51Finite(lat2) || !v51Finite(lng2)){
    return null;
  }

  dLat = (lat2-lat1) * Math.PI / 180;
  dLng = (lng2-lng1) * Math.PI / 180;
  a1 = lat1 * Math.PI / 180;
  a2 = lat2 * Math.PI / 180;

  a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.sin(dLng/2) * Math.sin(dLng/2) *
    Math.cos(a1) * Math.cos(a2);

  return R * 2 * Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
}

var V51_DISTRICTS = [
  {
    id:"harborwalk_village_destin",
    label:"HarborWalk Village",
    aliases:["harborwalk village","harbor walk village","destin harborwalk","destin harbor walk","harborwalk destin","harbor walk destin"],
    city:"Destin",
    state:"FL",
    lat:30.3935,
    lng:-86.5104,
    districtType:"adventure",
    districtLabel:"Waterfront Entertainment District",
    tags:["waterfront","entertainment","nightlife","food","shopping","activities","live music","boating"],
    authority:99,
    experienceCount:18,
    summary:"Waterfront entertainment district with restaurants, bars, shopping, live music, and harbor activities.",
    topPlaceNames:["AJ's Seafood & Oyster Bar","Harry T's","Margaritaville","Tailfins","Harbor Docks","Boogies Watersports"]
  },
  {
    id:"destin_commons",
    label:"Destin Commons",
    aliases:["destin commons","the destin commons"],
    city:"Destin",
    state:"FL",
    lat:30.3909,
    lng:-86.4271,
    districtType:"shopping",
    districtLabel:"Shopping & Entertainment District",
    tags:["shopping","retail","restaurants","movies","family","entertainment","coffee"],
    authority:96,
    experienceCount:45,
    summary:"Retail-first shopping district with stores, restaurants, coffee, movies, and family entertainment.",
    topPlaceNames:["Bass Pro Shops","AMC Destin Commons","Belk","H&M","Uncle Buck's FishBowl","Starbucks"]
  },
  {
    id:"silo_square_southaven",
    label:"Silo Square",
    aliases:["silo square","silo square southaven","silo square ms"],
    city:"Southaven",
    state:"MS",
    lat:34.9619,
    lng:-89.9907,
    districtType:"local",
    districtLabel:"Local District",
    tags:["local","restaurants","coffee","events","shops","patios","community"],
    authority:93,
    experienceCount:12,
    summary:"Walkable local district with restaurants, coffee, events, shops, patios, and gathering spaces.",
    topPlaceNames:["Local restaurants","Coffee","Community events","Boutiques","Patios"]
  },
  {
    id:"beale_street_memphis",
    label:"Beale Street",
    aliases:["beale street","beale st","beale street memphis"],
    city:"Memphis",
    state:"TN",
    lat:35.1398,
    lng:-90.0524,
    districtType:"entertainment",
    districtLabel:"Historic Entertainment District",
    tags:["nightlife","live music","bars","food","tourism","walkable","iconic"],
    authority:98,
    experienceCount:25,
    summary:"Iconic entertainment district known for live music, nightlife, restaurants, and Memphis energy.",
    topPlaceNames:["B.B. King's Blues Club","Rum Boogie Cafe","Silky O'Sullivan's","A. Schwab","Handy Park"]
  },
  {
    id:"broadway_nashville",
    label:"Broadway Nashville",
    aliases:["broadway nashville","lower broadway","broadway downtown nashville","honky tonk highway"],
    city:"Nashville",
    state:"TN",
    lat:36.1611,
    lng:-86.7789,
    districtType:"entertainment",
    districtLabel:"Live Music Entertainment District",
    tags:["nightlife","live music","bars","food","tourism","walkable","iconic"],
    authority:99,
    experienceCount:35,
    summary:"High-energy entertainment district with honky-tonks, live music, rooftop bars, and restaurants.",
    topPlaceNames:["Tootsie's Orchid Lounge","The Stage","Robert's Western World","Acme Feed & Seed","Assembly Food Hall"]
  },
  {
    id:"the_square_oxford",
    label:"The Square",
    aliases:["the square oxford","oxford square","historic square oxford"],
    city:"Oxford",
    state:"MS",
    lat:34.3668,
    lng:-89.5192,
    districtType:"local",
    districtLabel:"Historic Local District",
    tags:["restaurants","shopping","coffee","nightlife","local","walkable","college town"],
    authority:95,
    experienceCount:20,
    summary:"Walkable local district with restaurants, boutiques, coffee, bookstores, nightlife, and Oxford character.",
    topPlaceNames:["City Grocery","Ajax Diner","Square Books","Boure","Proud Larry's"]
  },
  {
    id:"downtown_gatlinburg",
    label:"Downtown Gatlinburg",
    aliases:["downtown gatlinburg","gatlinburg strip","the strip gatlinburg","gatlinburg downtown"],
    city:"Gatlinburg",
    state:"TN",
    lat:35.7110,
    lng:-83.5186,
    districtType:"adventure",
    districtLabel:"Mountain Adventure District",
    tags:["attractions","food","shopping","family","walkable","tourism","mountains"],
    authority:97,
    experienceCount:40,
    summary:"Walkable mountain-town district with attractions, food, shops, family activities, and Smoky Mountain energy.",
    topPlaceNames:["Anakeesta","Ripley's Aquarium","Ole Smoky","The Village Shops","SkyPark"]
  },
  {
    id:"disney_springs",
    label:"Disney Springs",
    aliases:["disney springs","downtown disney orlando"],
    city:"Lake Buena Vista",
    state:"FL",
    lat:28.3702,
    lng:-81.5197,
    districtType:"adventure",
    districtLabel:"Dining, Shopping & Entertainment District",
    tags:["shopping","restaurants","entertainment","family","nightlife","tourism","walkable"],
    authority:99,
    experienceCount:80,
    summary:"Large entertainment, dining, and shopping district with restaurants, shops, shows, and family experiences.",
    topPlaceNames:["The Boathouse","World of Disney","Gideon's Bakehouse","Raglan Road","House of Blues"]
  },
  {
    id:"the_wharf_orange_beach",
    label:"The Wharf",
    aliases:["the wharf orange beach","the wharf","orange beach wharf"],
    city:"Orange Beach",
    state:"AL",
    lat:30.2946,
    lng:-87.6289,
    districtType:"adventure",
    districtLabel:"Waterfront Adventure District",
    tags:["waterfront","restaurants","shopping","events","amphitheater","family","nightlife"],
    authority:96,
    experienceCount:25,
    summary:"Waterfront adventure district with restaurants, shops, events, concerts, activities, and marina energy.",
    topPlaceNames:["The Wharf Amphitheater","Villaggio Grille","YoHo Rum & Tacos","Ferris Wheel","Marina"]
  }
];

function v51ExplicitIntentText(options){
  return v51Norm(
    String(options && options.intent || "") + " " +
    String(options && options.category || "") + " " +
    String(options && options.lens || "") + " " +
    String(options && options.profile || "")
  );
}

function v51QueryText(options){
  return v51Norm(
    String(options && options.query || "") + " " +
    String(options && options.sourceQuery || "")
  );
}

function v51Intent(options,base){
  var explicit = v51ExplicitIntentText(options);
  var query = v51QueryText(options);
  var baseId = base && base.intent ? String(base.intent) : "";

  /* Explicit selected category is authoritative. */
  if(/\blocal favorite|local favorites|local faves|hangout\b/.test(explicit)){ return "local_favorites"; }
  if(/\bhidden gem|hidden gems\b/.test(explicit)){ return "hidden_gems"; }
  if(/\bshopping|shop|boutique|market|retail|outlet\b/.test(explicit)){ return "shopping"; }
  if(/\bnightlife|live music|bar|pub|brewery|cocktail\b/.test(explicit)){ return "nightlife"; }
  if(/\bfamily|kids|family fun\b/.test(explicit)){ return "family_fun"; }
  if(/\blake|waterfront|marina|reservoir\b/.test(explicit)){ return "lake"; }
  if(/\bpark|outdoor|scenic|trail\b/.test(explicit)){ return "parks"; }
  if(/\bcoffee|cafe|espresso|roaster\b/.test(explicit)){ return "coffee"; }
  if(/\bgolf|course|driving range\b/.test(explicit)){ return "golf"; }
  if(/\bdessert|ice cream|treat|bakery\b/.test(explicit)){ return "dessert"; }
  if(/\bbreakfast|brunch\b/.test(explicit)){ return "breakfast"; }
  if(/\bdinner|date night|supper\b/.test(explicit)){ return "dinner"; }
  if(/\bfood|restaurant|lunch|pizza|burger|taco|bbq|seafood\b/.test(explicit)){ return "food"; }

  /* Trust the prior engine when it already resolved a specific intent. */
  if(baseId && baseId !== "general"){ return baseId; }

  if(/\blocal favorite|local favorites|local faves|hangout\b/.test(query)){ return "local_favorites"; }
  if(/\bhidden gem|hidden gems\b/.test(query)){ return "hidden_gems"; }
  if(/\bshopping district|shopping|boutique|retail|outlet\b/.test(query)){ return "shopping"; }
  if(/\blive music|nightlife|cocktail|brewery|pub\b/.test(query)){ return "nightlife"; }
  if(/\bfamily|kids\b/.test(query)){ return "family_fun"; }
  if(/\blake|waterfront|marina|reservoir\b/.test(query)){ return "lake"; }
  if(/\bpark|outdoor|scenic|trail\b/.test(query)){ return "parks"; }
  if(/\bcoffee|cafe|espresso|roaster\b/.test(query)){ return "coffee"; }
  if(/\bgolf|course|driving range\b/.test(query)){ return "golf"; }
  if(/\bdessert|ice cream|treat|bakery\b/.test(query)){ return "dessert"; }
  if(/\bbreakfast|brunch\b/.test(query)){ return "breakfast"; }
  if(/\bdinner|date night|supper\b/.test(query)){ return "dinner"; }
  if(/\bfood|restaurant|lunch|pizza|burger|taco|bbq|seafood\b/.test(query)){ return "food"; }

  return "general";
}

function v51Center(options,baseResults){
  var c;
  var i;
  var latTotal = 0;
  var lngTotal = 0;
  var count = 0;

  c = options && options.location;
  if(c && v51Finite(Number(c.lat)) && v51Finite(Number(c.lng))){
    return {lat:Number(c.lat),lng:Number(c.lng),source:"options.location"};
  }

  c = options && options.center;
  if(c && v51Finite(Number(c.lat)) && v51Finite(Number(c.lng))){
    return {lat:Number(c.lat),lng:Number(c.lng),source:"options.center"};
  }

  c = options && options.destination;
  if(c && v51Finite(Number(c.lat)) && v51Finite(Number(c.lng))){
    return {lat:Number(c.lat),lng:Number(c.lng),source:"options.destination"};
  }

  for(i=0;i<v51List(baseResults).length;i++){
    if(v51Finite(Number(baseResults[i].lat)) && v51Finite(Number(baseResults[i].lng))){
      latTotal += Number(baseResults[i].lat);
      lngTotal += Number(baseResults[i].lng);
      count += 1;
      if(count >= 12){ break; }
    }
  }

  if(count){
    return {
      lat:latTotal/count,
      lng:lngTotal/count,
      source:"result_cluster"
    };
  }

  return null;
}

function v51DistrictAllowed(intent,district){
  var type = String(district && district.districtType || "adventure");

  if(intent === "food" || intent === "coffee" || intent === "breakfast" ||
     intent === "dinner" || intent === "dessert" || intent === "golf"){
    return false;
  }

  if(intent === "shopping"){
    return type === "shopping" || type === "local" || type === "adventure";
  }

  if(intent === "nightlife"){
    return type === "entertainment" || type === "adventure";
  }

  if(intent === "lake" || intent === "parks"){
    return v51Norm((district.tags || []).join(" ")).indexOf("waterfront") > -1 ||
      type === "adventure";
  }

  return intent === "general" ||
    intent === "local_favorites" ||
    intent === "hidden_gems" ||
    intent === "family_fun";
}

function v51DistrictRadius(intent){
  if(intent === "shopping"){ return 24; }
  if(intent === "nightlife"){ return 20; }
  if(intent === "local_favorites"){ return 18; }
  if(intent === "hidden_gems"){ return 18; }
  if(intent === "family_fun"){ return 22; }
  if(intent === "lake" || intent === "parks"){ return 28; }
  return 16;
}

function v51ExactDistrictQuery(options,district){
  var q = v51QueryText(options);
  var aliases = [district.label].concat(district.aliases || []);
  var i;
  var a;

  if(!q){ return false; }

  for(i=0;i<aliases.length;i++){
    a = v51Norm(aliases[i]);
    if(q === a || q.indexOf(a) > -1 || a.indexOf(q) > -1){
      return true;
    }
  }

  return false;
}

function v51DistrictScore(district,intent,distanceMiles,exact){
  var score = Number(district.authority || 90);

  if(exact){ score += 35; }
  if(intent === "local_favorites"){ score += 16; }
  if(intent === "shopping" && district.districtType === "shopping"){ score += 22; }
  if(intent === "nightlife" && district.districtType === "entertainment"){ score += 22; }
  if(intent === "family_fun" && v51Norm((district.tags || []).join(" ")).indexOf("family") > -1){ score += 15; }

  if(v51Finite(distanceMiles)){
    if(distanceMiles <= 2){ score += 18; }
    else if(distanceMiles <= 6){ score += 13; }
    else if(distanceMiles <= 12){ score += 8; }
    else if(distanceMiles <= 20){ score += 2; }
  }

  return Math.max(0,Math.min(100,Math.round(score)));
}

function v51MakeDistrict(district,intent,center,options){
  var d = center ? v51Miles(center.lat,center.lng,district.lat,district.lng) : null;
  var exact = v51ExactDistrictQuery(options,district);
  var score = v51DistrictScore(district,intent,d,exact);
  var tags = district.tags || [];

  return {
    id:"district:" + district.id,
    entityId:"district:" + district.id,
    districtId:district.id,
    label:district.label,
    name:district.label,
    full:district.summary,
    address:[district.city,district.state].join(", "),
    city:district.city,
    state:district.state,
    lat:Number(district.lat),
    lng:Number(district.lng),
    category:"district",
    kind:"district",
    type:"district",
    resultType:"entity_registry_district",
    entityType:"district",
    entityClass:"district",
    poiClass:"district",
    districtType:district.districtType,
    districtLabel:district.districtLabel,
    tags:tags.slice(0),
    experienceCount:Number(district.experienceCount || 0),
    topPlaceNames:(district.topPlaceNames || []).slice(0,6),
    summary:district.summary,
    authority:Number(district.authority || 90),
    canonical:true,
    isRealResult:true,
    consumerDestination:true,
    placeTruth:true,
    categoryTruth:100,
    finalAuthority:true,
    localFavorite:true,
    isLocalFavorite:true,
    distMiles:v51Finite(d) ? Math.round(d*10)/10 : null,
    distanceMiles:v51Finite(d) ? Math.round(d*10)/10 : null,
    diqScore:score,
    confidence:score,
    worthScore:score,
    worthTheDriveScore:score,
    recommendationProfile:intent,
    userFacingCategory:district.districtLabel,
    recommendedBecause:
      district.districtLabel +
      " • " + String(district.experienceCount || 0) + " experiences" +
      (v51Finite(d) ? " • " + String(Math.round(d*10)/10) + " mi away" : ""),
    whyRecommended:district.summary,
    source:"drive-iq-v51-canonical-district-registry",
    scanEngineBuild:V51_BUILD,
    recommendationObjectVersion:"v2",
    diqPhotoUrl:"",
    photoUrl:"",
    imageUrl:"",
    thumbnailUrl:"",
    _v51ProtectedDistrict:true
  };
}

function v51BuildDistricts(intent,center,options){
  var out = [];
  var radius = v51DistrictRadius(intent);
  var i;
  var district;
  var d;
  var exact;

  for(i=0;i<V51_DISTRICTS.length;i++){
    district = V51_DISTRICTS[i];
    if(!v51DistrictAllowed(intent,district)){ continue; }

    exact = v51ExactDistrictQuery(options,district);
    d = center ? v51Miles(center.lat,center.lng,district.lat,district.lng) : null;

    if(!exact && (!v51Finite(d) || d > radius)){ continue; }

    out.push(v51MakeDistrict(district,intent,center,options));
  }

  out.sort(function(a,b){
    return Number(b.diqScore || 0) - Number(a.diqScore || 0);
  });

  return out;
}

function v51Key(item){
  if(item && item.entityType === "district"){
    return "district:" + String(item.districtId || item.entityId || item.id || v51Norm(item.label));
  }

  return "place:" + v51Norm(
    String(item && (item.placeId || item.googlePlaceId || item.mapboxId || "")) +
    "|" + String(item && (item.label || item.name || "")) +
    "|" + String(item && item.lat || "") +
    "|" + String(item && item.lng || "")
  );
}

function v51Dedupe(items){
  var out = [];
  var seen = {};
  var i;
  var item;
  var key;

  for(i=0;i<v51List(items).length;i++){
    item = items[i];
    if(!item){ continue; }
    key = v51Key(item);
    if(seen[key]){ continue; }
    seen[key] = true;
    out.push(item);
  }

  return out;
}

function v51NormalizePlace(item,intent,index){
  var out = v51Clone(item);
  var photo = v51Clean(
    out.diqPhotoUrl ||
    out.photoUrl ||
    out.imageUrl ||
    out.thumbnailUrl ||
    ""
  );

  if(!out.entityType){ out.entityType = "place"; }
  if(!out.entityClass){ out.entityClass = out.entityType; }
  if(!out.poiClass){ out.poiClass = "place"; }
  if(!out.userFacingCategory){ out.userFacingCategory = out.category || "Place"; }
  if(!out.recommendedBecause){
    out.recommendedBecause = out.whyRecommended || "A strong match for this scan.";
  }

  out.diqPhotoUrl = photo;
  out.photoUrl = photo;
  out.imageUrl = photo;
  out.thumbnailUrl = photo;
  out.scanEngineBuild = V51_BUILD;
  out.priorScanEngineBuild = item && item.scanEngineBuild ? item.scanEngineBuild : V51_PRIOR_BUILD;
  out.recommendationObjectVersion = "v2";
  out.rank = index + 1;
  out.isRealResult = out.isRealResult !== false;

  return out;
}

function v51Merge(baseResults,districts,intent,max){
  var out = [];
  var places = v51Dedupe(baseResults || []);
  var ds = v51Dedupe(districts || []);
  var i;

  /* Districts are inserted before the visible cap, not appended afterward. */
  if(ds.length){
    out.push(ds[0]);

    /* Keep a strong individual-place choice immediately after the district. */
    if(places.length){ out.push(places.shift()); }

    if(ds.length > 1 && (intent === "shopping" || intent === "local_favorites" || intent === "general")){
      out.push(ds[1]);
    }
  }

  for(i=0;i<places.length && out.length<max;i++){
    out.push(places[i]);
  }

  for(i=2;i<ds.length && out.length<max;i++){
    out.push(ds[i]);
  }

  out = v51Dedupe(out);

  return out.slice(0,max);
}

function v51Scan(options){
  var startedAt = Date.now();
  var base;
  var baseResults;
  var intent;
  var center;
  var districts;
  var max;
  var merged;
  var finalResults = [];
  var i;
  var report;

  options = options || {};

  if(!V51_PRIOR_SCAN){
    return {
      ok:false,
      build:V51_BUILD,
      error:"Prior scan engine is unavailable.",
      intent:"general",
      profile:{id:"general",label:"Adventure IQ"},
      results:[],
      report:null
    };
  }

  /* Exactly one call into the proven place-truth pipeline. */
  base = V51_PRIOR_SCAN(options) || {};
  baseResults = base.results || [];
  intent = v51Intent(options,base);
  center = v51Center(options,baseResults);
  districts = v51BuildDistricts(intent,center,options);

  max = Number(
    options.targetResults ||
    options.maxResults ||
    (base.profile && base.profile.maxResults) ||
    10
  );
  if(max < 4){ max = 4; }
  if(max > 24){ max = 24; }

  merged = v51Merge(baseResults,districts,intent,max);

  for(i=0;i<merged.length;i++){
    if(merged[i] && merged[i].entityType === "district"){
      merged[i].rank = i + 1;
      finalResults.push(merged[i]);
    }else{
      finalResults.push(v51NormalizePlace(merged[i],intent,i));
    }
  }

  report = v51Clone(base.report || {});
  report.build = V51_BUILD;
  report.priorBuild = V51_PRIOR_BUILD;
  report.version = "v5_1_single_public_authority";
  report.engineTimeMs = Math.max(0,Date.now()-startedAt);
  report.intent = intent;
  report.centerAuthority = center;
  report.counts = report.counts || {};
  report.counts.priorReturned = baseResults.length;
  report.counts.districtsInjected = districts.length;
  report.counts.returned = finalResults.length;
  report.districts = [];
  report.finalResults = [];

  for(i=0;i<districts.length;i++){
    report.districts.push({
      name:districts[i].label,
      distanceMiles:districts[i].distMiles,
      score:districts[i].diqScore,
      districtType:districts[i].districtType
    });
  }

  for(i=0;i<finalResults.length;i++){
    report.finalResults.push({
      rank:i+1,
      name:finalResults[i].label,
      entityType:finalResults[i].entityType || "place",
      score:Number(finalResults[i].diqScore || 0)
    });
  }

  report.notes = report.notes || [];
  report.notes.push(
    districts.length
      ? "V5.1 injected verified districts before the visible result cap."
      : "V5.1 found no eligible verified district within the active radius."
  );

  v51SetJson(V51_REPORT_KEY,report);

  try{
    console.log("🏙 DriveIQ Scan Engine V5.1",{
      intent:intent,
      center:center,
      districtsInjected:districts.length,
      results:finalResults.map ? finalResults.map(function(x){
        return {name:x.label,entityType:x.entityType || "place"};
      }) : []
    });
  }catch(eLog){}

  return {
    ok:true,
    build:V51_BUILD,
    priorBuild:V51_PRIOR_BUILD,
    intent:intent,
    profile:base.profile || {id:intent,label:intent},
    results:finalResults,
    report:report
  };
}

function v51LastReport(){ return v51GetJson(V51_REPORT_KEY); }

window.DriveIQScanEngine = window.DriveIQScanEngine || {};
window.DriveIQScanEngine.build = V51_BUILD;
window.DriveIQScanEngine.priorBuild = V51_PRIOR_BUILD;
window.DriveIQScanEngine.scan = v51Scan;
window.DriveIQScanEngine.lastV51Report = v51LastReport;
window.DriveIQScanEngine.recommendationObjectVersion = "v2";
window.DriveIQScanEngine.entityIQ = window.DriveIQScanEngine.entityIQ || {};
window.DriveIQScanEngine.entityIQ.build = V51_BUILD;
window.DriveIQScanEngine.entityIQ.registry = V51_DISTRICTS;
window.DriveIQScanEngine.districtIQ = {
  build:V51_BUILD,
  registry:V51_DISTRICTS,
  lastReport:v51LastReport
};

try{
  console.log("✅ DriveIQScanEngine V5.1 Single Public Authority loaded",{
    build:V51_BUILD,
    priorBuild:V51_PRIOR_BUILD,
    districts:V51_DISTRICTS.length
  });
}catch(eAttach){}

}());
